import React, { useState, useEffect, useRef } from 'react';
import ReactGA from 'react-ga4';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { collection, getDocs,setDoc,onSnapshot, addDoc, deleteDoc, doc, query, orderBy, serverTimestamp, where, writeBatch, updateDoc,getCountFromServer,limit } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { auth, db, storage } from './firebase';
import { getToken,deleteToken,onMessage } from "firebase/messaging";
import { messaging } from "./firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import emailjs from '@emailjs/browser';

import { ReactComponent as OfficialLogo } from './baladiye-logo.svg'; 
import { ReactComponent as OfficialLogoInvert } from './baladiye-logo-invert.svg'; 
import homeImage from './img/roumine_trees.jpg';
import historicalRoumine from './img/historical_roumine.jpg';
import roumineRoad from './img/roumine_road.jpg';
import modernRoumine from './img/modern_roumine.jpg';


// --- HELPER COMPONENTS ---
const PageWrapper = ({ children }) => (
    <div className="animate-fadeIn">{children}</div>
);

// --- PAGE COMPONENTS ---

// --- NOTIFICATION BELL BUTTON (WITH SMART IPHONE DIAGNOSTICS) ---
function NotificationBellButton() {
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const storedToken = localStorage.getItem('roumine_fcm_token');
        if (storedToken && typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
            setIsSubscribed(true);
        } else {
            setIsSubscribed(false);
        }
    }, []);

    // Helper to detect iOS version
    const getIOSVersion = () => {
        if (typeof window === 'undefined') return null;
        const match = navigator.userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
        if (match && match[1]) {
            return parseFloat(`${match[1]}.${match[2]}`);
        }
        return null;
    };

    const handleSubscribe = async (isIOS) => {
        setIsLoading(true);
        try {
            let permission = Notification.permission;
            if (permission !== 'granted') {
                permission = await Notification.requestPermission();
            }

            if (permission !== 'granted') {
                alert("لم يتم منح إذن الإشعارات من إعدادات الهاتف.");
                setIsLoading(false);
                return;
            }

            let swReg = null;
            if ('serviceWorker' in navigator) {
                swReg = await navigator.serviceWorker.ready;
            }

            const msg = typeof messaging === 'function' ? await messaging() : null;
            if (!msg) throw new Error("خدمة إشعارات Firebase غير مدعومة في هذا المتصفح.");

            const rawVapidKey = "BOJzt71ktlZFvKe4KzBXjbTQqnw1T6EPZAKoRbNEZJMc7tl91dkwbNz5X7ITXWBP_XTd8Z17wCxcIxnVLZ6B8DE";
            const cleanVapidKey = rawVapidKey ? rawVapidKey.trim() : "";

            const token = await getToken(msg, {
                vapidKey: cleanVapidKey,
                serviceWorkerRegistration: swReg
            });

            if (!token) throw new Error("لم يتم استلام مفتاح الجهاز (Token).");

            await setDoc(doc(db, "fcmTokens", token), {
                token: token,
                deviceType: isIOS ? 'ios' : 'android_or_desktop',
                updatedAt: serverTimestamp(),
                createdAt: serverTimestamp()
            }, { merge: true });

            localStorage.setItem('roumine_fcm_token', token);
            setIsSubscribed(true);
            alert("تم تفعيل إشعارات بلدية رومين بنجاح! 🔔");
        } catch (error) {
            console.error("Subscription error:", error);
            alert("فشل تفعيل الإشعارات: " + (error.message || "حدث خطأ غير معروف"));
        } finally {
            setIsLoading(false);
        }
    };

    const handleUnsubscribe = async () => {
        const confirmDisable = window.confirm("هل تريد إيقاف استلام إشعارات بلدية رومين على هذا الجهاز؟");
        if (!confirmDisable) return;

        setIsLoading(true);
        try {
            const currentToken = localStorage.getItem('roumine_fcm_token');
            if (currentToken) {
                await deleteDoc(doc(db, "fcmTokens", currentToken)).catch(() => {});
            }

            const msg = typeof messaging === 'function' ? await messaging() : null;
            if (msg) {
                await deleteToken(msg).catch(() => {});
            }

            localStorage.removeItem('roumine_fcm_token');
            setIsSubscribed(false);
            alert("تم إيقاف تفعيل الإشعارات بنجاح. 🔕");
        } catch (error) {
            console.error("Unsubscribe error:", error);
            alert("حدث خطأ أثناء محاولة إيقاف الإشعارات.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleBellClick = async () => {
        if (typeof window !== 'undefined' && window.location.protocol === 'http:' && window.location.hostname !== 'localhost') {
            alert("تنبيه: تتطلب إشعارات الويب اتصالاً آمناً (HTTPS).");
            return;
        }

        const isIOS = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
        const iosVersion = getIOSVersion();
        const isStandalone = window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;

        // Specific iPhone diagnostics
        if (isIOS) {
            if (iosVersion && iosVersion < 16.4) {
                alert(`تنبيه: إصدار نظام هاتفك (iOS ${iosVersion}) قديم. أضافت شركة Apple ميزة إشعارات الويب بدءاً من الإصدار iOS 16.4. يرجى تحديث نظام الـ iPhone لتفعيل الإشعارات.`);
                return;
            }

            if (!isStandalone) {
                alert("لتفعيل الإشعارات على iPhone:\n1. افتح الموقع عبر متصفح Safari.\n2. احذف أي اختصار قديم للموقع من شاشة الهاتف.\n3. اضغط على زر المشاركة (Share ⎋) ثم اختر 'إضافة إلى الشاشة الرئيسية' (Add to Home Screen ➕).\n4. افتح التطبيق الجديد من شاشة هاتفك الرئيسية واضغط على جرس الإشعارات مجدداً.");
                return;
            }
        }

        if (!('Notification' in window)) {
            alert("المتصفح الحالي لا يدعم إشعارات الويب.");
            return;
        }

        if (Notification.permission === 'denied') {
            alert("تم حظر الإشعارات مسبقاً. لإعادة تفعيلها: ادخل إلى إعدادات الـ iPhone -> الإشعارات -> ابحث عن تطبيق 'بلدية رومين' وفعل خيار 'السماح بالإشعارات'.");
            return;
        }

        if (isSubscribed) {
            await handleUnsubscribe();
        } else {
            await handleSubscribe(isIOS);
        }
    };

    return (
        <button
            type="button"
            onClick={handleBellClick}
            disabled={isLoading}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold transition-all border shrink-0 ${
                isSubscribed
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-red-50 hover:text-red-700 hover:border-red-300'
                    : 'bg-gray-100 hover:bg-emerald-50 text-gray-600 hover:text-emerald-700 border-gray-200'
            }`}
            title={isSubscribed ? "انقر لإيقاف الإشعارات" : "تفعيل إشعارات البلدية"}
        >
            <span className={isLoading ? "animate-spin" : isSubscribed ? "text-emerald-600" : ""}>
                {isLoading ? "⏳" : isSubscribed ? "🔔" : "🔕"}
            </span>
            <span className="hidden sm:inline">
                {isLoading ? "جاري التحديث..." : isSubscribed ? "مفعّلة" : "إشعارات"}
            </span>
            {isSubscribed && (
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse hidden sm:inline-block"></span>
            )}
        </button>
    );
}
const Header = ({ navigate, currentPage,onOpenSearch }) => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const navLinks = [
        { id: 'home', text: 'الرئيسية' },
        { id: 'about', text: 'عن رومين' },
        { id: 'news', text: 'أخبار وبيانات' },
        { id: 'activities', text: 'الأنشطة المحلية' },
        { id: 'permits', text: 'معاملات ونماذج' },
        { id: 'fees', text: 'الرسوم البلدية' },
        { id: 'emergency', text: 'دليل الطوارئ' },
{ id: 'complaints', text: 'تقديم شكوى' },  
    { id: 'surveys', text: 'الاستطلاعات' },
            { id: 'faqs', text: 'الأسئلة الشائعة' },
            { id: 'directory', text: 'دليل البلدة' },
        { id: 'contact', text: 'تواصل معنا' },
        { id: 'admin', text: 'Admin' },
    ];

    const handleNavClick = (arg1, arg2) => {
        // Prevent default browser jump if an Event object was passed
        if (arg1 && typeof arg1.preventDefault === 'function') {
            arg1.preventDefault();
        }

        // Detect whether the page name came in arg1 or arg2
        const targetPage = typeof arg1 === 'string' ? arg1 : arg2;
        if (targetPage && typeof navigate === 'function') {
            navigate(targetPage);
        }
        setIsMobileMenuOpen(false);
    };

    return (
        <header className="bg-white/80 backdrop-blur-lg shadow-sm sticky top-0 z-50">
            <div className="container mx-auto px-6 py-3 flex justify-between items-center">
                <a href="#home" className="flex items-center space-x-2" onClick={(e) => handleNavClick(e, 'home')}>
                    <OfficialLogo className="h-14 w-auto" />
                    <span className="mr-10 text-xl font-bold text-gray-800 hidden sm:inline">بلدية رومين</span>
                </a>
                <nav className="hidden md:flex items-center space-x-2">
                    {navLinks.map(link => (
                        <a 
                            key={link.id} 
                            href={`#${link.id}`} 
                            onClick={(e) => handleNavClick(e, link.id)} 
                            className={`px-4 py-2 rounded-md text-base font-semibold transition-all duration-300 ${
                                currentPage === link.id 
                                    ? 'bg-emerald-600 text-white shadow-md' 
                                    : 'text-gray-600 hover:bg-emerald-50 hover:text-emerald-700'
                            }`}
                        >
                            {link.text}
                        </a>
                    ))}
                </nav>
{/* Action Buttons: Search + Mobile Menu Trigger */}
                <div className="flex items-center gap-3">
                    <button 
                        onClick={onOpenSearch}
                        className="flex items-center gap-1.5 bg-gray-100 hover:bg-emerald-50 text-gray-600 hover:text-emerald-700 px-3 py-2 rounded-xl text-sm font-semibold transition-all border border-gray-200"
                        title="البحث في الموقع"
                    >
                        <span>🔍</span>
                        <span className="hidden sm:inline">بحث</span>
                    </button>
{/* --- NOTIFICATION BELL BUTTON --- */}
    <NotificationBellButton />


                    <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden text-gray-700">
                        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7"></path></svg>
                    </button>
                </div>
            </div>
            {isMobileMenuOpen && (
                <div className="md:hidden px-6 pb-4 space-y-2 text-center animate-fadeIn">
                    {navLinks.map(link => (
                        <a 
                            key={link.id} 
                            href={`#${link.id}`} 
                            onClick={(e) => handleNavClick(e, link.id)} 
                            className={`block py-2 rounded-md text-lg font-semibold ${currentPage === link.id ? 'bg-emerald-600 text-white' : 'text-gray-700'}`}
                        >
                            {link.text}
                        </a>
                    ))}
                </div>
            )}
        </header>
    );
};

const Footer = () => (
    <footer className="bg-gray-800 text-white">
        <div className="container mx-auto px-6 py-8 text-center flex flex-col items-center">
            <OfficialLogoInvert className="h-24 w-auto mb-4" />
            <p className="text-gray-300">&copy; {new Date().getFullYear()} بلدية رومين. جميع الحقوق محفوظة.</p>
            <p className="text-sm text-gray-400 mt-2">تم التصميم بعناية لمجتمعنا.</p>
        </div>
    </footer>
);

// --- HELPER: ANIMATED NUMBER COMPONENT ---
const AnimatedNumber = ({ end, duration = 2000, suffix = "", decimals = 0 }) => {
    const [count, setCount] = useState(0);
    const countRef = useRef(null);
    const [hasStarted, setHasStarted] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasStarted) {
                    setHasStarted(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.5 }
        );

        if (countRef.current) {
            observer.observe(countRef.current);
        }

        return () => observer.disconnect();
    }, [hasStarted]);

    useEffect(() => {
        if (!hasStarted) return;

        let startTime;
        let animationFrame;

        const step = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            const easeProgress = 1 - Math.pow(1 - progress, 3);
            
            setCount(easeProgress * end);

            if (progress < 1) {
                animationFrame = requestAnimationFrame(step);
            }
        };

        animationFrame = requestAnimationFrame(step);

        return () => cancelAnimationFrame(animationFrame);
    }, [hasStarted, end, duration]);

    const formattedNumber = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(count);

    return (
        <span ref={countRef} className="tabular-nums">
            {formattedNumber}{suffix}
        </span>
    );
};

// --- QUICK STATS COMPONENT ---
const QuickStats = () => {
    const stats = [
        { 
            id: 1, 
            label: "المساحة الإجمالية", 
            number: 5.4, 
            suffix: " كلم²",
            decimals: 1,
            icon: <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 7m0 13V7m0 0L9 7" /></svg>
        },
        { 
            id: 2, 
            label: "عدد السكان", 
            number: 4500, 
            suffix: " نسمة", 
            decimals: 0,
            icon: <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
        },
        { 
            id: 3, 
            label: "الوحدات السكنية", 
            number: 850, 
            suffix: " وحدة",
            decimals: 0, 
            icon: <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
        },
        { 
            id: 4, 
            label: "المؤسسات التجارية", 
            number: 45, 
            suffix: " محل",
            decimals: 0, 
            icon: <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
        },
    ];

    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16 container mx-auto px-4">
            {stats.map((stat) => (
                <div key={stat.id} className="bg-white p-6 rounded-xl shadow-lg border-b-4 border-emerald-500 text-center transform transition duration-300 hover:-translate-y-1 hover:shadow-xl group">
                    <div className="text-emerald-600 flex justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                        {stat.icon}
                    </div>
                    <h4 className="text-3xl font-bold text-gray-800 mb-1">
                        <AnimatedNumber 
                            end={stat.number} 
                            suffix={stat.suffix} 
                            decimals={stat.decimals} 
                        />
                    </h4>
                    <p className="text-gray-500 font-medium">{stat.label}</p>
                </div>
            ))}
        </div>
    );
};

const HomePage = ({ navigate, waterSchedule,obituaries= [] }) => (
    <PageWrapper>
        <div className="relative rounded-xl overflow-hidden h-[500px] mb-16 shadow-2xl">
            <img src={homeImage} alt="غابات السنديان المحيطة برومين" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex flex-col justify-end items-center text-white text-center p-10">
                <h1 className="text-4xl md:text-6xl font-bold mb-4">أهلاً بكم في رومين</h1>
                <p className="text-lg md:text-xl max-w-2xl">بلدة التاريخ العريق، والمجتمع النابض بالحياة، والطبيعة الهادئة في قلب جنوب لبنان.</p>
            </div>
        </div>

        
       <div className="mb-16">
            <QuickStats />
        </div>
        <CondolencesWidget obituaries={obituaries} navigate={navigate} />
        <WaterScheduleWidget schedule={waterSchedule} />
        <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">خدمات البلدية</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <ServiceCard 
              icon="news" 
              title="آخر الأخبار" 
              description="ابق على اطلاع بآخر الإعلانات الرسمية وأخبار البلدة." 
              onClick={() => navigate('news')} 
            />
            <ServiceCard 
              icon="docs" 
              title="معاملات ونماذج" 
              description="يمكنك الوصول إلى جميع الطلبات والتصاريح البلدية اللازمة." 
              onClick={() => navigate('permits')} 
            />
            <ServiceCard 
              icon="fees"
              title="الرسوم البلدية" 
              description="الاستعلام عن الرسوم البلدية المتوجبة على القيمة التأجيرية."
              onClick={() => navigate('fees')} 
            />
            <ServiceCard 
              icon="surveys"
              title="شكاوى واستمارات" 
              description="شارك برأيك وقدم ملاحظاتك لتحسين بلدتنا."
              onClick={() => navigate('surveys')} 
            />
        </div>
    </PageWrapper>
);

const ServiceCard = ({ icon, title, description, onClick }) => {
    const icons = {
        news: <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3h2m-4 3H9m-4 4h2m-4 4h2m4-8h2m-4 4h2m-4 4h2m4-8h2m-4 4h2m-4 4h2" /></svg>,
        docs: <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
        contact: <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
        surveys: <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>,
        fees: <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
    };

    const handleClick = (e) => {
        if (e && typeof e.preventDefault === 'function') {
            e.preventDefault();
        }
        if (typeof onClick === 'function') {
            onClick();
        }
    };

    return (
        <div 
            onClick={handleClick}
            className="bg-white p-8 rounded-xl shadow-lg text-center transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 border-t-4 border-transparent hover:border-emerald-500 cursor-pointer"
        >
            <div className="text-emerald-600 mx-auto mb-4">{icons[icon]}</div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">{title}</h3>
            <p className="text-gray-500 mb-6">{description}</p>
            <button type="button" className="font-semibold text-emerald-600 hover:text-emerald-800 transition-colors">
                اعرف المزيد &larr;
            </button>
        </div>
    );
};

const AboutPage = () => (
    <PageWrapper>
        <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">عن رومين</h2>
        <div className="bg-white p-8 md:p-12 rounded-xl shadow-xl overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-12">
                <div className="md:col-span-2 flex flex-col items-center space-y-6 order-first md:order-first">
                    <img src={historicalRoumine} alt="رومين قديماً" className="w-full h-auto rounded-lg shadow-md" loading="lazy" />
                    <img src={roumineRoad} alt="رومين في منتصف القرن" className="w-full h-auto rounded-lg shadow-md" loading="lazy" />
                    <img src={modernRoumine} alt="بلدتنا اليوم" className="w-full h-auto rounded-lg shadow-md" loading="lazy" />
                </div>

                <div className="md:col-span-3 order-last md:order-last">
                    <h3 className="text-3xl font-bold text-emerald-700 mb-4">تراث بلدتنا</h3>
                    <div className="text-gray-600 leading-relaxed mb-4 text-lg">
                        <p className="mb-4">
                            تقع بلدة رومين على تلال منبسطة في قضاء النبطية بجنوب لبنان، حاملةً في اسمها وتضاريسها شذرات من تاريخ منطقة جبل عامل العريقة. يستند تاريخها إلى مصادر متعددة، أبرزها المعاجم الجغرافية التاريخية لمنطقة جبل عامل، بالإضافة إلى الروايات الشفهية والآثار المكتشفة في أراضيها.
                        </p>

                        <h4 className="text-xl font-semibold text-gray-800 mt-6 mb-3">أصل التسمية ودلالاتها القديمة</h4>
                        <p className="mb-4">
                            يُرجّح المؤرخون، وعلى رأسهم الشيخ سليمان ظاهر في كتابه "معجم قرى جبل عامل"، أن اسم "رومين" يعود إلى أصل سرياني هو "Rawmin" (ܪܘܡܝܢ)، والذي يعني "الهضاب" أو "المرتفعات". يتطابق هذا التفسير مع الطبيعة الجغرافية للبلدة التي تقوم على مجموعة من التلال.
                        </p>
                        <p className="mb-4">
                            وإلى جانب هذا التفسير العلمي، تتداول رواية شعبية أن الاسم مرتبط بوجود روماني قديم في المنطقة، وأن "رومين" هي تحريف لكلمة "رومان". يدعم هذه الرواية وجود بعض الآثار في البلدة، مثل المغاور المدفنية والنواويس المحفورة في الصخر في منطقة "ظهر النسور" شمال البلدة، بالإضافة إلى أسطورة محلية تتحدث عن مغارة دُفن فيها ملك وملكة من العهد الروماني. وعلى الرغم من شيوع هذه الرواية، لم يتم العثور على آثار كبرى تؤكد وجود مدينة رومانية متكاملة في الموقع.
                        </p>

                        <h4 className="text-xl font-semibold text-gray-800 mt-6 mb-3">رومين في العهد العثماني: الإقطاع والملكية</h4>
                        <p className="mb-4">
                            كغيرها من قرى جبل عامل، خضعت رومين لنظام الإقطاع الذي كان سائدًا في العهد العثماني. وتشير المصادر التاريخية إلى أن أراضي البلدة كانت في فترة من الفترات مملوكة لعائلات إقطاعية نافذة في المنطقة. فقد ذكر الشيخ سليمان ظاهر أن قسماً كبيراً من أراضي رومين كان ملكاً لـ "حسن أفندي والحاج حسين الزين"، وهما من عائلة "آل الزين" التي كانت تمتلك نفوذاً واسعاً وأملاكاً شاسعة في عموم جبل عامل.
                        </p>
                        <p className="mb-4">
                            ولا يزال في البلدة حتى اليوم دار قديمة ذات طابع تراثي مسقوفة بالقرميد، يُقال إنها كانت ملكاً لإسماعيل الزين، أحد وجهاء العائلة. مع مرور الزمن وتغير الأنظمة السياسية والاجتماعية في لبنان، انتقلت ملكية هذه الأراضي تدريجياً من العائلات الإقطاعية إلى أهالي البلدة وسكانها.
                        </p>

                        <h4 className="text-xl font-semibold text-gray-800 mt-6 mb-3">التاريخ الاجتماعي والاقتصادي</h4>
                        <p className="mb-4">
                            اعتمد اقتصاد رومين تقليدياً على الزراعة، مستفيداً من أراضيها الخصبة ومناخها المتوسطي. وقد اشتهرت بزراعة الزيتون، الذي لا تزال أشجاره المعمرة تغطي أجزاء واسعة من أراضيها، بالإضافة إلى الكرمة والتين واللوز وبعض الزراعات الموسمية.
                        </p>
                        <p className="mb-4">
                            في العقود الأخيرة من القرن العشرين، برزت في رومين صناعة حرفية دقيقة، وهي صياغة الذهب والفضة، حيث عُرفت البلدة بوجود عدد من المعامل والورش التي عمل فيها أبناؤها واكتسبوا شهرة في هذا المجال. 
                        </p>

                        <h4 className="text-xl font-semibold text-gray-800 mt-6 mb-3">رومين في التاريخ الحديث</h4>
                        <p className="mb-4">
                            إدارياً، كانت رومين تتبع قديماً "إقليم التفاح"، وهو أحد التقسيمات التاريخية لمنطقة جبل عامل. أما في التقسيمات الإدارية الحديثة للدولة اللبنانية، فأصبحت جزءاً من قضاء النبطية في محافظة النبطية.
                        </p>
                        <p className="mb-4">
                            وكحال معظم القرى اللبنانية، شهدت رومين هجرة كبيرة لأبنائها، سواء إلى العاصمة بيروت أو إلى خارج لبنان، بحثاً عن فرص العلم والعمل، مما أثر على بنيتها الديموغرافية. ورغم ذلك، لا تزال البلدة مرتبطة بتاريخها وجذورها، محافظةً على طابعها القروي الهادئ ضمن السياق التاريخي والثقافي لمنطقة جبل عامل.
                        </p>
                        <div className="flex justify-center mt-12">
                            <OfficialLogo className="text-center h-32 w-auto" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </PageWrapper>
);

const NewsPage = ({ news = [],navigate,setSelectedActivityId }) => {

    const [selectedFile, setSelectedFile] = useState(null);
    const [zoomLevel, setZoomLevel] = useState(1);

    // Helper: Safely converts any value to a lowercased string
    const safeStr = (val) => (typeof val === 'string' ? val.toLowerCase() : '');

    const handleNewsClick = (item) => {
        if (!item) return;

        const titleText = safeStr(item.title);
        const isActivity = item.fileType === 'activity' || Boolean(item.activityId) || titleText.includes('نشاط بلدي');

        if (isActivity) {
            if (item.activityId && typeof setSelectedActivityId === 'function') {
                setSelectedActivityId(item.activityId);
            }
            if (typeof navigate === 'function') {
                navigate('activities');
                return;
            }
        }

        openPreview(item.imageUrl, item.title, item.fileType);
    };

    const openPreview = (fileUrl, title, type) => {
        const urlStr = typeof fileUrl === 'string' ? fileUrl : '';
        const lowerUrl = safeStr(urlStr);

        let detectedType = type;
        if (!detectedType || detectedType === 'activity') {
            if (lowerUrl.includes('.pdf')) detectedType = 'pdf';
            else if (lowerUrl.includes('.xls') || lowerUrl.includes('.xlsx') || lowerUrl.includes('.csv')) detectedType = 'excel';
            else detectedType = 'image';
        }

        setSelectedFile({ url: urlStr, title: typeof title === 'string' ? title : '', type: detectedType });
        setZoomLevel(1);
    };

    const closePreview = () => {
        setSelectedFile(null);
        setZoomLevel(1);
    };

    const handleZoomIn = () => setZoomLevel((prev) => Math.min(prev + 0.5, 3));
    const handleZoomOut = () => setZoomLevel((prev) => Math.max(prev - 0.5, 1));

    const handleDownload = async (fileUrl, title, type) => {
        const urlStr = typeof fileUrl === 'string' ? fileUrl : '';
        if (!urlStr) return;

        try {
            const response = await fetch(urlStr);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            
            const ext = type === 'pdf' ? 'pdf' : type === 'excel' ? 'xlsx' : 'jpg';
            link.download = `${title || 'attachment'}.${ext}`;
            
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            window.open(urlStr, '_blank');
        }
    };

    const renderThumbnail = (item) => {
        if (!item) return null;

        const imgUrlStr = typeof item.imageUrl === 'string' ? item.imageUrl : '';
        const thumbUrlStr = typeof item.thumbnailUrl === 'string' ? item.thumbnailUrl : '';
        const lowerUrl = safeStr(imgUrlStr);

        const type = item.fileType || (lowerUrl.includes('.pdf') ? 'pdf' : (lowerUrl.includes('.xls') || lowerUrl.includes('.xlsx') ? 'excel' : 'image'));
        
        const hasValidThumbnail = thumbUrlStr && (
            thumbUrlStr.includes('.webp') || 
            thumbUrlStr.includes('thumb_') || 
            thumbUrlStr.includes('opt_')
        );

        if (type === 'image' || type === 'activity' || hasValidThumbnail) {
            return (
                <img 
                    src={thumbUrlStr || imgUrlStr} 
                    alt={item.title || ''} 
                    loading="lazy"
                    decoding="async"
                    className="object-contain max-h-96 w-full h-auto group-hover:scale-105 transition-transform duration-300 rounded-md shadow-sm" 
                />
            );
        }

        if (type === 'pdf') {
            return (
                <div className="w-full h-56 md:h-full bg-red-50 border border-red-200 flex flex-col items-center justify-center p-6 rounded-xl text-red-700 min-h-[200px]">
                    <div className="bg-red-600 text-white p-3 rounded-full mb-3 shadow-md">
                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <span className="font-bold text-lg text-red-900">مستند PDF رسمـي</span>
                    <span className="text-sm font-semibold text-red-600 mt-1">{item.title}</span>
                    <span className="text-xs text-red-400 mt-2">انقر للمعاينة والتنزيل</span>
                </div>
            );
        }

        if (type === 'excel') {
            return (
                <div className="w-full h-56 md:h-full bg-emerald-50 border border-emerald-200 flex flex-col items-center justify-center p-6 rounded-xl text-emerald-800 min-h-[200px]">
                    <div className="bg-emerald-600 text-white p-3 rounded-full mb-3 shadow-md">
                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    </div>
                    <span className="font-bold text-lg text-emerald-950">ملف EXCEL رسمـي</span>
                    <span className="text-sm font-semibold text-emerald-700 mt-1">{item.title}</span>
                    <span className="text-xs text-emerald-500 mt-2">انقر للمعاينة والتنزيل</span>
                </div>
            );
        }

        return (
            <img 
                src={imgUrlStr} 
                alt={item.title || ''} 
                className="object-contain max-h-96 w-full h-auto rounded-md" 
            />
        );
    };

    return (
        <PageWrapper>
            <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">أخبار وبيانات رسمية</h2>
            
            <div className="space-y-8">
                {news.length > 0 ? (
                    news.map((item) => {
                        const titleText = safeStr(item?.title);
                        const isActivity = item?.fileType === 'activity' || Boolean(item?.activityId) || titleText.includes('نشاط بلدي');

                        return (
                            <div key={item.id} className="bg-white rounded-xl shadow-lg overflow-hidden transition-shadow hover:shadow-2xl flex flex-col md:flex-row md:items-stretch">
                                <div 
                                    className="w-full md:w-1/3 p-3 bg-gray-50 relative group cursor-pointer overflow-hidden flex items-center justify-center border-b md:border-b-0 md:border-l border-gray-100 min-h-[220px]"
                                    onClick={() => handleNewsClick(item)}
                                >
                                    {renderThumbnail(item)}
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center text-white font-semibold gap-2 rounded-md">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                        <span>{isActivity ? 'عرض ألبوم النشاط' : 'معاينة المرفق'}</span>
                                    </div>
                                </div>

                                <div className="w-full md:w-2/3 p-6 md:p-8 flex flex-col justify-center">
                                    <p className="text-sm text-gray-500 mb-2">{item.date}</p>
                                    <h3 
                                        className="text-2xl font-bold text-emerald-800 mb-3 cursor-pointer hover:underline"
                                        onClick={() => handleNewsClick(item)}
                                    >
                                        {item.title}
                                    </h3>
                                    <p className="text-gray-600 leading-relaxed">{item.content}</p>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <p className="text-center text-gray-600 text-lg">لا توجد أخبار حالياً.</p>
                )}
            </div>

            {selectedFile && (
                <div 
                    className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex flex-col justify-between items-center p-2 md:p-4 animate-fadeIn"
                    onClick={closePreview}
                >
                    <div 
                        className="w-full max-w-6xl flex justify-between items-center text-white z-10 py-2 px-2"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h4 className="text-lg font-semibold truncate max-w-xs md:max-w-xl">
                            {selectedFile.title}
                        </h4>
                        <button 
                            onClick={closePreview}
                            className="bg-white/20 hover:bg-white/30 text-white rounded-full p-2 transition-colors"
                        >
                            ✕
                        </button>
                    </div>

                    <div 
                        className="flex-1 w-full max-w-6xl flex items-center justify-center overflow-auto p-1 md:p-2"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {selectedFile.type === 'image' ? (
                            <div 
                                className="transition-transform duration-200 ease-out flex items-center justify-center w-full h-full"
                                style={{ transform: `scale(${zoomLevel})` }}
                            >
                                <img 
                                    src={selectedFile.url} 
                                    alt={selectedFile.title} 
                                    className="max-h-[83vh] w-full object-contain rounded-lg shadow-2xl"
                                />
                            </div>
                        ) : (
                            <iframe 
                                src={`https://docs.google.com/gview?url=${encodeURIComponent(selectedFile.url)}&embedded=true`}
                                title={selectedFile.title}
                                className="w-full h-[80vh] rounded-xl bg-white shadow-2xl border-0"
                            />
                        )}
                    </div>

                    <div 
                        className="bg-gray-800/90 backdrop-blur-md text-white px-6 py-3 rounded-full flex items-center gap-6 shadow-xl z-10 my-2"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {selectedFile.type === 'image' && (
                            <>
                                <button onClick={handleZoomOut} disabled={zoomLevel <= 1} className="hover:text-emerald-400 disabled:opacity-40 p-1">
                                    -
                                </button>
                                <span className="text-sm font-mono font-bold w-12 text-center select-none">
                                    {Math.round(zoomLevel * 100)}%
                                </span>
                                <button onClick={handleZoomIn} disabled={zoomLevel >= 3} className="hover:text-emerald-400 disabled:opacity-40 p-1">
                                    +
                                </button>
                                <div className="h-4 w-px bg-gray-600"></div>
                            </>
                        )}
                        <button 
                            onClick={() => handleDownload(selectedFile.url, selectedFile.title, selectedFile.type)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-md"
                        >
                            تحميل
                        </button>
                    </div>
                </div>
            )}
        </PageWrapper>
    );
};

// --- PROFESSIONAL PHOTO COLLAGE GRID COMPONENT ---
const ActivityCollage = ({ images = [], title, onOpenGallery }) => {
    if (!images || images.length === 0) return null;

    const displayCount = 4;
    const extraCount = images.length - displayCount;

    return (
        <div className="w-full rounded-2xl overflow-hidden shadow-md bg-gray-100 border border-gray-100">
            {images.length === 1 && (
                <div className="relative group cursor-pointer overflow-hidden h-72 md:h-96" onClick={() => onOpenGallery(0)}>
                    <img src={images[0]} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
            )}

            {images.length === 2 && (
                <div className="grid grid-cols-2 gap-1.5 h-64 md:h-80">
                    {images.map((img, idx) => (
                        <div key={idx} className="relative group cursor-pointer overflow-hidden h-full" onClick={() => onOpenGallery(idx)}>
                            <img src={img} alt={`${title} ${idx + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        </div>
                    ))}
                </div>
            )}

            {images.length === 3 && (
                <div className="grid grid-cols-3 gap-1.5 h-64 md:h-80">
                    <div className="col-span-2 relative group cursor-pointer overflow-hidden h-full" onClick={() => onOpenGallery(0)}>
                        <img src={images[0]} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                    <div className="grid grid-rows-2 gap-1.5 h-full">
                        {images.slice(1, 3).map((img, idx) => (
                            <div key={idx} className="relative group cursor-pointer overflow-hidden h-full" onClick={() => onOpenGallery(idx + 1)}>
                                <img src={img} alt={`${title} ${idx + 2}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {images.length >= 4 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 h-64 md:h-80">
                    {images.slice(0, 4).map((img, idx) => {
                        const isLast = idx === 3 && extraCount > 0;
                        return (
                            <div key={idx} className="relative group cursor-pointer overflow-hidden h-full" onClick={() => onOpenGallery(idx)}>
                                <img src={img} alt={`${title} ${idx + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                                {isLast && (
                                    <div className="absolute inset-0 bg-black/60 backdrop-blur-xs flex flex-col items-center justify-center text-white font-bold text-lg group-hover:bg-black/70 transition-colors">
                                        <span>+{extraCount}</span>
                                        <span className="text-xs font-normal">صور إضافية</span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// --- LOCAL ACTIVITIES PAGE COMPONENT ---
const LocalActivitiesPage = ({ activities, selectedActivityId, setSelectedActivityId }) => {
    const [activeGallery, setActiveGallery] = useState(null); // { images, title, activeIndex }

    useEffect(() => {
        if (selectedActivityId) {
            const element = document.getElementById(`activity-${selectedActivityId}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [selectedActivityId]);

    const openGallery = (activity, index = 0) => {
        setActiveGallery({
            images: activity.images || [activity.imageUrl],
            title: activity.title,
            activeIndex: index
        });
    };

    const nextImage = () => {
        if (!activeGallery) return;
        setActiveGallery(prev => ({
            ...prev,
            activeIndex: (prev.activeIndex + 1) % prev.images.length
        }));
    };

    const prevImage = () => {
        if (!activeGallery) return;
        setActiveGallery(prev => ({
            ...prev,
            activeIndex: (prev.activeIndex - 1 + prev.images.length) % prev.images.length
        }));
    };

    return (
        <PageWrapper>
            <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-gray-800 mb-4">الأنشطة والفعاليات المحلية</h2>
                <p className="text-gray-500 max-w-2xl mx-auto text-lg">
                    توثيق مصور لأبرز الأنشطة الثقافية، الاجتماعية، والإنمائية التي تنظمها وتستضيفها بلدية رومين.
                </p>
            </div>

            <div className="space-y-12 max-w-5xl mx-auto">
                {activities.length > 0 ? (
                    activities.map((activity) => (
                        <div 
                            key={activity.id} 
                            id={`activity-${activity.id}`}
                            className={`bg-white p-6 md:p-8 rounded-2xl shadow-lg border transition-all duration-500 ${
                                selectedActivityId === activity.id ? 'ring-2 ring-emerald-500 shadow-2xl bg-emerald-50/20' : 'border-gray-100 hover:shadow-xl'
                            }`}
                        >
                            {/* Activity Header */}
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4 border-b border-gray-100 pb-4">
                                <div>
                                    <span className="inline-block bg-emerald-100 text-emerald-800 text-xs font-bold px-3 py-1 rounded-full mb-2">
                                        نشاط بلدي
                                    </span>
                                    <h3 className="text-2xl font-bold text-gray-800">{activity.title}</h3>
                                </div>
                                <span className="text-sm font-semibold text-gray-400 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-100">
                                    📅 {activity.date}
                                </span>
                            </div>

                            {/* Description */}
                            <p className="text-gray-600 leading-relaxed mb-6 text-lg whitespace-pre-wrap">
                                {activity.description || activity.content}
                            </p>

                            {/* Photo Collage Grid */}
                            <ActivityCollage 
                                images={activity.images || [activity.imageUrl]} 
                                title={activity.title} 
                                onOpenGallery={(idx) => openGallery(activity, idx)} 
                            />
                        </div>
                    ))
                ) : (
                    <div className="text-center py-16 bg-white rounded-2xl shadow-md border border-gray-100">
                        <p className="text-gray-500 text-lg">لا توجد أنشطة محلية مضافة حالياً.</p>
                    </div>
                )}
            </div>

            {/* FULLSCREEN LIGHTBOX GALLERY MODAL */}
            {activeGallery && (
                <div 
                    className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex flex-col justify-between items-center p-4 animate-fadeIn"
                    onClick={() => setActiveGallery(null)}
                >
                    {/* Top Bar */}
                    <div className="w-full max-w-6xl flex justify-between items-center text-white z-10 py-2" onClick={e => e.stopPropagation()}>
                        <div>
                            <h4 className="text-lg font-bold truncate max-w-xs md:max-w-xl">{activeGallery.title}</h4>
                            <p className="text-xs text-gray-400">صورة {activeGallery.activeIndex + 1} من {activeGallery.images.length}</p>
                        </div>
                        <button 
                            onClick={() => setActiveGallery(null)} 
                            className="bg-white/20 hover:bg-white/30 text-white rounded-full p-2 transition-colors"
                        >
                            ✕
                        </button>
                    </div>

                    {/* Image Stage */}
                    <div className="relative flex-1 w-full max-w-5xl flex items-center justify-center p-2" onClick={e => e.stopPropagation()}>
                        <img 
                            src={activeGallery.images[activeGallery.activeIndex]} 
                            alt={activeGallery.title} 
                            className="max-h-[78vh] w-auto max-w-full object-contain rounded-xl shadow-2xl transition-all duration-300"
                        />

                        {activeGallery.images.length > 1 && (
                            <>
                                <button 
                                    onClick={prevImage}
                                    className="absolute right-4 bg-black/50 hover:bg-black/80 text-white p-3 rounded-full transition-colors backdrop-blur-xs"
                                >
                                    ❯
                                </button>
                                <button 
                                    onClick={nextImage}
                                    className="absolute left-4 bg-black/50 hover:bg-black/80 text-white p-3 rounded-full transition-colors backdrop-blur-xs"
                                >
                                    ❮
                                </button>
                            </>
                        )}
                    </div>

                    {/* Bottom Thumbnails */}
                    {activeGallery.images.length > 1 && (
                        <div className="flex gap-2 overflow-x-auto py-2 max-w-4xl z-10" onClick={e => e.stopPropagation()}>
                            {activeGallery.images.map((img, idx) => (
                                <button 
                                    key={idx} 
                                    onClick={() => setActiveGallery(prev => ({ ...prev, activeIndex: idx }))}
                                    className={`w-14 h-14 rounded-lg overflow-hidden border-2 transition-all shrink-0 ${
                                        activeGallery.activeIndex === idx ? 'border-emerald-500 scale-105' : 'border-transparent opacity-50'
                                    }`}
                                >
                                    <img src={img} alt="thumb" className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </PageWrapper>
    );
};

const PermitsPage = ({ documents }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [sortOrder, setSortOrder] = useState('asc'); // 'asc' | 'desc'
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    // Live filtering and sorting based on search input & filter selection
    const filteredDocuments = documents
        .filter(doc => {
            const title = doc.title || '';
            const matchesSearch = title.toLowerCase().includes(searchQuery.toLowerCase().trim());
            const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
            return matchesSearch && matchesCategory;
        })
        .sort((a, b) => {
            const titleA = a.title || '';
            const titleB = b.title || '';
            return sortOrder === 'asc'
                ? titleA.localeCompare(titleB, 'ar')
                : titleB.localeCompare(titleA, 'ar');
        });

    return (
        <PageWrapper>
            <h2 className="text-4xl font-bold text-center text-gray-800 mb-6">معاملات ونماذج</h2>
            <p className="text-center text-gray-500 max-w-2xl mx-auto mb-8">
                هنا يمكنك العثور على جميع النماذج اللازمة للخدمات البلدية وتنزيلها. يرجى تقديم النماذج المكتملة إلى مكتب البلدية خلال ساعات العمل الرسمية.
            </p>

            {/* Search, Sort, & Filter Control Bar */}
            <div className="bg-white p-6 rounded-xl shadow-md mb-8 max-w-4xl mx-auto">
                <div className="flex flex-col md:flex-row items-center gap-4">
                    
                    {/* Live Search Bar */}
                    <div className="relative flex-1 w-full">
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="ابحث عن نموذج أو معاملة بالاسم..."
                            className="w-full pr-11 pl-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-right text-gray-800 outline-none transition-all shadow-sm"
                        />
                        {/* Search Icon */}
                        <div className="absolute inset-y-0 right-0 pr-3.5 flex items-center pointer-events-none text-gray-400">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        {/* Clear Search Button */}
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                                title="مسح البحث"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>

                    {/* Action Buttons: Sort & Filter */}
                    <div className="flex items-center gap-3 w-full md:w-auto justify-end">
                        
                        {/* Sort Button */}
                        <button
                            onClick={() => setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))}
                            className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-4 py-3 rounded-lg transition-all border border-gray-200 shadow-sm whitespace-nowrap"
                            title="تغيير الترتيب الأبجدي"
                        >
                            <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                            </svg>
                            <span>ترتيب: {sortOrder === 'asc' ? 'أ - ي' : 'ي - أ'}</span>
                        </button>

                        {/* Filter Button */}
                        <button
                            onClick={() => setIsFilterOpen(prev => !prev)}
                            className={`flex items-center gap-2 px-4 py-3 rounded-lg font-semibold transition-all border shadow-sm whitespace-nowrap ${
                                isFilterOpen || selectedCategory !== 'all'
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-300 ring-2 ring-emerald-200'
                                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-200'
                            }`}
                        >
                            <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                            </svg>
                            <span>تصفية</span>
                        </button>
                    </div>
                </div>

                {/* Filter Menu Bar (Future Usage & Categorization) */}
                {isFilterOpen && (
                    <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-center gap-3 animate-fadeIn">
                        <span className="text-sm text-gray-600 font-medium">التصنيف:</span>
                        {[
                            { id: 'all', label: 'الكل' },
                            { id: 'رخص', label: 'رخص' },
                            { id: 'إفادات', label: 'إفادات' },
                            { id: 'تصاريح', label: 'تصاريح' }
                        ].map(category => (
                            <button
                                key={category.id}
                                onClick={() => setSelectedCategory(category.id)}
                                className={`text-sm px-3.5 py-1.5 rounded-full transition-all ${
                                    selectedCategory === category.id
                                        ? 'bg-emerald-600 text-white font-bold shadow-sm'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                            >
                                {category.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Document Results List */}
            <div className="bg-white p-8 rounded-xl shadow-xl max-w-4xl mx-auto">
                <ul className="divide-y divide-gray-200">
                    {filteredDocuments.length > 0 ? (
                        filteredDocuments.map(doc => (
                            <li key={doc.id} className="py-5 flex flex-col sm:flex-row justify-between items-center text-center sm:text-right">
                                <div className="mb-3 sm:mb-0">
                                    <h4 className="font-semibold text-lg text-gray-800">{doc.title}</h4>
                                    {doc.category && (
                                        <span className="inline-block mt-1 text-xs text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-200">
                                            {doc.category}
                                        </span>
                                    )}
                                </div>
                                <a
                                    href={doc.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-emerald-600 text-white font-bold py-2 px-6 rounded-full hover:bg-emerald-700 transition-all duration-300 shadow-md hover:shadow-lg w-full sm:w-auto transform hover:-translate-y-1"
                                >
                                    تحميل PDF
                                </a>
                            </li>
                        ))
                    ) : (
                        <div className="text-center py-8">
                            <p className="text-gray-600 text-lg">لم يتم العثور على مستندات مطابقة للبحث.</p>
                            {(searchQuery || selectedCategory !== 'all') && (
                                <button
                                    onClick={() => {
                                        setSearchQuery('');
                                        setSelectedCategory('all');
                                    }}
                                    className="mt-3 text-emerald-600 hover:text-emerald-700 font-semibold underline text-sm"
                                >
                                    إلغاء البحث والتصفية وعرض كل المستندات
                                </button>
                            )}
                        </div>
                    )}
                </ul>
            </div>
        </PageWrapper>
    );
};
const DueFeesPage = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    const handleSearch = async (e) => {
        e.preventDefault();
        const term = searchTerm.trim();
        if (!term) return;

        setIsLoading(true);
        setHasSearched(true);
        setResults([]);

        try {
            const cleanDigits = term.replace(/[\s-]/g, '');
            const feesRef = collection(db, "dueFees");
            const queries = [];

            // 1. Search by exact full name
            queries.push(getDocs(query(feesRef, where("fullName", "==", term))));

            // 2. Search by phone number (if input contains digits)
            if (/\d+/.test(cleanDigits)) {
                queries.push(getDocs(query(feesRef, where("cleanPhone", "==", cleanDigits))));
                queries.push(getDocs(query(feesRef, where("phone", "==", term))));
            }

            const snapshots = await Promise.all(queries);
            const foundMap = new Map();

            snapshots.forEach(snap => {
                snap.docs.forEach(d => foundMap.set(d.id, { id: d.id, ...d.data() }));
            });

            // Fallback: If no exact match, perform client-side prefix matching for names
            if (foundMap.size === 0 && !/\d+/.test(term)) {
                const broadSnap = await getDocs(query(feesRef, orderBy("fullName"), limit(250)));
                broadSnap.docs.forEach(d => {
                    const data = d.data();
                    if (data.fullName && data.fullName.includes(term)) {
                        foundMap.set(d.id, { id: d.id, ...data });
                    }
                });
            }

            setResults(Array.from(foundMap.values()));
        } catch (error) {
            console.error("Error searching due fees:", error);
            alert("حدث خطأ أثناء البحث، يرجى المحاولة لاحقاً.");
        } finally {
            setIsLoading(false);
        }
    };

    // Mask phone for privacy (e.g. "03 426 041" -> "03 ••• 041")
    const maskPhone = (phoneStr) => {
        if (!phoneStr || phoneStr.length < 6) return phoneStr || "غير مسجل";
        const clean = phoneStr.replace(/\s+/g, '');
        return `${clean.substring(0, 2)} ••• ${clean.slice(-3)}`;
    };

    return (
        <PageWrapper>
            {/* Page Header */}
            <div className="text-center max-w-3xl mx-auto mb-10">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-700 mb-4 shadow-sm border border-emerald-100 text-2xl">
                    🏛️
                </div>
                <h2 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">
                    الاستعلام عن الرسوم البلدية المتوجبة
                </h2>
                <p className="text-sm sm:text-base text-gray-600 mt-2 font-medium">
                    بوابة المواطن الشفافة للاطلاع على رسوم القيمة التأجيرية (المسقفات) واشتراكات جمع النفايات المنزلية.
                </p>
                <div className="w-20 h-1 bg-emerald-500 mx-auto mt-4 rounded-full"></div>
            </div>

            {/* Search Input Box */}
            <div className="bg-white p-6 sm:p-8 rounded-3xl shadow-xl border border-gray-100 max-w-2xl mx-auto mb-10">
                <form onSubmit={handleSearch} className="space-y-4">
                    <div>
                        <label className="block text-xs font-bold text-gray-700 mb-2">
البحث بالاسم الثلاثي للمكلف أو برقم الهاتف الخلوي:                        </label>
                        <div className="relative">
                            <input 
                                type="text" 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="مثال: يوسف محمد شكرون أو 03426041 " 
                                className="w-full pr-11 pl-4 py-3.5 bg-gray-50 border border-gray-300 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500 outline-none transition-all text-sm font-semibold text-gray-800"
                                required
                            />
                            <span className="absolute right-4 top-3.5 text-gray-400 text-lg">🔍</span>
                        </div>
                    </div>

                    <button 
                        type="submit" 
                        disabled={isLoading}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3.5 px-6 rounded-2xl transition-all shadow-md active:scale-95 disabled:bg-gray-300 flex items-center justify-center gap-2 text-sm"
                    >
                        {isLoading ? (
                            <>
                                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                <span>جاري البحث في السجلات الرسمية...</span>
                            </>
                        ) : (
                            <span>استعلام عن الذمة المالية</span>
                        )}
                    </button>
                </form>
            </div>

            {/* Results Section */}
            {hasSearched && (
                <div className="max-w-4xl mx-auto space-y-8 animate-fadeIn">
                    {results.length === 0 ? (
                        <div className="bg-white p-10 rounded-3xl text-center border border-gray-200 shadow-sm">
                            <span className="text-4xl block mb-3">🔍</span>
                            <h4 className="text-lg font-bold text-gray-800">لم يتم العثور على سجلات مطابقة لبحثك</h4>
                            <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
                                يرجى التأكد من كتابة الاسم الثلاثي كما هو وارد في بطاقة الهوية أو إدخال رقم الهاتف المسجل لدى البلدية.
                            </p>
                        </div>
                    ) : (
                        results.map((citizen) => (
                            <div 
                                key={citizen.id}
                                className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden"
                            >
                                {/* Taxpayer Identity Card */}
                                <div className="bg-gradient-to-r from-stone-900 via-stone-800 to-stone-900 text-white p-6 sm:p-7">
                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                        <div>
                                            <div className="flex items-center gap-2 mb-1.5">
                                                <span className="text-xs bg-white/10 px-2.5 py-0.5 rounded-full border border-white/20 text-stone-300 font-bold">
                                                    رقم المكلف: #{citizen.userId}
                                                </span>
                                                {citizen.sejel && (
                                                    <span className="text-xs bg-emerald-500/20 text-emerald-300 px-2.5 py-0.5 rounded-full border border-emerald-400/30 font-bold">
                                                        سجل: {citizen.sejel}
                                                    </span>
                                                )}
                                            </div>
                                            <h3 className="text-2xl sm:text-3xl font-black text-amber-100">
                                                {citizen.fullName}
                                            </h3>
                                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-stone-300 mt-2 font-medium">
                                                {citizen.motherName && (
                                                    <span>اسم الأم: <strong className="text-white">{citizen.motherName}</strong></span>
                                                )}
                                                {citizen.hay && (
                                                    <span>الحي: <strong className="text-white">{citizen.hay}</strong></span>
                                                )}
                                                <span>الهاتف: <strong className="text-white font-mono" dir="ltr">{maskPhone(citizen.phone)}</strong></span>
                                            </div>
                                        </div>

                                        {/* Financial Clearance Badge */}
                                        <div className={`px-4 py-2.5 rounded-2xl border text-center shadow-lg shrink-0 ${
                                            citizen.isCleared
                                                ? "bg-emerald-500/20 text-emerald-300 border-emerald-400/40"
                                                : "bg-red-500/20 text-rose-200 border-rose-400/40"
                                        }`}>
                                            <span className="text-lg block">{citizen.isCleared ? "🟢" : "🔴"}</span>
                                            <span className="text-xs font-black block mt-0.5">
                                                {citizen.isCleared ? "خالص الذمة المالية" : "متوجبات قيد السداد"}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Outstanding Breakdown or Cleared Banner */}
                                <div className="p-6 sm:p-8 space-y-6">
                                    {citizen.isCleared ? (
                                        <div className="bg-emerald-50 border border-emerald-200 p-6 rounded-2xl text-center space-y-2">
                                            <span className="text-4xl block">✨</span>
                                            <h4 className="text-lg font-bold text-emerald-900">
                                                ذمتكم المالية خالصة تجاه بلدية رومين بالكامل
                                            </h4>
                                            <p className="text-xs text-emerald-700 max-w-lg mx-auto">
                                                جميع رسوم المسقفات واشتراكات جمع النفايات مسددة حتى تاريخه. شكراً لالتزامكم بدعم الخدمات والتنمية في بلدتنا.
                                            </p>
                                        </div>
                                    ) : (
                                        <>
                                            {/* Summary Cards */}
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                                <div className="bg-stone-50 p-4 rounded-2xl border border-stone-200">
                                                    <span className="text-xs text-stone-500 block mb-1 font-bold">اشتراكات النفايات:</span>
                                                    <span className="text-lg font-black text-stone-800">
                                                        {new Intl.NumberFormat('ar-LB').format(citizen.wasteTotal || 0)} ل.ل.
                                                    </span>
                                                    <span className="text-[11px] text-stone-400 block mt-0.5">
                                                        {citizen.unpaidWaste?.length || 0} أشهر مستحقة
                                                    </span>
                                                </div>

                                                <div className="bg-stone-50 p-4 rounded-2xl border border-stone-200">
                                                    <span className="text-xs text-stone-500 block mb-1 font-bold">رسوم المسقفات:</span>
                                                    <span className="text-lg font-black text-stone-800">
                                                        {new Intl.NumberFormat('ar-LB').format(citizen.mosakafatTotal || 0)} ل.ل.
                                                    </span>
                                                    <span className="text-[11px] text-stone-400 block mt-0.5">
                                                        {citizen.unpaidMosakafat?.length || 0} عقارات / سنوات
                                                    </span>
                                                </div>

                                                <div className="bg-rose-50 p-4 rounded-2xl border border-rose-200 sm:col-span-1">
                                                    <span className="text-xs text-rose-700 block mb-1 font-bold">المجموع الإجمالي المتوجب:</span>
                                                    <span className="text-xl font-black text-rose-900">
                                                        {new Intl.NumberFormat('ar-LB').format(citizen.totalDue || 0)} ل.ل.
                                                    </span>
                                                    <span className="text-[11px] text-rose-600 font-bold block mt-0.5">
                                                        واجب السداد في الصندوق
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Unpaid Waste Months Breakdown */}
                                            {citizen.unpaidWaste && citizen.unpaidWaste.length > 0 && (
                                                <div className="bg-white rounded-2xl border border-stone-200 p-5 space-y-3">
                                                    <div className="flex items-center justify-between border-b pb-2">
                                                        <h4 className="text-sm font-bold text-stone-800 flex items-center gap-1.5">
                                                            <span>🗑️</span>
                                                            <span>بيان الأشهر غير المسددة (اشتراك جمع ومعالجة النفايات):</span>
                                                        </h4>
                                                        <span className="text-xs font-semibold text-stone-500">
                                                            المجموع: {new Intl.NumberFormat('ar-LB').format(citizen.wasteTotal)} ل.ل.
                                                        </span>
                                                    </div>

                                                    <div className="flex flex-wrap gap-2 pt-1">
                                                        {citizen.unpaidWaste.map((w, idx) => (
                                                            <div 
                                                                key={idx}
                                                                className="bg-amber-50 border border-amber-200 text-amber-900 px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-xs"
                                                            >
                                                                <span>📅</span>
                                                                <span>{w.label}</span>
                                                                <span className="text-stone-400 font-normal">|</span>
                                                                <span className="text-amber-800 font-black">{new Intl.NumberFormat('ar-LB').format(w.amount)} ل.ل.</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Unpaid Mosakafat (Property Taxes) */}
                                            {citizen.unpaidMosakafat && citizen.unpaidMosakafat.length > 0 && (
                                                <div className="bg-white rounded-2xl border border-stone-200 p-5 space-y-3">
                                                    <div className="flex items-center justify-between border-b pb-2">
                                                        <h4 className="text-sm font-bold text-stone-800 flex items-center gap-1.5">
                                                            <span>🏡</span>
                                                            <span>بيان رسوم المسقفات والقيمة التأجيرية للعقارات:</span>
                                                        </h4>
                                                        <span className="text-xs font-semibold text-stone-500">
                                                            المجموع: {new Intl.NumberFormat('ar-LB').format(citizen.mosakafatTotal)} ل.ل.
                                                        </span>
                                                    </div>

                                                    <div className="overflow-x-auto">
                                                        <table className="w-full text-right text-xs">
                                                            <thead>
                                                                <tr className="border-b text-stone-500 font-bold">
                                                                    <th className="py-2 px-3">رقم العقار / القسم</th>
                                                                    <th className="py-2 px-3">نوع الإشغال</th>
                                                                    <th className="py-2 px-3">سنة الرسم</th>
                                                                    <th className="py-2 px-3">القيمة المتوجبة</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-stone-100">
                                                                {citizen.unpaidMosakafat.map((m, idx) => (
                                                                    <tr key={idx} className="hover:bg-stone-50/60 font-medium">
                                                                        <td className="py-2.5 px-3 font-mono font-bold text-stone-800">{m.propertyNumber || "عقار عام"}</td>
                                                                        <td className="py-2.5 px-3">{m.type}</td>
                                                                        <td className="py-2.5 px-3 font-bold">{m.year}</td>
                                                                        <td className="py-2.5 px-3 font-bold text-stone-900">
                                                                            {new Intl.NumberFormat('ar-LB').format(m.amount)} ل.ل.
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Payment Instructions & Municipal Contact */}
                                            <div className="bg-stone-50 p-4 rounded-2xl border border-stone-200 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-stone-600">
                                                <div>
                                                    <p className="font-bold text-stone-800 mb-0.5">
                                                        طريقة التسديد: صندوق بلدية رومين - مبنى القصر البلدي
                                                    </p>
                                                    <p>دوام الاستقبال: الاثنين إلى الجمعة من 8:00 صباحاً حتى 2:00 ظهراً.</p>
                                                </div>

                                                <a 
                                                    href={`https://wa.me/96170681711?text=${encodeURIComponent(`السلام عليكم، استفسار بشأن الرسوم البلدية للمكلف: ${citizen.fullName} (رقم المكلف: ${citizen.userId})`)}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-2 rounded-xl transition-all shadow-sm flex items-center gap-1.5 shrink-0"
                                                >
                                                    <span>💬</span>
                                                    <span>مراجعة الجباية والصندوق</span>
                                                </a>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </PageWrapper>
    );
};

const ContactPage = () => {
    const form = useRef();
    const [statusMessage, setStatusMessage] = useState("");
    const [isError, setIsError] = useState(false);
    const [isSending, setIsSending] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSending(true);
        setStatusMessage("");
        setIsError(false);

        const formData = new FormData(form.current);
        const name = formData.get('name');
        const email = formData.get('email');
        const message = formData.get('message');
        
        if (!name || !email || !message) {
            setStatusMessage("يرجى ملء جميع الحقول.");
            setIsError(true);
            setIsSending(false);
            return;
        }

        try {
            await addDoc(collection(db, "messages"), {
                name: name,
                email: email,
                message: message,
                createdAt: serverTimestamp()
            });

            if (process.env.REACT_APP_EMAILJS_SERVICE_ID) {
                await emailjs.sendForm(
                    process.env.REACT_APP_EMAILJS_SERVICE_ID,
                    process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
                    form.current,
                    process.env.REACT_APP_EMAILJS_PUBLIC_KEY
                ).catch(err => console.warn("EmailJS failed, but message was saved to database:", err));
            }

            setStatusMessage("تم إرسال رسالتك بنجاح!");
            setIsError(false);
            form.current.reset();
        } catch (err) {
            console.error('Failed to submit message:', err);
            setStatusMessage("فشل إرسال الرسالة. الرجاء المحاولة مرة أخرى.");
            setIsError(true);
        } finally {
            setIsSending(false);
        }
    };

    return (
        <PageWrapper>
            <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">تواصل معنا</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div className="bg-white p-8 rounded-xl shadow-xl">
                    <h3 className="text-3xl font-bold text-emerald-700 mb-6">مكتب البلدية</h3>
                    <div className="space-y-6 text-gray-600 text-lg">
                        <p className="flex items-start"><svg className="w-7 h-7 ml-4 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg><span>مدخل القرية الجنوبي مقابل ثانوية رومين الرسمية ، رومين ، محافظة النبطية ، لبنان</span></p>
                        <p className="flex items-center"><svg className="w-7 h-7 ml-4 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg><span dir="ltr" className="inline-block">70 681 711</span></p>
                        <p className="flex items-center"><svg className="w-7 h-7 ml-4 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg><span>baladiyat.roumine@gmail.com</span></p>
                        <p className="flex items-center">
        <svg className="w-7 h-7 ml-4 text-emerald-500 flex-shrink-0 fill-current" viewBox="0 0 24 24">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
        <a 
            href="https://www.facebook.com/share/1Ji8cfe6dP/" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="hover:text-emerald-600 transition-colors font-medium"
        >
            بلدية رومين Roumine Municipality
        </a>
    </p>
                        
                        <hr className="my-6 border-gray-200" />
                        <h4 className="font-semibold text-xl text-gray-800">ساعات العمل:</h4>
                        <p>الاثنين - الجمعة: 8:00 صباحًا - 2:00 ظهراً</p>
                        <p>السبت - الأحد: مغلق</p>
                    </div>
                    <div className="mt-8">
                        <h4 className="font-semibold text-xl text-gray-800 mb-4">موقعنا على الخريطة:</h4>
                        <div className="rounded-lg overflow-hidden shadow-md">
                            <iframe
                                title="Roumine Municipality Location"
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3319.4673680979667!2d35.43841808486987!3d33.722513980703816!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1516f882199b50e3%3A0xc3b8a3e7e81b6c7a!2sRoumine!5e0!3m2!1sen!2slb!4v1663152206673!5m2!1sen!2slb"
                                width="100%"
                                height="300"
                                style={{ border: 0 }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            ></iframe>
                        </div>
                    </div>
                </div>
                <div className="bg-white p-8 rounded-xl shadow-xl">
                    <h3 className="text-3xl font-bold text-emerald-700 mb-6">أرسل لنا رسالة</h3>
                    <form ref={form} onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="name" className="block text-gray-700 font-semibold mb-2">الاسم الكامل</label>
                            <input type="text" id="name" name="name" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-shadow" required />
                        </div>
                        <div>
                            <label htmlFor="email" className="block text-gray-700 font-semibold mb-2">البريد الإلكتروني</label>
                            <input type="email" id="email" name="email" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-shadow" required />
                        </div>
                        <div>
                            <label htmlFor="message" className="block text-gray-700 font-semibold mb-2">الرسالة</label>
                            <textarea id="message" name="message" rows="5" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-shadow" required></textarea>
                        </div>
                        <button type="submit" disabled={isSending} className="w-full bg-emerald-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-emerald-700 transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-1 disabled:bg-emerald-300">
                             {isSending ? 'جار الإرسال...' : 'إرسال الرسالة'}
                        </button>
                        {statusMessage && (
                            <p className={`text-center font-medium ${isError ? 'text-red-600' : 'text-green-600'}`}>
                                {statusMessage}
                            </p>
                        )}
                    </form>
                </div>
            </div>
        </PageWrapper>
    );
};

const ComplaintsAndSurveysPage = ({ surveys, addComplaint, view = 'complaints', navigate }) => {
    const [name, setName] = useState('');
    const [contact, setContact] = useState('');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [isError, setIsError] = useState(false);

    const isComplaintsOnly = view === 'complaints';
    const isSurveysOnly = view === 'surveys';

const handleComplaintSubmit = async (e) => {
    e.preventDefault();
    
    // 1. Check required fields
    if (!name.trim() || !contact.trim() || !subject.trim() || !message.trim()) {
        setStatus("يرجى ملء جميع الحقول المطلوبة.");
        setIsError(true);
        return;
    }

    // 2. Clean up spaces and dashes
    const cleanPhone = contact.replace(/[\s-]/g, '');

    // 3. Lebanese mobile regex (supports: 70xxxxxx, 03xxxxxx, 3xxxxxx, and +961/00961 prefixes)
    const lebaneseMobileRegex = /^(\+?961|00961)?(0?3|70|71|76|78|79|81)\d{6}$/;

    if (!lebaneseMobileRegex.test(cleanPhone)) {
        setStatus("الرجاء إدخال رقم هاتف خلوي لبناني صحيح (مثال: 70123456 أو 03123456).");
        setIsError(true);
        return;
    }
    
    try {
        // Save cleaned phone number
        await addComplaint({ 
            name: name.trim(), 
            contact: cleanPhone, 
            subject: subject.trim(), 
            message: message.trim() 
        });

        // Send email notification via EmailJS
        if (process.env.REACT_APP_EMAILJS_SERVICE_ID) {
            await emailjs.send(
                process.env.REACT_APP_EMAILJS_SERVICE_ID,
                process.env.REACT_APP_EMAILJS_COMPLAINT_TEMPLATE_ID,
                {
                    name: name.trim(),
                    contact: cleanPhone,
                    subject: subject.trim(),
                    message: message.trim(),
                    to_email: 'baladiyat.roumine@gmail.com',
                },
                process.env.REACT_APP_EMAILJS_PUBLIC_KEY
            ).catch(err => console.warn("Email notification failed, but complaint was saved to database:", err));
        }

        setStatus("تم إرسال الشكوى بنجاح. شكراً لمساهمتكم.");
        setIsError(false);
        setName('');
        setContact('');
        setSubject('');
        setMessage('');
    } catch (error) {
        console.error("Error submitting complaint: ", error);
        setStatus("حدث خطأ أثناء إرسال الشكوى. يرجى المحاولة مرة أخرى.");
        setIsError(true);
    }
};

    return (
        <PageWrapper>
            {/* Header & View Switcher */}
            <div className="text-center mb-10">
                <h2 className="text-4xl font-bold text-gray-800 mb-3">
                    {isComplaintsOnly ? 'تقديم شكوى أو اقتراح' : 'استطلاعات واستبيانات'}
                </h2>
                <p className="text-gray-500 max-w-2xl mx-auto text-lg mb-6">
                    {isComplaintsOnly 
                        ? 'منصتكم للتواصل المباشر مع المجلس البلدي لتقديم الشكاوى والمتابعة بسرية.'
                        : 'شاركونا آراءكم وساهموا في اتخاذ القرارات التنموية عبر الاستبيانات الرسمية.'}
                </p>

                {/* Sub-Tab Navigation Switcher */}
                <div className="inline-flex p-1.5 bg-gray-200/80 rounded-full shadow-inner border border-gray-200">
                    <button 
                        onClick={() => navigate('complaints')}
                        className={`px-6 py-2 rounded-full font-bold text-sm transition-all duration-300 ${
                            isComplaintsOnly ? 'bg-emerald-600 text-white shadow-md' : 'text-gray-600 hover:text-emerald-700'
                        }`}
                    >
                        📝 تقديم شكوى
                    </button>
                    <button 
                        onClick={() => navigate('surveys')}
                        className={`px-6 py-2 rounded-full font-bold text-sm transition-all duration-300 ${
                            isSurveysOnly ? 'bg-emerald-600 text-white shadow-md' : 'text-gray-600 hover:text-emerald-700'
                        }`}
                    >
                        📊 الاستطلاعات
                    </button>
                </div>
            </div>

            <div className="max-w-3xl mx-auto">
                {/* --- COMPLAINTS SECTION ONLY --- */}
                {isComplaintsOnly && (
                    <div className="bg-white p-6 md:p-8 rounded-2xl shadow-xl border border-gray-100 animate-fadeIn">
                        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
                            <div>
                                <h3 className="text-2xl font-bold text-gray-800">نموذج تقديم الشكاوى</h3>
                                <p className="text-sm text-gray-500 mt-1">سيتم التعامل مع رسالتك بسرية تامة ومتابعتها</p>
                            </div>
                            <div className="bg-red-50 p-3 rounded-full text-red-500">
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                        </div>

                        {/* WhatsApp Direct Contact Banner */}
                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-right">
                            <div>
                                <h4 className="font-bold text-emerald-900 text-base mb-1 flex items-center gap-2">
                                    <span>💬</span>
                                    <span>التواصل المباشر عبر واتساب</span>
                                </h4>
                                <p className="text-sm text-emerald-800 leading-relaxed">
                                    يمكنك تعبئة النموذج أدناه أو مراسلتنا مباشرة عبر خط الشكاوى: 
                                    <span dir="ltr" className="font-bold mr-1 inline-block">81 886 539</span>
                                </p>
                            </div>
                            <a 
                                href="https://wa.me/96181886539?text=%D8%A7%D9%84%D8%B3%D9%84%D8%A7%D9%85%20%D8%B9%D9%84%D9%8A%D9%83%D9%85%D8%8C%20%D8%A3%D9%88%D8%AF%20%D8%AA%D9%82%D8%AF%D9%8A%D9%85%20%D8%B4%D9%83%D9%88%D9%89%20%D8%A3%D9%88%20%D8%A7%D9%82%D8%AA%D8%B1%D8%A7%D8%AD" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold py-2.5 px-5 rounded-lg flex items-center justify-center gap-2 whitespace-nowrap shadow-md transition-all active:scale-95 text-sm w-full sm:w-auto"
                            >
                                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                                </svg>
                                <span>مراسلة عبر واتساب</span>
                            </a>
                        </div>

                        <form onSubmit={handleComplaintSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-1">الاسم الكامل</label>
                                    <input 
                                        type="text" 
                                        placeholder="الاسم الثلاثي" 
                                        value={name} 
                                        onChange={e => setName(e.target.value)} 
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none" 
                                        required 
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-1">رقم الهاتف</label>
                                    <input 
                                        type="tel" 
                                        placeholder="مثال: 70011222" 
                                        value={contact} 
                                        onChange={e => setContact(e.target.value)} 
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none" 
                                        required 
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-gray-700 font-semibold mb-1">الموضوع</label>
                                <input 
                                    type="text" 
                                    placeholder="اختصار لموضوع الشكوى" 
                                    value={subject} 
                                    onChange={e => setSubject(e.target.value)} 
                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none" 
                                    required 
                                />
                            </div>

                            <div>
                                <label className="block text-gray-700 font-semibold mb-1">التفاصيل</label>
                                <textarea 
                                    placeholder="اشرح لنا المشكلة أو الاقتراح بالتفصيل..." 
                                    value={message} 
                                    onChange={e => setMessage(e.target.value)} 
                                    rows="4" 
                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none" 
                                    required 
                                ></textarea>
                            </div>

                            {status && (
                                <p className={`text-sm font-bold text-center p-3 rounded-lg ${isError ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'}`}>
                                    {status}
                                </p>
                            )}

                            <button 
                                type="submit" 
                                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 px-6 rounded-xl transition-all shadow-md flex items-center justify-center gap-2"
                            >
                                <span>إرسال الشكوى</span>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                </svg>
                            </button>
                        </form>
                    </div>
                )}

                {/* --- SURVEYS SECTION ONLY --- */}
                {isSurveysOnly && (
                    <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 animate-fadeIn">
                        <div className="flex items-center mb-6 border-b border-gray-100 pb-4">
                            <div className="bg-blue-100 p-3 rounded-full ml-4">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-2xl font-bold text-gray-800">قائمة الاستطلاعات المتاحة</h3>
                                <p className="text-sm text-gray-500">مشاركتكم تمكننا من اتخاذ قرارات تناسب احتياجات البلدة</p>
                            </div>
                        </div>
                        
                        <div className="space-y-4">
                            {surveys.length > 0 ? surveys.map(survey => (
                                <div key={survey.id} className="group bg-white border border-gray-200 p-6 rounded-xl hover:border-emerald-500 hover:shadow-md transition-all duration-300">
                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                        <div>
                                            <h4 className="font-bold text-lg text-gray-800 group-hover:text-emerald-700 transition-colors">{survey.title}</h4>
                                            {survey.details && <p className="text-sm text-gray-500 mt-1">{survey.details}</p>}
                                        </div>
                                        <a 
                                            href={survey.link} 
                                            target="_blank" 
                                            rel="noopener noreferrer" 
                                            className="w-full sm:w-auto text-center bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold py-2 px-6 rounded-full hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-all duration-300 text-sm whitespace-nowrap"
                                        >
                                            المشاركة الآن
                                        </a>
                                    </div>
                                </div>
                            )) : (
                                <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <p className="text-gray-500 font-medium">لا توجد استطلاعات نشطة حالياً</p>
                                    <p className="text-sm text-gray-400 mt-1">يرجى التحقق لاحقاً</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </PageWrapper>
    );
};

const FAQPage = () => {
    const faqs = [
        {
            question: "ما هو مفهوم القيمة التأجيريّة للعقار؟",
            answer: "القيمة التأجيريّة هي مقدار الإيجار السنويّ للبناء القائم على عقار ما، أو على قسم من عقار، ويجري تحديد هذه القيمة وفقاًَ «للأسعار الرائجة» أي وفقاً لما هو متعارف عليه بين المؤجّرين والمستأجرين في منطقة سكنيّة أو غير سكنيّة (صناعيّة، تجاريّة، سياحيّة، شتويّة، صيفيّة إلخ.) خلال مدّة زمنيّة بحيث تقل أو تزيد قيمة الإيجارات في فترات لاحقة، وتؤخذ هذه القيمة عادةً لفرض الرسم البلدي وفقا لعقود الإيجار الجاريّة، أو وفقا للافادة الصادرة عن دوائر وزارة الماليّة المختصة، وتعرف «بالقيمة التأجيريّة»."
        },
        {
            question: " هل يجوز للمواطن تقسيط رسوم البلدية السنوية؟ ",
            answer: "يمكن في حالات خاصة يعود تقديرها لرئيس السلطة التنفيذية في البلدية، تقسيط المبالغ المفروضة وفقا للاصول على ان تتخذ التدابير القانونية التي تحول دون شمولها، كليا أو جزئيا، بعامل مرور الزمن. تصبح المبالغ المقسطة متوجبة الاداء بكاملها في الحالات الآتية: 1 - إذا تأخرالمكلف عن تأدية أحد الاقساط في موعد استحقاقه. 2 - عند بيع اموال المكلف بيعا جبريا او اختياريا. 3 - عند افلاس المكلف أو تصفية امواله تصفية قضائية. ... "
        },
        {
            question: "ما هي الغرامة المترتبة على الرسوم البلدية؟ وكيف يتم احتسابها ؟ ",
            answer: "ان غرامة التأخير تتوجب بمجرد ّ تأخّر المكلّف عن سداد ما يتوجب عليه في خلال مهلة الشهرين القانونية من تاريخ الإعلان في الجريدة الرسمية سندا ( الى نص ّ المادة ۱٠٩من القانون رقم ٦٠/٨٨ قانون الرسوم والعلاوات البلدية وتعديلاته). تُحتَسب على الشكل الآتي:  يترتب على التأخير في تأدية الرسوم البلدية غرامة قدرها ۲% عن كل شهر تأخير، على أن يعتبر كسر الشهر شهرًا كاملا"
        },
        {
            question: "هل هناك من قانون يجازي من لم يصرّح عن اشغاله للمسكن؟",
            answer: "على مالك العقار أن يُعلم البلدية باشغال العقار، وإلّا تعرّض للغرامة المنصوص عليها في المادة ۱٤ من القانون رقم٦٠/٨٨–(قانون الرسوم والعلاوات البلدية وتعديلاته)"
        },
        {
            question: "كيف تكلّف الإعلانات؟",
            answer: "يخضع كل اعلان (دائم أو موقّت) لرسم ترخيص ورسم استثمار. واذا كان موضوعاً في أملاك عمومية فهو يخضع أيضاً لرسم اشغال أملاك عمومية. تحدد البلدية رسمَي الترخيص واالاستثمار، بصورة مقطوعة، بين الحدين الأقصى والأدنى الواردين في القانون. ويُستوفيان استناداً الى مساحة اللوحة بالمتر المربع، ويعتبر كسرالمتر المربع مترًا مربعًا واحدًا"
        },
        {
            question: "ما هي مهام المفرزة الصحية في البلدية؟",
            answer: "تعتبر المفرزة الصحية جهازاً رقابياً أساسياً يهتم بصحة السكان وسلامة الغذاء. تشمل مهامها: إجراء فحوصات دورية لمياه الشرب، مراقبة المؤسسات الغذائية (مطاعم، ملاحم، أفران) للتأكد من مطابقتها للمواصفات الصحية، ومنح الشهادات الصحية للعاملين في هذا القطاع."
        },
        {
            question: "ما هو دور الشرطة البلدية؟",
            answer: "الشرطة البلدية هي الضابطة التي تسهر على الراحة والسلامة العامة. تتولى مهام تنظيم السير، قمع مخالفات البناء والتعديات على الأملاك العامة والأرصفة، ومساعدة المواطنين في حالات الطوارئ، بالإضافة إلى تنفيذ القرارات الصادرة عن المجلس البلدي."
        },
        {
            question: "كيف يمكنني تقديم شكوى أو مقترح للبلدية؟",
            answer: "يمكنكم تقديم شكوى مباشرة عبر هذا الموقع من خلال صفحة 'شكاوى واستمارات'. يتم التعامل مع جميع الرسائل بسرية تامة ومتابعتها من قبل القسم المختص. كما يمكنكم زيارة القصر البلدي خلال أوقات الدوام الرسمي."
        },
        {
            question: "كيف يمكنني الاستعلام عن الرسوم البلدية المتوجبة؟",
            answer: "وفرنا لكم خدمة إلكترونية جديدة عبر صفحة 'الرسوم البلدية' في هذا الموقع. يكفي إدخال الاسم الثلاثي للمكلف أو رقم الهاتف لمعرفة القيمة التأجيرية والرسوم المتوجبة."
        },
        {
            question: "ما هي المستندات المطلوبة لإنجاز المعاملات؟",
            answer: "يمكنكم الاطلاع على النماذج الرسمية ولائحة المستندات المطلوبة لمختلف المعاملات (تصاريح، إفادات، رخص) عبر زيارة صفحة 'معاملات ونماذج' وتحميل الملفات بصيغة PDF."
        },
        {
            question: "كيف يتم التعامل مع النفايات والردميات؟",
            answer: "تقوم البلدية بجمع النفايات المنزلية بشكل دوري. أما بالنسبة للردميات ومخلفات البناء، يمنع رميها في المستوعبات العادية أو الأماكن العامة، ويجب التنسيق مع البلدية لتحديد كيفية التخلص منها بشكل قانوني."
        }
    ];

    const [openIndex, setOpenIndex] = useState(null);

    const toggleFAQ = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <PageWrapper>
            <h2 className="text-4xl font-bold text-center text-gray-800 mb-6">الأسئلة الشائعة</h2>
            <p className="text-center text-gray-500 max-w-2xl mx-auto mb-12">
                إجابات على أكثر الاستفسارات شيوعاً حول خدمات البلدية والإجراءات الإدارية.
            </p>
            <div className="max-w-3xl mx-auto space-y-4">
                {faqs.map((faq, index) => (
                    <div 
                        key={index} 
                        className={`bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 border border-gray-100 ${openIndex === index ? 'ring-2 ring-emerald-500 shadow-lg' : 'hover:shadow-lg'}`}
                    >
                        <button 
                            onClick={() => toggleFAQ(index)} 
                            className="w-full px-6 py-5 text-right flex justify-between items-center focus:outline-none"
                        >
                            <span className={`text-lg font-bold ${openIndex === index ? 'text-emerald-700' : 'text-gray-700'}`}>
                                {faq.question}
                            </span>
                            <span className={`transform transition-transform duration-300 text-emerald-500 ${openIndex === index ? 'rotate-180' : ''}`}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </span>
                        </button>
                        <div 
                            className={`px-6 text-gray-600 leading-relaxed bg-emerald-50/50 transition-all duration-300 ease-in-out ${openIndex === index ? 'max-h-96 py-5 opacity-100' : 'max-h-0 py-0 opacity-0'}`}
                        >
                            {faq.answer}
                        </div>
                    </div>
                ))}
            </div>
        </PageWrapper>
    );
};

const AdminPage = ({ user, handleLogin, handleLogout, news,activities, documents, surveys, complaints,banners=[],addBanner, toggleBannerStatus,deleteBanner, addNews,addActivity,deleteActivity, deleteNews, updateNews, addDocument, deleteDocument,updateDocumentCategory, addSurvey, deleteSurvey, updateComplaintStatus, deleteComplaint, handleFeesUpload, optimizeAllExistingNews,waterSchedule,updateWaterSchedule, obituaries, addObituary,updateObituary, deleteObituary }) => {
    const functions = getFunctions();
    const [isSyncingFees, setIsSyncingFees] = useState(false);
    
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');

    const [newsTitle, setNewsTitle] = useState('');
    const [newsContent, setNewsContent] = useState('');
    const [newsImage, setNewsImage] = useState(null);
    
    const [docTitle, setDocTitle] = useState('');
    const [docCategory, setDocCategory] = useState('رخص');
    const [docFile, setDocFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    const [surveyTitle, setSurveyTitle] = useState('');
    const [surveyLink, setSurveyLink] = useState('');
    const [surveyDetails, setSurveyDetails] = useState(''); 
    const [filter, setFilter] = useState('all');
    const [editingNews, setEditingNews] = useState(null);
    const [editTitle, setEditTitle] = useState('');
    const [editContent, setEditContent] = useState('');
    const [editImage, setEditImage] = useState(null);
    const [isUpdatingNews, setIsUpdatingNews] = useState(false);
    const [subscriberCount, setSubscriberCount] = useState(null);
    const [editingObituary, setEditingObituary] = useState(null);
const [isSendingTest, setIsSendingTest] = useState(false);


// 2. Date state (defaults to saved date or today's date in YYYY-MM-DD)
const todayIso = new Date().toISOString().split('T')[0];
 const [obDateIso, setObDateIso] = useState(new Date().toISOString().split('T')[0]);
const [obTimeRaw, setObTimeRaw] = useState("16:30"); // Default 4:30 PM
const [scheduleDateIso, setScheduleDateIso] = useState(
    waterSchedule?.dateIso || todayIso
);


    const onLogin = (e) => {
        e.preventDefault();
        if(!email || !password) {
            setError('Please enter email and password.');
            return;
        }
        handleLogin(email, password).catch(err => setError(err.message));
    };
    
    const onAddNews = (e) => {
        e.preventDefault();
        if(!newsTitle || !newsContent || !newsImage) {
            alert("يرجى ملء جميع الحقول واختيار ملف (صورة، PDF، أو Excel).");
            return;
        }
        const date = new Date().toLocaleDateString('ar-LB', { day: 'numeric', month: 'long', year: 'numeric' });
        addNews({ title: newsTitle, content: newsContent, date, imageFile: newsImage });
        setNewsTitle('');
        setNewsContent('');
        setNewsImage(null);
        e.target.reset();
    };

    const startEditing = (item) => {
        setEditingNews(item);
        setEditTitle(item.title);
        setEditContent(item.content);
        setEditImage(null);
    };

    const handleSaveEdit = async (e) => {
        e.preventDefault();
        setIsUpdatingNews(true);

        try {
            await updateNews(editingNews.id, { 
                title: editTitle,
                content: editContent,
                newImageFile: editImage, 
                oldImageUrl: editingNews.imageUrl,
                oldThumbnailUrl: editingNews.thumbnailUrl 
            });
            
            setEditingNews(null);
            setEditImage(null);
            setEditTitle('');
            setEditContent('');
        } catch (error) {
            console.error("Failed to update news:", error);
        } finally {
            setIsUpdatingNews(false);
        }
    };

    const handleStartEdit = (ob) => {
        setEditingObituary(ob);
        setObDateIso(ob.eventDateIso || todayIso);
        setObTimeRaw(ob.eventTimeRaw || "16:30");

        const formElement = document.getElementById("obituary-admin-form");
        if (formElement) {
            formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const handleCancelEdit = (formRef) => {
        setEditingObituary(null);
        setObDateIso(todayIso);
        setObTimeRaw("16:30");
        if (formRef) formRef.reset();
    };

    const onAddDocument = (e) => {
        e.preventDefault();
        if(!docTitle || !docFile) return;
        setIsUploading(true);
        addDocument({ title: docTitle, category: docCategory, file: docFile }).then(() => {
        setDocTitle('');
        setDocCategory('رخص');
        setDocFile(null);
        e.target.reset(); 
    }).finally(() => setIsUploading(false));
    };

    const onAddSurvey = (e) => {
        e.preventDefault();
        if (!surveyTitle || !surveyLink) return;
        addSurvey({ title: surveyTitle, link: surveyLink, details: surveyDetails });
        setSurveyTitle('');
        setSurveyLink('');
        setSurveyDetails('');
    };

    const filteredComplaints = complaints.filter(complaint => {
        if (filter === 'all') return true;
        return complaint.status === filter;
    });

    const [optStatus, setOptStatus] = useState('');
    const [isOptimizing, setIsOptimizing] = useState(false);
    const handleRunOptimization = async () => {
        if (!window.confirm("هل تريد تحسين وضغط جميع صور الأخبار الحالية لسرعة التحميل؟")) return;
        
        setIsOptimizing(true);
        setOptStatus("جاري بدء الضغط...");
        
        await optimizeAllExistingNews((progressMsg) => {
            setOptStatus(progressMsg);
        });

        setIsOptimizing(false);
        setOptStatus("تم ضغط وتحسين جميع الصور بنجاح! 🚀");
        setTimeout(() => setOptStatus(''), 5000);
    };

    // 1. Helper to format YYYY-MM-DD into Lebanese Arabic text
const formatArabicDate = (isoStr) => {
    if (!isoStr) return "";
    try {
        const [year, month, day] = isoStr.split('-').map(Number);
        const dateObj = new Date(year, month - 1, day);
        return dateObj.toLocaleDateString('ar-LB', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    } catch {
        return isoStr;
    }
};
const formatArabicTime = (timeStr) => {
    if (!timeStr) return "";
    const [hStr, mStr] = timeStr.split(":");
    let h = parseInt(hStr, 10);
    const m = mStr;
    let period = "صباحاً";
    if (h >= 12 && h < 15) period = "ظهراً";
    else if (h >= 15 && h < 19) period = "عصراً";
    else if (h >= 19) period = "مساءً";
    h = h % 12 || 12;
    return `الساعة ${h}:${m} ${period}`;
};


// Keep in sync when waterSchedule loads from Firestore
useEffect(() => {
    if (waterSchedule?.dateIso) {
        setScheduleDateIso(waterSchedule.dateIso);
    }
}, [waterSchedule?.dateIso]);
//Display a Live Counter in Admin Dashboard
useEffect(() => {
    const fetchSubscribers = async () => {
        try {
            // Replace "fcmTokens" with your exact Firestore collection name if different
            const tokenColl = collection(db, "fcmTokens");
            const snapshot = await getCountFromServer(tokenColl);
            setSubscriberCount(snapshot.data().count);
        } catch (error) {
            console.error("Error fetching subscriber count:", error);
        }
    };

    fetchSubscribers();
}, []);

    if (!user) {
        return (
            <PageWrapper>
                <div className="max-w-md mx-auto mt-10 bg-white p-8 rounded-xl shadow-2xl">
                    <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">تسجيل دخول المسؤول</h2>
                    <form onSubmit={onLogin} className="space-y-6">
                        <div>
                            <label className="block text-gray-700 font-semibold mb-2">البريد الإلكتروني</label>
                            <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-semibold mb-2">كلمة المرور</label>
                            <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                        </div>
                        {error && <p className="text-red-500 text-sm text-center">{error}</p>}
                        <button type="submit" className="w-full bg-emerald-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-emerald-700 transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-1">دخول</button>
                    </form>
                </div>
            </PageWrapper>
        );
    }

    return (
        <PageWrapper>
            <div className="container mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <h2 className="text-4xl font-bold text-gray-800">لوحة تحكم المسؤول</h2>
                    <button onClick={handleLogout} className="bg-red-600 text-white font-bold py-2 px-5 rounded-lg hover:bg-red-700 transition-colors shadow-md">تسجيل الخروج</button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    <div>
                        {/* --- URGENT BANNER CONTROLS --- */}
<div className="bg-white p-8 rounded-xl shadow-xl mb-12 border-t-4 border-red-500">
    <div className="flex items-center justify-between mb-4 border-b pb-3">
        <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <span>🚨</span>
            <span>إدارة التنبيهات العاجلة والإعلانات</span>
        </h3>
    </div>
   <div className="flex justify-start mb-6">
    <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 border border-blue-200 px-3.5 py-1.5 rounded-xl text-xs font-bold shadow-sm">
        <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
        <span>الأجهزة المشتركة بالإشعارات:</span>
        <span className="bg-white px-2 py-0.5 rounded-md border border-blue-100 text-blue-900 font-black">
            {subscriberCount !== null ? subscriberCount : "جاري التحميل..."}
        </span>
    </div>
</div>
{/* --- SINGLE-DEVICE TEST PUSH BUTTON --- */}
<div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 flex flex-col sm:flex-row items-center justify-between gap-3">
    <div>
        <h4 className="text-xs font-bold text-amber-900">🧪 اختبار الإشعارات على هذا الجهاز فقط:</h4>
        <p className="text-[11px] text-amber-700 mt-0.5">
            إرسال إشعار تجريبي إلى هاتفك الحالي دون إزعاج باقي الأهالي والمشتركين.
        </p>
    </div>

   <button
    type="button"
    disabled={isSendingTest}
    onClick={async () => {
        if (isSendingTest) return;
        const myToken = localStorage.getItem('roumine_fcm_token');
        if (!myToken) {
            alert("يرجى تفعيل جرس الإشعارات 🔔 أولاً.");
            return;
        }

        setIsSendingTest(true);
        alert("⏳ سيتم إرسال الإشعار بعد 5 ثوانٍ. اخرج إلى الشاشة الرئيسية أو أغلق الشاشة الآن!");

        setTimeout(async () => {
            try {
                const testFn = httpsCallable(functions, "sendSingleTestPush");
                await testFn({
                    token: myToken,
                    title: "🕊️ مجلس عزاء (إشعار تجريبي)",
                    body: "السبت • الساعة ٤:٣٠ عصراً • حسينية بلدة رومين",
                    targetUrl: "/#obituaries"
                });
            } catch (err) {
                console.error("Test push error:", err);
                  alert("فشل إرسال الإشعار: " + err.message);
            } finally {
                setIsSendingTest(false);
            }
        }, 5000);
    }}
    className="w-full sm:w-auto px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs rounded-lg shadow-sm transition-all active:scale-95 disabled:bg-gray-400 whitespace-nowrap"
>
    {isSendingTest ? "جاري الإرسال..." : "🔔 إرسال إشعار تجريبي لهاتفي"}
</button>
</div>

{/* --- RESET SERVICE WORKER BUTTON --- */}
<div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 flex flex-col sm:flex-row items-center justify-between gap-3">
    <div>
        <h4 className="text-xs font-bold text-red-900">🧹 إعادة ضبط وإلغاء الكاش للإشعارات:</h4>
        <p className="text-[11px] text-red-700 mt-0.5">
            يقوم بحذف أي Service Worker قديم عالق في ذاكرة هذا الهاتف وإعادة تفعيل الإشعارات بنسختها الجديدة.
        </p>
    </div>

    <button
        type="button"
        onClick={async () => {
            if (!('serviceWorker' in navigator)) return;
            try {
                const registrations = await navigator.serviceWorker.getRegistrations();
                for (const reg of registrations) {
                    await reg.unregister();
                }
                localStorage.removeItem('roumine_fcm_token');
                alert("تم تنظيف مسجلات الإشعارات القديمة بنجاح!\n\nاضغط الآن على جرس 🔔 أعلى الموقع لإعادة التسجيل بنسخة نظيفة.");
                window.location.reload();
            } catch (err) {
                console.error("Failed to unregister workers:", err);
            }
        }}
        className="w-full sm:w-auto px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold text-xs rounded-lg shadow-sm transition-all active:scale-95 whitespace-nowrap"
    >
        🔄 تنظيف ذاكرة الإشعارات القديمة
    </button>
</div>
    {/* Form to Add New Banner */}
    <form onSubmit={async (e) => {
        e.preventDefault();
        const text = e.target.bannerText.value.trim();
        const type = e.target.bannerType.value;
        const isActive = e.target.bannerActive.checked;
        const sendPush = e.target.sendPush.checked;
        if (!text) return;
        await addBanner({ text, type, isActive, sendPush });
        e.target.reset();
    }} className="space-y-4 mb-8 bg-gray-50 p-5 rounded-xl border border-gray-100">
        <h4 className="font-bold text-sm text-gray-700">إضافة تنبيه جديد</h4>
        
        <div>
            <input 
                type="text"
                name="bannerText"
                placeholder="نص التنبيه أو الإعلان..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none text-sm"
                required
            />
            {/* 💡 FORMATTING HELPER NOTE */}
    <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500 mt-1.5 select-none">
        <span className="text-amber-500 font-bold">💡 تلميح:</span>
        <span>لإضافة رابط داخل النص اكتب:</span>
        <code className="bg-white px-1.5 py-0.5 rounded border border-gray-200 text-red-600 font-mono text-[11px] dir-ltr inline-block">
            [اسم الرابط](https://...)
        </code>
        <span className="text-gray-400">أو الصق الرابط المباشر.</span>
    </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 items-center">
            <select name="bannerType" className="w-full sm:w-auto px-4 py-2.5 border border-gray-300 rounded-lg text-sm outline-none">
                <option value="urgent">🚨 طارئ (أحمر)</option>
                <option value="warning">⚠️ تحذير (أصفر)</option>
                <option value="info">📢 إعلان (أخضر)</option>
            </select>
            
            <label className="flex items-center gap-2 cursor-pointer w-full sm:w-auto">
                <input type="checkbox" name="bannerActive" defaultChecked className="w-5 h-5 text-red-600 rounded" />
                <span className="text-sm font-bold text-gray-700">مُفعّل فوراً</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer select-none bg-white px-3 py-2 border border-gray-300 rounded-lg">
            <input 
                type="checkbox" 
                name="sendPush" 
                defaultChecked 
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500" 
            />
            <span className="text-xs sm:text-sm font-semibold text-gray-700">إرسال إشعار فوري (Push)</span>
        </label>
            <button type="submit" className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white font-bold py-2.5 px-6 rounded-lg text-sm transition-colors shadow">
                إضافة ونشر
            </button>
        </div>
    </form>

    {/* List of Existing Banners */}
    <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
        {banners.map(b => (
            <div key={b.id} className={`p-4 rounded-xl border flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 ${b.isActive ? 'bg-white border-red-200 shadow-sm' : 'bg-gray-50 border-gray-200 opacity-75'}`}>
                <div className="flex-1">
                    <p className="font-bold text-gray-800 text-sm leading-relaxed">{b.text}</p>
                    <span className={`text-xs mt-1 inline-block px-2 py-0.5 rounded-full ${b.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                        {b.isActive ? 'يظهر حالياً' : 'مُعطل'}
                    </span>
                    {/* Push indicator */}
        <span className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded-md ${
            b.sendPush !== false ? 'bg-blue-50 text-blue-700 border border-blue-100' : 'bg-gray-100 text-gray-500'
        }`}>
            {b.sendPush !== false ? '🔔 تم إرسال إشعار' : '🔕 بدون إشعار'}
        </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <button 
                        onClick={() => toggleBannerStatus(b.id, b.isActive)}
                        className={`text-xs font-bold px-3 py-1.5 rounded-lg border transition-colors ${b.isActive ? 'bg-white text-gray-600 hover:bg-gray-100' : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'}`}
                    >
                        {b.isActive ? 'إيقاف' : 'تفعيل'}
                    </button>
                    <button 
                        onClick={() => deleteBanner(b.id)}
                        className="text-xs font-bold px-3 py-1.5 rounded-lg bg-red-50 text-red-600 border border-red-100 hover:bg-red-100"
                    >
                        حذف
                    </button>
                </div>
            </div>
        ))}
        {banners.length === 0 && <p className="text-sm text-gray-400 text-center py-4">لا توجد تنبيهات سابقة.</p>}
    </div>
</div>

{/* --- WATER SCHEDULE CONTROLS --- */}
{/* --- WATER SCHEDULE CONTROLS --- */}
<div className="bg-white p-6 sm:p-8 rounded-2xl shadow-xl mb-12 border-t-4 border-t-cyan-500">
    {/* Card Header */}
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 pb-4 border-b border-gray-100">
        <div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
                <span>💧</span>
                <span>إدارة جدول توزيع المياه اليومي</span>
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
                تحديث أهداف التوزيع للآبار الشرقية والغربية يوماً بيوم.
            </p>
        </div>
        <span className={`self-start sm:self-auto text-xs font-bold px-3 py-1 rounded-full border ${
            waterSchedule?.isActive 
                ? 'bg-cyan-50 text-cyan-700 border-cyan-200' 
                : 'bg-gray-100 text-gray-500 border-gray-200'
        }`}>
            {waterSchedule?.isActive ? 'الجدول منشور' : 'مخفي حالياً'}
        </span>
    </div>

    {/* Schedule Form */}
    <form onSubmit={async (e) => {
        e.preventDefault();
        const formattedArabic = formatArabicDate(scheduleDateIso);
        const sendPush = e.target.sendPush.checked;

        const formData = {
            isActive: e.target.scheduleActive.checked,
            sendPush: sendPush,
            date: formattedArabic,
            dateIso: scheduleDateIso,
            westernWell: {
                target: e.target.westernTarget.value.trim(),
                power: e.target.westernPower.value.trim()
            },
            easternWell: {
                target: e.target.easternTarget.value.trim(),
                power: e.target.easternPower.value.trim()
            }
        };

        await updateWaterSchedule(formData);
        alert(sendPush ? "تم تحديث الجدول وإرسال الإشعار لجميع الأهالي بنجاح!" : "تم حفظ وتحديث الجدول على الموقع بنجاح.");
        e.target.sendPush.checked = false;
    }} className="space-y-6">

        {/* Top Controls Box: Date Picker (Right) & Toggles (Left) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 bg-cyan-50/60 p-5 rounded-2xl border border-cyan-100">
            
            {/* 1. Date Picker Column */}
            <div className="flex flex-col justify-between space-y-2">
                <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-gray-700">تاريخ / يوم التوزيع:</label>
                    <div className="flex items-center gap-1.5">
                        <button
                            type="button"
                            onClick={() => setScheduleDateIso(todayIso)}
                            className="text-[11px] bg-white px-2.5 py-0.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 font-medium transition-colors shadow-sm"
                        >
                            اليوم
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                const tomorrow = new Date();
                                tomorrow.setDate(tomorrow.getDate() + 1);
                                setScheduleDateIso(tomorrow.toISOString().split('T')[0]);
                            }}
                            className="text-[11px] bg-white px-2.5 py-0.5 rounded-md border border-gray-200 text-cyan-700 hover:bg-gray-50 font-medium transition-colors shadow-sm"
                        >
                            غداً
                        </button>
                    </div>
                </div>

                <input 
                    type="date" 
                    value={scheduleDateIso}
                    onChange={(e) => setScheduleDateIso(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-cyan-500 font-semibold text-gray-800 cursor-pointer shadow-sm"
                    required 
                />

                <div className="flex items-center gap-1.5 text-xs text-cyan-800 bg-white/80 px-3 py-1.5 rounded-lg border border-cyan-100">
                    <span className="font-bold text-[11px] shrink-0">المعاينة:</span>
                    <span className="font-semibold truncate">{formatArabicDate(scheduleDateIso)}</span>
                </div>
            </div>

            {/* 2. Toggles Column */}
            <div className="flex flex-col justify-center gap-3">
                {/* Visibility Toggle */}
                <label className="flex items-center gap-3 cursor-pointer bg-white px-4 py-3 rounded-xl border border-gray-200 shadow-sm hover:border-cyan-300 transition-colors select-none">
                    <input 
                        type="checkbox" 
                        name="scheduleActive" 
                        defaultChecked={waterSchedule?.isActive ?? true} 
                        className="w-4 h-4 text-cyan-600 rounded focus:ring-cyan-500 cursor-pointer" 
                    />
                    <span className="text-xs sm:text-sm font-bold text-gray-700">
                        تفعيل وإظهار الجدول على الموقع
                    </span>
                </label>

                {/* Push Notification Toggle */}
                <label className="flex items-center gap-3 cursor-pointer bg-white px-4 py-3 rounded-xl border border-cyan-200 shadow-sm hover:bg-cyan-50/50 transition-colors select-none">
                    <input 
                        type="checkbox" 
                        name="sendPush" 
                        defaultChecked={false} 
                        className="w-4 h-4 text-cyan-600 rounded focus:ring-cyan-500 cursor-pointer" 
                    />
                    <span className="text-xs sm:text-sm font-bold text-cyan-800 flex items-center gap-1.5">
                        <span>🔔</span>
                        <span>إرسال إشعار فوري للأهالي (Push)</span>
                    </span>
                </label>
            </div>
        </div>

        {/* Wells Input Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Western Well */}
            <div className="p-5 rounded-2xl border border-gray-200 bg-gray-50/70 space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                    <div className="flex items-center gap-2 font-bold text-sm text-gray-800">
                        <span>🚰</span>
                        <span>البئر الغربي</span>
                    </div>
                    <span className="text-[11px] font-semibold text-gray-400 bg-white px-2 py-0.5 rounded border border-gray-100">
                        المحطة الغربية
                    </span>
                </div>
                <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">المنطقة / الشارع المستفيد:</label>
                    <input 
                        type="text" 
                        name="westernTarget" 
                        defaultValue={waterSchedule?.westernWell?.target || "الحارة التحتا"}
                        placeholder="مثال: الحارة التحتا، حي البيدر..."
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-cyan-500 font-medium" 
                        required 
                    />
                </div>
                <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">مصدر الطاقة المتوفر:</label>
                    <input 
                        type="text" 
                        name="westernPower" 
                        defaultValue={waterSchedule?.westernWell?.power || "طاقة شمسية / كهرباء / مولد كهرباء"}
                        placeholder="مثال: طاقة شمسية / كهرباء..."
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-xs outline-none focus:ring-2 focus:ring-cyan-500 font-medium" 
                    />
                </div>
            </div>

            {/* Eastern Well */}
            <div className="p-5 rounded-2xl border border-gray-200 bg-gray-50/70 space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                    <div className="flex items-center gap-2 font-bold text-sm text-gray-800">
                        <span>🚰</span>
                        <span>البئر الشرقي</span>
                    </div>
                    <span className="text-[11px] font-semibold text-gray-400 bg-white px-2 py-0.5 rounded border border-gray-100">
                        المحطة الشرقية
                    </span>
                </div>
                <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">المنطقة / الشارع المستفيد:</label>
                    <input 
                        type="text" 
                        name="easternTarget" 
                        defaultValue={waterSchedule?.easternWell?.target || "الطريق العام من الجامع حتى مفرق القلع الحاووز"}
                        placeholder="مثال: الطريق العام، حي المدارس..."
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-cyan-500 font-medium" 
                        required 
                    />
                </div>
                <div>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">مصدر الطاقة المتوفر:</label>
                    <input 
                        type="text" 
                        name="easternPower" 
                        defaultValue={waterSchedule?.easternWell?.power || "طاقة شمسية / كهرباء / مولد كهرباء"}
                        placeholder="مثال: طاقة شمسية / مولد البلدية..."
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-xs outline-none focus:ring-2 focus:ring-cyan-500 font-medium" 
                    />
                </div>
            </div>

        </div>

        {/* Submit Button */}
        <div className="pt-2">
            <button 
                type="submit" 
                className="w-full sm:w-auto px-8 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-bold rounded-xl text-sm transition-all shadow-md active:scale-95"
            >
                حفظ ونشر التوزيع المحدث
            </button>
        </div>
    </form>
</div>

                        <div className="bg-white p-8 rounded-xl shadow-xl mb-12">

                            <h3 className="text-2xl font-bold text-emerald-700 mb-6 border-b pb-4">إدارة الأخبار</h3>
                            
                            <div className="bg-emerald-50 p-4 rounded-lg mb-6 border border-emerald-200 flex flex-col sm:flex-row justify-between items-center gap-4">
                                <div>
                                    <h4 className="font-bold text-emerald-800">تسريع تحميل صور الأخبار</h4>
                                    <p className="text-xs text-gray-600 mt-1">
                                        ضغط جميع الصور المرفوعة سابقاً وتحويلها إلى صيغة WebP الخفيفة.
                                    </p>
                                </div>
                                <button 
                                    onClick={handleRunOptimization} 
                                    disabled={isOptimizing || news.length === 0}
                                    className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold py-2 px-4 rounded-lg text-sm transition-all disabled:bg-gray-400 whitespace-nowrap shadow"
                                >
                                    {isOptimizing ? 'جاري التحسين...' : '⚡ تحسين وتحويل كافة الصور'}
                                </button>
                            </div>

                            {optStatus && (
                                <p className="text-center font-bold text-sm text-emerald-700 mb-4 bg-emerald-100 p-2 rounded animate-pulse">
                                    {optStatus}
                                </p>
                            )}

                            <form onSubmit={onAddNews} className="mb-6 space-y-4">
                                <input type="text" placeholder="عنوان الخبر" value={newsTitle} onChange={e => setNewsTitle(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
                                <textarea placeholder="محتوى الخبر" value={newsContent} onChange={e => setNewsContent(e.target.value)} rows="4" className="w-full px-4 py-3 border border-gray-300 rounded-lg"></textarea>
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">مرفق الخبر (صورة، PDF، أو Excel)</label>
                                    <input 
                                        type="file" 
                                        accept="image/*,.pdf,.xls,.xlsx" 
                                        onChange={e => setNewsImage(e.target.files[0])} 
                                        className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 cursor-pointer" 
                                    />
                                </div>
                                <button type="submit" className="bg-emerald-600 text-white font-bold py-2 px-5 rounded-lg hover:bg-emerald-700">إضافة خبر</button>
                            </form>

                            <div className="space-y-4 max-h-60 overflow-y-auto">
                                {news.map(item => (
                                    <div key={item.id} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg border gap-4">
                                        <span className="font-semibold text-gray-700 flex-1 leading-relaxed">
                                            {item.title}
                                        </span>
                                        <div className="flex items-center gap-3 shrink-0 whitespace-nowrap">
                                            <button 
                                                onClick={() => startEditing(item)} 
                                                className="text-emerald-600 hover:text-emerald-800 font-semibold transition-colors"
                                            >
                                                تعديل
                                            </button>
                                            <button 
                                                onClick={() => deleteNews(item.id)} 
                                                className="text-red-500 hover:text-red-700 font-semibold transition-colors"
                                            >
                                                حذف
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
<div className="bg-white p-8 rounded-xl shadow-xl mb-12">
    <h3 className="text-2xl font-bold text-emerald-700 mb-6 border-b pb-4">إدارة الأنشطة والفعاليات المحلية</h3>

    <form onSubmit={(e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const title = formData.get('actTitle');
        const description = formData.get('actDesc');
        const files = e.target.actFiles.files;

        if (!title || !description || files.length === 0) {
            alert("يرجى تعبئة جميع الحقول واختيار صورة واحدة على الأقل.");
            return;
        }

        const date = new Date().toLocaleDateString('ar-LB', { day: 'numeric', month: 'long', year: 'numeric' });
        addActivity({ title, description, date, imageFiles: files });
        e.target.reset();
    }} className="mb-6 space-y-4">
        <input type="text" name="actTitle" placeholder="عنوان النشاط" className="w-full px-4 py-3 border border-gray-300 rounded-lg" required />
        <textarea name="actDesc" placeholder="تفاصيل ووصف النشاط..." rows="4" className="w-full px-4 py-3 border border-gray-300 rounded-lg" required></textarea>
        <div>
            <label className="block text-gray-700 font-semibold mb-2">صور ألبوم النشاط (يمكنك اختيار عدة صور معاً)</label>
            <input 
                type="file" 
                name="actFiles" 
                multiple 
                accept="image/*" 
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 cursor-pointer" 
                required
            />
        </div>
        <button type="submit" className="bg-emerald-600 text-white font-bold py-2.5 px-6 rounded-lg hover:bg-emerald-700 transition-colors shadow">
            إضافة نشاط جديد
        </button>
    </form>

    <div className="space-y-4 max-h-60 overflow-y-auto">
        {activities.map(act => (
            <div key={act.id} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg border gap-4">
                <div className="flex items-center gap-3">
                    <span className="font-semibold text-gray-700">{act.title}</span>
                    <span className="text-xs text-gray-400">({act.images?.length || 1} صور)</span>
                </div>
                <button onClick={() => deleteActivity(act.id)} className="text-red-500 hover:text-red-700 font-semibold shrink-0">
                    حذف
                </button>
            </div>
        ))}
    </div>
</div>

                    </div>
                    
                    <div>
{/* --- OBITUARIES & CONDOLENCES MANAGEMENT --- */}
<div id="obituary-admin-form" className="bg-white p-6 sm:p-8 rounded-2xl shadow-xl mb-12 border-t-4 border-t-stone-800">
    <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
        <div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
                <span>🕊️</span>
                <span>{editingObituary ? "تعديل إعلان النعي والمجلس" : "إدارة الوفيات ومجالس العزاء"}</span>
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
                {editingObituary 
                    ? `جاري تعديل إعلان: ${editingObituary.deceasedName}` 
                    : "نشر إعلانات الوفاة ومجالس العزاء. يتم حذف الإعلانات المنتهية تلقائياً بمجرد انقضاء تاريخها."}
            </p>
        </div>
        <span className="text-xs font-bold px-3 py-1 rounded-full bg-stone-100 text-stone-700 border border-stone-200">
            {obituaries?.length || 0} إعلانات نشطة
        </span>
    </div>

    {/* Form: Handles both Add and Edit */}
    <form 
        key={editingObituary ? editingObituary.id : "new-obituary"} 
        onSubmit={async (e) => {
            e.preventDefault();
            const type = e.target.obType.value;
            const audience = e.target.obAudience.value; // 👈 Capture audience value
            const deceasedName = e.target.obDeceasedName.value.trim();
            const location = e.target.obLocation.value.trim();
            const content = e.target.obContent.value.trim();
            const sendPush = e.target.obSendPush?.checked || false;

            const formattedDate = formatArabicDate(obDateIso);
            const formattedTime = formatArabicTime(obTimeRaw);

            const payload = {
                deceasedName,
                type,
                audience,
                content,
                eventDate: formattedDate,
                eventDateIso: obDateIso,
                eventTime: formattedTime,
                eventTimeRaw: obTimeRaw,
                location,
                sendPush
            };

            if (editingObituary) {
                await updateObituary(editingObituary.id, payload);
                setEditingObituary(null);
            } else {
                await addObituary(payload);
            }

            e.target.reset();
            setObDateIso(todayIso);
            setObTimeRaw("16:30");
        }} 
        className={`space-y-5 mb-8 p-5 rounded-2xl border transition-colors ${
            editingObituary 
                ? "bg-amber-50/50 border-amber-300 shadow-inner" 
                : "bg-stone-50/70 border-stone-200"
        }`}
    >
        {/* Editing Alert Banner */}
        {editingObituary && (
            <div className="flex items-center justify-between bg-amber-100 border border-amber-300 px-4 py-2.5 rounded-xl text-amber-900 text-xs font-bold">
                <span>✏️ وضع التعديل مفعّل لهذا الإعلان</span>
                <button
                    type="button"
                    onClick={(e) => handleCancelEdit(e.target.closest("form"))}
                    className="text-stone-700 underline hover:text-stone-900 cursor-pointer"
                >
                    إلغاء التعديل
                </button>
            </div>
        )}

        {/* Type & Deceased Name */}
<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
    {/* 1. Announcement Type */}
    <div>
        <label className="block text-xs font-bold text-gray-700 mb-1">نوع المناسبة:</label>
        <select 
            name="obType" 
            defaultValue={editingObituary?.type || "funeral"}
            className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-stone-500 font-bold text-stone-800 cursor-pointer"
        >
            <option value="funeral">🕊️ وفاة ودفن</option>
            <option value="memorial">🕯️ مجلس عزاء / أسبوع</option>
            <option value="anniversary">📅 ذكرى سنوية</option>
        </select>
    </div>

    {/* 2. Invitation Audience Dropdown */}
    <div>
        <label className="block text-xs font-bold text-gray-700 mb-1">طبيعة الدعوة:</label>
        <select 
            name="obAudience" 
            defaultValue={editingObituary?.audience || "general"}
            className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-stone-500 font-bold text-stone-800 cursor-pointer"
        >
            <option value="general">👥 الدعوة عامة</option>
            <option value="men">👨 الدعوة خاصة بالرجال</option>
            <option value="women">👩 الدعوة خاصة بالنساء</option>
        </select>
    </div>

    {/* 3. Deceased Name */}
    <div>
        <label className="block text-xs font-bold text-gray-700 mb-1">اسم الفقيد / المرحوم:</label>
        <input 
            type="text" 
            name="obDeceasedName" 
            defaultValue={editingObituary?.deceasedName || ""}
            placeholder="مثال: الحاج وجيه محمد جوني (أبو طارق)" 
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-stone-500 font-medium" 
            required 
        />
    </div>
</div>

        {/* Date, Time, and Location Pickers */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-white p-4 rounded-xl border border-stone-200/80">
            <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">تاريخ المناسبة:</label>
                <input 
                    type="date" 
                    value={obDateIso}
                    onChange={(e) => setObDateIso(e.target.value)}
                    className="w-full px-3 py-2 bg-stone-50 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-stone-500 font-medium text-gray-800 cursor-pointer" 
                    required 
                />
                <p className="text-[11px] text-stone-600 mt-1 font-semibold truncate">
                    {formatArabicDate(obDateIso)}
                </p>
            </div>

            <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">توقيت المناسبة:</label>
                <input 
                    type="time" 
                    value={obTimeRaw}
                    onChange={(e) => setObTimeRaw(e.target.value)}
                    className="w-full px-3 py-2 bg-stone-50 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-stone-500 font-medium text-gray-800 cursor-pointer" 
                    required 
                />
                <p className="text-[11px] text-stone-600 mt-1 font-semibold">
                    {formatArabicTime(obTimeRaw)}
                </p>
            </div>

            <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">المكان / الحسينية:</label>
                <input 
                    type="text" 
                    name="obLocation" 
                    defaultValue={editingObituary?.location || "حسينية بلدة رومين"} 
                    className="w-full px-3 py-2 bg-stone-50 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-stone-500" 
                    required
                />
                <p className="text-[11px] text-gray-400 mt-1">البلدة أو القاعة</p>
            </div>
        </div>

        {/* Text Content */}
        <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">
                نص النعي والتعزية (الأسفون، الأنساب، والتفاصيل):
            </label>
            <textarea 
                name="obContent" 
                rows="4" 
                defaultValue={editingObituary?.content || ""}
                placeholder="إنتقل إلى رحمة الله تعالى... الأسفون: آل جوني، وعموم أهالي بلدة رومين..." 
                className="w-full px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-stone-500 leading-relaxed font-cairo"
            ></textarea>
        </div>

        {/* Push Notification Toggle and Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
            <label className="flex items-center gap-2 cursor-pointer bg-white px-4 py-2.5 rounded-xl border border-stone-300 shadow-sm hover:bg-stone-50 w-full sm:w-auto">
                <input 
                    type="checkbox" 
                    name="obSendPush" 
                    defaultChecked={false} 
                    className="w-4 h-4 text-stone-700 rounded focus:ring-stone-500" 
                />
                <span className="text-xs sm:text-sm font-bold text-stone-800 flex items-center gap-1.5">
                    <span>🕊️</span>
                    <span>{editingObituary ? "إعادة إرسال إشعار بالتعديل للأهالي" : "إرسال إشعار فوري للأهالي"}</span>
                </span>
            </label>

            <div className="flex items-center gap-2 w-full sm:w-auto">
                {editingObituary && (
                    <button
                        type="button"
                        onClick={(e) => handleCancelEdit(e.target.closest("form"))}
                        className="w-1/2 sm:w-auto px-5 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold rounded-xl text-sm transition-all shadow-sm"
                    >
                        إلغاء
                    </button>
                )}
                <button 
                    type="submit" 
                    className={`w-full sm:w-auto px-8 py-2.5 text-white font-bold rounded-xl text-sm transition-all shadow-md active:scale-95 ${
                        editingObituary 
                            ? "bg-amber-600 hover:bg-amber-700" 
                            : "bg-stone-900 hover:bg-black"
                    }`}
                >
                    {editingObituary ? "حفظ التعديلات" : "نشر إعلان النعي"}
                </button>
            </div>
        </div>
    </form>

    {/* Existing Obituaries List with Edit & Delete Buttons */}
    <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
        {obituaries.map((ob) => (
            <div 
                key={ob.id} 
                className={`p-3.5 rounded-xl border flex items-center justify-between gap-3 transition-colors ${
                    editingObituary?.id === ob.id 
                        ? "bg-amber-50 border-amber-300 ring-2 ring-amber-200" 
                        : "bg-stone-50/50 border-gray-200"
                }`}
            >
                <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xl shrink-0">🕯️</span>
                    <div className="truncate">
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-bold px-2 py-0.5 rounded bg-stone-200 text-stone-800">
                                {ob.title}
                            </span>
                            <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${
        ob.audience === 'men' 
            ? 'bg-blue-50 text-blue-800 border-blue-200' 
            : ob.audience === 'women' 
                ? 'bg-rose-50 text-rose-800 border-rose-200' 
                : 'bg-emerald-50 text-emerald-800 border-emerald-200'
    }`}>
        {ob.audience === 'men' ? '👨 للرجال' : ob.audience === 'women' ? '👩 للنساء' : '👥 عامة'}
    </span>
                            <span className="font-bold text-sm text-gray-800 truncate">{ob.deceasedName}</span>
                        </div>
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                            {ob.eventDate} • {ob.eventTime} • {ob.location}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    <button 
                        onClick={() => handleStartEdit(ob)}
                        className="text-xs font-bold px-3 py-1 rounded-lg bg-stone-100 text-stone-700 border border-stone-300 hover:bg-stone-200 transition-colors"
                    >
                        تعديل
                    </button>
                    <button 
                        onClick={() => deleteObituary(ob.id)} 
                        className="text-xs font-bold px-3 py-1 rounded-lg bg-red-50 text-red-600 border border-red-100 hover:bg-red-100 transition-colors"
                    >
                        حذف
                    </button>
                </div>
            </div>
        ))}
        {obituaries.length === 0 && (
            <p className="text-xs text-gray-400 text-center py-4">
                لا توجد إعلانات وفيات نشطة حالياً.
            </p>
        )}
    </div>
</div>

<div className="bg-white p-8 rounded-xl shadow-xl mb-12">
    <h3 className="text-2xl font-bold text-emerald-700 mb-6 border-b pb-4">إدارة المعاملات والنماذج</h3>
    <form onSubmit={onAddDocument} className="mb-6 space-y-4">
        {/* Document Title */}
        <input 
            type="text" 
            placeholder="عنوان المستند" 
            value={docTitle} 
            onChange={e => setDocTitle(e.target.value)} 
            className="w-full px-4 py-3 border border-gray-300 rounded-lg text-right outline-none focus:ring-2 focus:ring-emerald-500" 
        />

        {/* Category Select Dropdown */}
        <select
            value={docCategory}
            onChange={e => setDocCategory(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg text-right bg-white text-gray-700 outline-none focus:ring-2 focus:ring-emerald-500"
        >
            <option value="رخص">رخص</option>
            <option value="إفادات">إفادات</option>
            <option value="تصاريح">تصاريح</option>
            <option value="عام">عام</option>
        </select>

        {/* File Input */}
        <input 
            type="file" 
            onChange={e => setDocFile(e.target.files[0])} 
            accept=".pdf" 
            className="w-full file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100" 
        />

        {/* Submit Button */}
        <button 
            type="submit" 
            disabled={isUploading} 
            className="bg-emerald-600 text-white font-bold py-2 px-5 rounded-lg hover:bg-emerald-700 disabled:bg-gray-400 transition-colors"
        >
            {isUploading ? 'جاري الرفع...' : 'إضافة مستند'}
        </button>
    </form>

    {/* List of uploaded documents */}
    <div className="space-y-4 max-h-80 overflow-y-auto">
        {documents.map(doc => (
            <div key={doc.id} className="flex flex-col sm:flex-row justify-between items-center p-4 bg-gray-50 rounded-lg border gap-4">
                <span className="font-semibold text-gray-700 flex-1 leading-relaxed text-right w-full sm:w-auto">
                    {doc.title}
                </span>
                
                <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                    {/* Inline Category Switcher for Existing & New Documents */}
                    <select
                        value={doc.category || 'عام'}
                        onChange={(e) => updateDocumentCategory(doc.id, e.target.value)}
                        className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:ring-2 focus:ring-emerald-500 outline-none"
                    >
                        <option value="رخص">رخص</option>
                        <option value="إفادات">إفادات</option>
                        <option value="تصاريح">تصاريح</option>
                        <option value="عام">عام</option>
                    </select>

                    <button 
                        onClick={() => deleteDocument(doc.id, doc.filePath)} 
                        className="text-red-500 hover:text-red-700 font-semibold shrink-0 whitespace-nowrap transition-colors text-sm px-2 py-1"
                    >
                        حذف
                    </button>
                </div>
            </div>
        ))}
    </div>
</div>

{/* --- GOOGLE DRIVE AUTOMATED FEES SYNC CARD --- */}
<div className="bg-white p-6 sm:p-8 rounded-2xl shadow-xl mb-12 border-t-4 border-t-emerald-600">
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 pb-4 border-b border-gray-100">
        <div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
                <span>🔄</span>
                <span>مزامنة الرسوم البلدية (Google Drive)</span>
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
                الربط المباشر مع الحساب الرسمي <code className="bg-emerald-50 text-emerald-800 px-1.5 py-0.5 rounded font-mono">baladiyat.roumine@gmail.com</code>
            </p>
        </div>
        <span className="text-xs font-bold px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
            مزامنة سحابية تلقائية (3:00 ص)
        </span>
    </div>

    <div className="bg-stone-50 p-5 rounded-2xl border border-stone-200 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
                <h4 className="font-bold text-sm text-stone-800">المزامنة الفورية من نسخة النظام المحلي:</h4>
                <p className="text-xs text-stone-500 mt-0.5 leading-relaxed">
                    تقوم هذه العملية بتحميل أحدث ملف قاعدة بيانات (<code className="font-mono">.db</code>) مرفوع على Google Drive وتحديث جدول الرسوم والذمم لكافة المكلفين فوراً.
                </p>
            </div>

            <button
                type="button"
                disabled={isSyncingFees}
                onClick={async () => {
                    if (!window.confirm("هل تريد بدء المزامنة وتحديث سجلات الرسوم الآن من Google Drive؟")) return;
                    setIsSyncingFees(true);
                    try {
                        const syncFn = httpsCallable(functions, "manualDriveFeesSync");
                        const result = await syncFn();
                        alert(`تمت المزامنة بنجاح! تم تحديث ${result.data.meta.totalRecords} سجلاً للمكلفين.`);
                    } catch (err) {
                        console.error("Sync error:", err);
                        alert("حدث خطأ أثناء المزامنة: " + (err.message || "تأكد من وجود مفتاح الخدمة ورفع الملف إلى Drive"));
                    } finally {
                        setIsSyncingFees(false);
                    }
                }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6 py-2.5 rounded-xl text-xs sm:text-sm transition-all shadow-md active:scale-95 disabled:bg-gray-300 flex items-center gap-2 shrink-0 justify-center"
            >
                <span>{isSyncingFees ? "⏳" : "⚡"}</span>
                <span>{isSyncingFees ? "جاري المعالجة والمزامنة..." : "بدء المزامنة الفورية الآن"}</span>
            </button>
        </div>
    </div>
</div>

                         <div className="bg-white p-8 rounded-xl shadow-xl mb-12">
                            <h3 className="text-2xl font-bold text-emerald-700 mb-6 border-b pb-4">إدارة الاستمارات</h3>
                            <form onSubmit={onAddSurvey} className="mb-6 space-y-4">
                                <input type="text" placeholder="عنوان الاستمارة" value={surveyTitle} onChange={e => setSurveyTitle(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
                                <textarea placeholder="تفاصيل الاستمارة (اختياري)" value={surveyDetails} onChange={e => setSurveyDetails(e.target.value)} rows="3" className="w-full px-4 py-3 border border-gray-300 rounded-lg"></textarea>
                                <input type="url" placeholder="رابط Microsoft or Google Forms" value={surveyLink} onChange={e => setSurveyLink(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
                                <button type="submit" className="bg-emerald-600 text-white font-bold py-2 px-5 rounded-lg hover:bg-emerald-700">إضافة استمارة</button>
                            </form>
                            <div className="space-y-4 max-h-60 overflow-y-auto">
                                {surveys.map(survey => (
                                    <div key={survey.id} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg border">
                                        <span className="font-semibold text-gray-700">{survey.title}</span>
                                        <button onClick={() => deleteSurvey(survey.id)} className="text-red-500 hover:text-red-700 font-semibold">حذف</button>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        <div className="bg-white p-8 rounded-xl shadow-xl">
                            <div className="flex justify-between items-center mb-6 border-b pb-4">
                                <h3 className="text-2xl font-bold text-emerald-700">الشكاوى والاقتراحات الواردة</h3>
                                <select value={filter} onChange={e => setFilter(e.target.value)} className="p-2 border rounded-md">
                                    <option value="all">الكل</option>
                                    <option value="pending">قيد المعالجة</option>
                                    <option value="resolved">تمت المعالجة</option>
                                </select>
                            </div>
                            <div className="space-y-4 max-h-[800px] overflow-y-auto">
                                {filteredComplaints.length > 0 ? filteredComplaints.map(complaint => (
                                    <div key={complaint.id} className="p-4 bg-white rounded-xl shadow-md border border-gray-200">
                                        <div className="flex justify-between items-center mb-2">
                                            <h4 className="font-bold text-lg text-gray-800">{complaint.subject}</h4>
                                            <span className={`text-sm font-semibold px-2 py-1 rounded-full ${complaint.status === 'resolved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                {complaint.status === 'resolved' ? 'تمت المعالجة' : 'قيد المعالجة'}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-1"><strong>من:</strong> {complaint.name}</p>
                                        <p className="text-sm text-gray-600 mb-3"><strong>للتواصل:</strong> {complaint.contact}</p>
                                        <p className="text-gray-700 whitespace-pre-wrap">{complaint.message}</p>
                                        <div className="mt-4 flex justify-end space-x-2">
                                            {complaint.status !== 'resolved' && (
                                                <button onClick={() => updateComplaintStatus(complaint.id, 'resolved')} className="bg-emerald-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors shadow-md">
                                                    وضع علامة "تمت المعالجة"
                                                </button>
                                            )}
                                            <button onClick={() => deleteComplaint(complaint.id)} className="bg-red-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-red-700 transition-colors shadow-md">
                                                حذف
                                            </button>
                                        </div>
                                    </div>
                                )) : <p className="text-center text-gray-500">لا توجد شكاوى حالياً.</p>}
                            </div>
                        </div>
                        
                    </div>
                    
                </div>
            </div>

            {editingNews && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-lg animate-fadeIn text-right">
                        <h3 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">تعديل الخبر</h3>
                        
                        <form onSubmit={handleSaveEdit} className="space-y-4">
                            <div>
                                <label className="block text-gray-700 font-semibold mb-1">عنوان الخبر</label>
                                <input 
                                    type="text" 
                                    value={editTitle} 
                                    onChange={e => setEditTitle(e.target.value)} 
                                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none" 
                                    required 
                                />
                            </div>

                            <div>
                                <label className="block text-gray-700 font-semibold mb-1">محتوى الخبر</label>
                                <textarea 
                                    value={editContent} 
                                    onChange={e => setEditContent(e.target.value)} 
                                    rows="4" 
                                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none" 
                                    required 
                                ></textarea>
                            </div>

                            <div>
                                <label className="block text-gray-700 font-semibold mb-2">تحديث المرفق (صورة، PDF، أو Excel)</label>
                                <input 
                                    type="file" 
                                    accept="image/*,.pdf,.xls,.xlsx" 
                                    onChange={e => setEditImage(e.target.files[0])} 
                                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 cursor-pointer" 
                                />
                                <p className="text-xs text-gray-400 mt-1">اتركه فارغاً للاحتفاظ بالملف الحالي</p>
                            </div>
                            
                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                                <button 
                                    type="button" 
                                    onClick={() => {
                                        setEditingNews(null);
                                        setEditImage(null);
                                    }} 
                                    className="bg-gray-200 text-gray-700 font-bold py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
                                >
                                    إلغاء
                                </button>
                                <button 
                                    type="submit" 
                                    disabled={isUpdatingNews} 
                                    className="bg-emerald-600 text-white font-bold py-2 px-5 rounded-lg hover:bg-emerald-700 disabled:bg-gray-400 transition-colors"
                                >
                                    {isUpdatingNews ? 'جاري الحفظ...' : 'حفظ التعديلات'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </PageWrapper>
    );
};

const EmergencyDirectoryPage = () => {
    const contacts = [
        {
            category: "الطوارئ الوطنية",
            icon: "🚨",
            items: [
                { name: "الصليب الأحمر اللبناني ", phone: "140", subtitle: "طوارئ طبية على مدار الساعة" },
                { name: "الدفاع المدني", phone: "125", subtitle: "إطفاء وإنقاذ" },
                { name: "قوى الأمن الداخلي", phone: "112", subtitle: "بلاغات أمنية" },
            ]
        },
        {
            category: "خدمات بلدة رومين والطوارئ المحلية",
            icon: "🏛️",
            items: [
                { name: "شرطة البلدية والطوارئ", phone: "03 526 273", subtitle: "متابعة أمنية وحالات طارئة محلية" },
                { name: "مكتب البلدية الرئيسي", phone: "70 681 711", subtitle: "الاستعلامات والمعاملات" },
                { name: "مكتب الشكاوى والمراجعات", phone: "81 886 539", subtitle: "للشكاوى والمراجعات والتبليغات" },
            ]
        },
        {
            category: "المرافق والخدمات العامة",
            icon: "⚡",
            items: [
                { name: "مشتكي المولدات المحلية", phone: "70 883 590", subtitle: "استفسارات وأعطال اشتراك الكهرباء" },
                { name: "مصلحة مياه لبنان الجنوبي", phone: "07 757 000", subtitle: "جدول وضخ مياه الشرب" },
                { name: "مسؤول الصيانة والخدمات", phone: "70 388 073", subtitle: "أعطال الشبكة والإنارة العامة" },
            ]
        }
    ];

    return (
        <PageWrapper>
            <div className="text-center mb-10">
                <h2 className="text-4xl font-bold text-gray-800 mb-3">دليل الطوارئ والخدمات</h2>
                <p className="text-gray-500 max-w-2xl mx-auto text-lg">
                    أرقام التواصل السريع مع فرق الإسعاف، الأمن، والخدمات البلدية والمرافق العامة في رومين.
                </p>
            </div>

            <div className="space-y-10 max-w-5xl mx-auto">
                {contacts.map((group, idx) => (
                    <div key={idx} className="bg-white p-6 md:p-8 rounded-2xl shadow-lg border border-gray-100">
                        <div className="flex items-center mb-6 border-b border-gray-100 pb-3">
                            <span className="text-3xl ml-3">{group.icon}</span>
                            <h3 className="text-2xl font-bold text-gray-800">{group.category}</h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {group.items.map((item, itemIdx) => (
                                <div key={itemIdx} className="bg-gray-50 p-5 rounded-xl border border-gray-200 flex flex-col justify-between hover:border-emerald-500 transition-colors">
                                    <div className="text-center mb-4">
                                        <h4 className="font-bold text-gray-800 text-lg mb-1">{item.name}</h4>
                                        <p className="text-sm text-gray-500">{item.subtitle}</p>
                                    </div>
                                    <a 
                                        href={`tel:${item.phone}`}
                                        className="w-full bg-emerald-600 text-white font-bold py-2.5 px-4 rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 text-center"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                        </svg>
                                        <span dir="ltr" className="inline-block">{item.phone}</span>
                                    </a>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </PageWrapper>
    );
};

const compressToWebP = (imageSource, maxWidth = 1000, quality = 0.65) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        
        const srcUrl = typeof imageSource === 'string' 
            ? imageSource 
            : URL.createObjectURL(imageSource);

        img.onload = () => {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;

            if (width > maxWidth) {
                height = Math.round((height * maxWidth) / width);
                width = maxWidth;
            }

            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob((blob) => {
                if (typeof imageSource !== 'string') URL.revokeObjectURL(srcUrl);
                if (blob) resolve(blob);
                else reject(new Error('Compression failed'));
            }, 'image/webp', quality);
        };

        img.onerror = (err) => {
            if (typeof imageSource !== 'string') URL.revokeObjectURL(srcUrl);
            reject(err);
        };

        img.src = srcUrl;
    });
};

const generateBrandedDocumentThumbnail = (file, title, fileType) => {
    return new Promise((resolve) => {
        const canvas = document.createElement('canvas');
        canvas.width = 800;
        canvas.height = 500;
        const ctx = canvas.getContext('2d');

        const isPdf = fileType === 'pdf';
        const accentColor = isPdf ? '#dc2626' : '#059669';
        const badgeBg = isPdf ? '#fef2f2' : '#ecfdf5';
        const badgeBorder = isPdf ? '#fca5a5' : '#a7f3d0';
        const badgeText = isPdf ? 'مستند PDF ' : 'ملف EXCEL ';

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, 800, 500);

        ctx.strokeStyle = '#e2e8f0';
        ctx.lineWidth = 4;
        ctx.strokeRect(2, 2, 796, 496);

        ctx.fillStyle = accentColor;
        ctx.fillRect(0, 0, 800, 10);

        const logo = new Image();
        logo.crossOrigin = 'Anonymous';
        logo.src = '/baladiye-logo.svg';

        const drawContent = (hasLogo) => {
            if (hasLogo) {
                const logoWidth = 140;
                const logoHeight = 100;
                ctx.drawImage(logo, (800 - logoWidth) / 2, 40, logoWidth, logoHeight);
            } else {
                ctx.fillStyle = '#1e293b';
                ctx.font = 'bold 28px Cairo, sans-serif';
                ctx.textAlign = 'center';
                ctx.fillText('بلدية رومين', 400, 80);
            }

            ctx.fillStyle = '#64748b';
            ctx.font = 'bold 15px Cairo, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('الجمهورية اللبنانية • بلدية رومين', 400, 160);

            ctx.strokeStyle = '#f1f5f9';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(150, 185);
            ctx.lineTo(650, 185);
            ctx.stroke();

            const badgeWidth = 220;
            const badgeHeight = 42;
            const badgeX = (800 - badgeWidth) / 2;
            const badgeY = 215;

            ctx.fillStyle = badgeBg;
            ctx.strokeStyle = badgeBorder;
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            if (ctx.roundRect) {
                ctx.roundRect(badgeX, badgeY, badgeWidth, badgeHeight, 21);
            } else {
                ctx.rect(badgeX, badgeY, badgeWidth, badgeHeight);
            }
            ctx.fill();
            ctx.stroke();

            ctx.fillStyle = accentColor;
            ctx.font = 'bold 17px Cairo, sans-serif';
            ctx.textBaseline = 'middle';
            ctx.fillText(badgeText, 400, badgeY + 21);

            ctx.fillStyle = '#0f172a';
            ctx.font = 'bold 26px Cairo, sans-serif';
            ctx.textBaseline = 'alphabetic';
            const displayTitle = title || file.name || 'مستند ';
            const truncatedTitle = displayTitle.length > 32 ? displayTitle.substring(0, 32) + '...' : displayTitle;
            ctx.fillText(truncatedTitle, 400, 335);

            ctx.fillStyle = '#f8fafc';
            ctx.fillRect(0, 410, 800, 90);
            ctx.strokeStyle = '#e2e8f0';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(0, 410);
            ctx.lineTo(800, 410);
            ctx.stroke();

            ctx.fillStyle = '#475569';
            ctx.font = 'bold 16px Cairo, sans-serif';
            ctx.fillText('انقر لمعاينة أو تحميل المستند 📄', 400, 460);

            canvas.toBlob((blob) => resolve(blob), 'image/webp', 0.95);
        };

        logo.onload = () => drawContent(true);
        logo.onerror = () => drawContent(false);
    });
};

const detectFileType = (fileOrUrl) => {
    if (!fileOrUrl) return 'image';
    const filename = typeof fileOrUrl === 'string' ? fileOrUrl : (fileOrUrl.name || '');
    if (!filename) return 'image';
    
    const ext = filename.split('.').pop().split('?')[0].toLowerCase();
    if (ext === 'pdf') return 'pdf';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'excel';
    return 'image';
};

const TownDirectoryPage = () => {
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    const categories = [
        { id: 'all', label: 'الكل', icon: '📍' },
        { id: 'landmarks', label: 'معالم وتراث', icon: '🏛️' },
        { id: 'nature', label: 'طبيعة ومواقع', icon: '🌲' },
        { id: 'education', label: 'تعليم وثقافة', icon: '🎓' },
        { id: 'health', label: 'صحة وإسعاف', icon: '🩺' },
        { id: 'services', label: 'مرافق وخدمات', icon: '⚡' },
    ];

    const directoryItems = [
        {
            id: 1,
            title: 'مقام النبي حم (ع)  ',
            category: 'landmarks',
            categoryLabel: 'معلم سياحي ديني',
            location: 'غرب البلدة حي النبي حم',
            description: 'مقام ديني تاريخي يطل على البلدة من الغرب، ويضم ضريح النبي حم (ع) الذي يعود تاريخه إلى أكثر من 400 سنة، ويستقطب الزوار والمصلين من مختلف المناطق.',
            mapUrl:'https://maps.app.goo.gl/h2kPWLDAQuMi77Wy9',
            badgeColor: 'bg-amber-100 text-amber-800 border-amber-200'
        },
        {
            id: 2,
            title: 'حديقة رومين العامة ',
            category: 'services',
            categoryLabel: 'معلم سياحي وترفيهي',
            location: 'قرب مبنى البلدية',
            description: 'اطلالة على البلدة وعلى تلال السنديان المحيطة، تضم مساحات خضراء وألعاب للأطفال ودكان وقهوة لتلبية طلباتكم ومقاعد للراحة والاستجمام.',
            mapUrl:'https://maps.app.goo.gl/aJdh9uzPHcKMd5CUA',
            badgeColor: 'bg-purple-100 text-purple-800 border-purple-200'
        },
        {
            id: 3,
            title: 'غابات وأحراج السنديان',
            category: 'nature',
            categoryLabel: 'طبيعة وسياحة بيئية',
            location: 'التلال والمنحدرات المحيطة بالبلدة',
            description: 'مساحات خضراء شاسعة تضم أشجار السنديان المعمرة ودروب مشي هادئة تمتاز بالهواء النقي.',
            badgeColor: 'bg-emerald-100 text-emerald-800 border-emerald-200'
        },
        {
            id: 4,
            title: 'كروم الزيتون التراثية',
            category: 'nature',
            categoryLabel: 'إنتاج زراعي',
            location: 'السهول والتلال الزراعية',
            description: 'أشجار زيتون معمرة تشكل عصب الاقتصاد الزراعي التراثي لأهالي رومين وزيت الزيتون المميز.',
            badgeColor: 'bg-emerald-100 text-emerald-800 border-emerald-200'
        },
        {
            id: 5,
            title: 'ثانوية رومين الرسمية',
            category: 'education',
            categoryLabel: 'مؤسسة تعليمية',
            location: 'مدخل البلدة الجنوبي',
            phone: '07 525 410 ',
            description: 'صرح تعليمي رسمي يخدم طلاب بلدة رومين والقرى المجاورة بكافة المراحل الثانوية.',
            mapUrl:'https://maps.app.goo.gl/nKkt1E6LGp5wRuSZA',
            badgeColor: 'bg-cyan-100 text-cyan-800 border-cyan-200'
        },
        {
            id: 6,
            title: 'ملعب البيدر البلدي',
            category: 'services',
            categoryLabel: 'معلم رياضي وترفيهي',
            location: 'حي البيدر',
            description: 'ملعب كرة قدم بلدي وملعب كرة سلة يستضيف مباريات محلية ويتيح لأهالي البلدة ممارسة الرياضة والاستجمام.',
            badgeColor: 'bg-purple-100 text-purple-800 border-purple-200'
        },
        {
            id: 7,
            title: 'ورش صياغة الذهب والفضة',
            category: 'services',
            categoryLabel: 'حرف تقليدية',
            location: 'مختلف أحياء البلدة',
            description: 'حرفة تاريخية دقيقة اشتهر بها أبناء رومين واكتسبوا بها شهرة واسعة في صياغة المجوهرات.',
            badgeColor: 'bg-purple-100 text-purple-800 border-purple-200'
        },
        {
            id: 8,
            title: 'القصر البلدي - مكتب البلدية',
            category: 'services',
            categoryLabel: 'إدارة عامة',
            location: 'مدخل القرية الجنوبي مقابل ثانوية رومين',
            phone: '70 681 711',
            description: 'المركز الإداري لتقديم المعاملات الرسمية واستقبال المواطنين ومتابعة شؤون البلدة.',
            mapUrl: 'https://maps.google.com/?q=Roumine+Municipality',
            badgeColor: 'bg-rose-100 text-rose-800 border-rose-200'
        },
        {
            id: 9,
            title: 'مدرسة رومين المتوسطة الرسمية',
            category: 'education',
            categoryLabel: 'مؤسسة تعليمية',
            location: 'مرج سلال ',
            phone: '07 525 218',
            description: 'مدرسة إعدادية تقدم التعليم المتوسط في منطقة رومين.',
            mapUrl: 'https://maps.app.goo.gl/emtfg4yovFVFS7qe9',
            badgeColor: 'bg-cyan-100 text-cyan-800 border-cyan-200'
        },
        {
            id: 10,
            title: 'معهد رومين الفني للتعليم المهني',
            category: 'education',
            categoryLabel: 'مؤسسة تعليمية',
            location: ' مدحل البلدة الجنوبي',
            phone: '07 525 628',
            description: 'معهد تعليمي تقني يوفر برامج تدريبية متنوعة لطلاب منطقة رومين والقرى المجاورة.',
            mapUrl:'https://maps.app.goo.gl/zChAopXT4stRjkdo9',
            badgeColor: 'bg-cyan-100 text-cyan-800 border-cyan-200'
        },
    ];

    const filteredItems = directoryItems.filter(item => {
        const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
        const matchesSearch = item.title.includes(searchQuery.trim()) || 
                              item.description.includes(searchQuery.trim()) || 
                              item.location.includes(searchQuery.trim());
        return matchesCategory && matchesSearch;
    });

    return (
        <PageWrapper>
            <div className="text-center mb-10">
                <h2 className="text-4xl font-bold text-gray-800 mb-3">دليل بلدتنا والمعالم</h2>
                <p className="text-gray-500 max-w-2xl mx-auto text-lg">
                    اكتشف معالم بلدة رومين الأثرية، مرافقها الحيوية، مؤسساتها، وثرواتها الطبيعية.
                </p>
            </div>

            {/* Search Input */}
            <div className="max-w-xl mx-auto mb-8">
                <div className="relative">
                    <input 
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="ابحث عن معلم، مرفق، أو خدمة..."
                        className="w-full px-5 py-3.5 pr-12 bg-white border border-gray-200 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-700"
                    />
                    <span className="absolute right-4 top-3.5 text-gray-400 text-xl">🔍</span>
                </div>
            </div>

            {/* Categories Filter Bar */}
            <div className="flex flex-wrap items-center justify-center gap-2 mb-12">
                {categories.map(cat => (
                    <button
                        key={cat.id}
                        onClick={() => setSelectedCategory(cat.id)}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition-all duration-300 shadow-xs ${
                            selectedCategory === cat.id
                                ? 'bg-emerald-600 text-white shadow-md scale-105'
                                : 'bg-white text-gray-600 border border-gray-200 hover:border-emerald-400 hover:text-emerald-700'
                        }`}
                    >
                        <span>{cat.icon}</span>
                        <span>{cat.label}</span>
                    </button>
                ))}
            </div>

            {/* Directory Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {filteredItems.length > 0 ? (
                    filteredItems.map(item => (
                        <div 
                            key={item.id}
                            className="bg-white rounded-2xl p-6 shadow-md border border-gray-100 hover:shadow-xl transition-all duration-300 flex flex-col justify-between hover:-translate-y-1"
                        >
                            <div>
                                <div className="flex justify-between items-start mb-3">
                                    <span className={`text-xs font-bold px-3 py-1 rounded-full border ${item.badgeColor}`}>
                                        {item.categoryLabel}
                                    </span>
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-2">{item.title}</h3>
                                <p className="text-xs text-emerald-700 font-semibold mb-3 flex items-center gap-1">
                                    <span>📍</span>
                                    <span>{item.location}</span>
                                </p>
                                <p className="text-sm text-gray-600 leading-relaxed mb-6">
                                    {item.description}
                                </p>
                            </div>

                            <div className="pt-4 border-t border-gray-100 flex items-center justify-between gap-2">
                                {item.phone ? (
                                    <a 
                                        href={`tel:${item.phone.replace(/\s+/g, '')}`}
                                        className="flex-1 bg-emerald-50 text-emerald-700 hover:bg-emerald-600 hover:text-white font-bold py-2 px-3 rounded-xl transition-colors text-center text-xs flex items-center justify-center gap-1.5"
                                    >
                                        <span>📞</span>
                                        <span dir="ltr">{item.phone}</span>
                                    </a>
                                ) : (
                                    <span className="text-xs text-gray-400 font-medium">موقع عام متاح للزيارة</span>
                                )}

                                {item.mapUrl && (
                                    <a 
                                        href={item.mapUrl} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-2 px-3 rounded-xl transition-colors text-xs flex items-center gap-1"
                                    >
                                        <span>🗺️</span>
                                        <span>الخريطة</span>
                                    </a>
                                )}
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="col-span-full text-center py-16 bg-white rounded-2xl shadow-sm border border-gray-100">
                        <p className="text-gray-500 text-lg">لم يتم العثور على معالم أو مرافق تطابق بحثك.</p>
                    </div>
                )}
            </div>
        </PageWrapper>
    );
};

// --- AUTO-DETECT AND CONVERT URLS TO CLICKABLE LINKS ---
// --- AUTO-PARSER FOR BOTH MARKDOWN LINKS [Label](URL) AND RAW URLS ---
const renderClickableText = (text) => {
    if (!text) return "";

    // Regex matches [label](url) OR standalone URLs (http://, https://, or www.)
    const combinedRegex = /\[([^\]]+)\]\((https?:\/\/[^\s\)]+|www\.[^\s\)]+)\)|(https?:\/\/[^\s]+|www\.[^\s]+)/g;

    const elements = [];
    let lastIndex = 0;
    let match;

    while ((match = combinedRegex.exec(text)) !== null) {
        // Push preceding plain text
        if (match.index > lastIndex) {
            elements.push(text.substring(lastIndex, match.index));
        }

        if (match[1] && match[2]) {
            // Case 1: Markdown-style link -> [Label](URL)
            const label = match[1];
            const rawUrl = match[2];
            const href = rawUrl.startsWith("http") ? rawUrl : `https://${rawUrl}`;

            elements.push(
                <a
                    key={match.index}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="underline underline-offset-4 font-bold text-amber-200 hover:text-white transition-colors mx-1"
                >
                    {label}
                </a>
            );
        } else if (match[3]) {
            // Case 2: Standalone raw URL
            const rawUrl = match[3];
            const href = rawUrl.startsWith("http") ? rawUrl : `https://${rawUrl}`;

            elements.push(
                <a
                    key={match.index}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="underline underline-offset-4 font-bold text-amber-200 hover:text-white transition-colors mx-1"
                >
                    {rawUrl}
                </a>
            );
        }

        lastIndex = combinedRegex.lastIndex;
    }

    // Push trailing plain text
    if (lastIndex < text.length) {
        elements.push(text.substring(lastIndex));
    }

    return elements;
};

// --- URGENT TICKER BANNER COMPONENT ---
// --- SINGLE BANNER ITEM ---
const SingleBanner = ({ banner }) => {
    const [isDismissed, setIsDismissed] = useState(false);

    useEffect(() => setIsDismissed(false), [banner.text]);

    if (isDismissed) return null;

    const colorThemes = {
        urgent: "bg-red-600 border-red-700 text-white",
        warning: "bg-amber-500 border-amber-600 text-white",
        info: "bg-emerald-700 border-emerald-800 text-white"
    };

    const activeTheme = colorThemes[banner.type] || colorThemes.urgent;

    return (
        <div className={`${activeTheme} border-b shadow-sm transition-all duration-300 relative z-40 animate-fadeIn`}>
            <div className="container mx-auto px-4 py-2.5 flex items-start sm:items-center justify-between gap-3 text-sm md:text-base font-semibold">
                <div className="flex items-start sm:items-center gap-2.5 flex-1 min-w-0">
                    <span className="flex h-3 w-3 relative shrink-0 mt-1 sm:mt-0">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
                    </span>
                    <span className="shrink-0 bg-black/25 px-2.5 py-0.5 rounded-full text-xs font-bold mt-0.5 sm:mt-0">
                        {banner.type === 'warning' ? '⚠️ تنبيه' : banner.type === 'info' ? '📢 إعلان' : '🚨 عاجل'}
                    </span>
                    <span className="leading-relaxed whitespace-normal break-words text-right flex-1">
                        {renderClickableText(banner.text)}
                    </span>
                </div>
                <button
                    onClick={() => setIsDismissed(true)}
                    className="bg-black/20 hover:bg-black/35 text-white rounded-full p-1.5 transition-colors shrink-0 mt-0.5 sm:mt-0"
                    title="إغلاق التنبيه"
                >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

// --- URGENT TICKERS CONTAINER ---
const UrgentTickerBanner = ({ banners = [] }) => {
    // فلترة التنبيهات المفعلة فقط
    const activeBanners = banners.filter(b => b.isActive);
    if (activeBanners.length === 0) return null;

    return (
        <div className="flex flex-col">
            {activeBanners.map(banner => (
                <SingleBanner key={banner.id} banner={banner} />
            ))}
        </div>
    );
};

// --- GLOBAL SITE SEARCH MODAL COMPONENT ---
const GlobalSearchModal = ({ 
    isOpen, 
    onClose, 
    navigate, 
    news = [], 
    activities = [], 
    documents = [], 
    setSelectedActivityId 
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const inputRef = useRef(null);

    // Auto-focus input when opened & handle Escape key
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 50);
            const handleKeyDown = (e) => {
                if (e.key === 'Escape') onClose();
            };
            window.addEventListener('keydown', handleKeyDown);
            return () => window.removeEventListener('keydown', handleKeyDown);
        } else {
            setSearchTerm('');
        }
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const query = searchTerm.trim().toLowerCase();

    // 1. Search in News
    const matchingNews = query ? news.filter(item => 
        item.title?.toLowerCase().includes(query) || 
        item.content?.toLowerCase().includes(query)
    ).slice(0, 4) : [];

    // 2. Search in Local Activities
    const matchingActivities = query ? activities.filter(item => 
        item.title?.toLowerCase().includes(query) || 
        item.description?.toLowerCase().includes(query)
    ).slice(0, 4) : [];

    // 3. Search in Official Forms & Documents
    const matchingDocs = query ? documents.filter(item => 
        item.title?.toLowerCase().includes(query)
    ).slice(0, 4) : [];

    const totalResults = matchingNews.length + matchingActivities.length + matchingDocs.length;

    const handleSelectResult = (type, item) => {
        onClose();
        if (type === 'activity') {
            if (setSelectedActivityId) setSelectedActivityId(item.id);
            navigate('activities');
        } else if (type === 'news') {
            navigate('news');
        } else if (type === 'document') {
            if (item.url) window.open(item.url, '_blank');
            else navigate('permits');
        }
    };

    return (
        <div 
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 pt-16 sm:pt-24 animate-fadeIn"
            onClick={onClose}
        >
            <div 
                className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden border border-gray-100 flex flex-col max-h-[80vh]"
                onClick={e => e.stopPropagation()}
                dir="rtl"
            >
                {/* Search Bar Input */}
                <div className="p-4 border-b border-gray-100 flex items-center gap-3 bg-gray-50/50">
                    <span className="text-gray-400 text-xl">🔍</span>
                    <input 
                        ref={inputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="ابحث في الأخبار، الأنشطة، المعاملات، والمستندات..."
                        className="w-full bg-transparent border-0 outline-none text-base sm:text-lg text-gray-800 placeholder-gray-400 font-medium"
                    />
                    {searchTerm && (
                        <button 
                            onClick={() => setSearchTerm('')} 
                            className="text-gray-400 hover:text-gray-600 text-sm bg-gray-200 rounded-full w-6 h-6 flex items-center justify-center shrink-0"
                        >
                            ✕
                        </button>
                    )}
                    <button 
                        onClick={onClose} 
                        className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded-md shrink-0 hover:bg-gray-300 transition-colors"
                    >
                        ESC
                    </button>
                </div>

                {/* Results List */}
                <div className="overflow-y-auto p-4 space-y-6 flex-1">
                    {!query ? (
                        <div className="text-center py-10 text-gray-400">
                            <p className="text-sm">اكتب كلمة البحث للوصول السريع إلى أقسام ومحتويات الموقع.</p>
                            <div className="flex flex-wrap justify-center gap-2 mt-4">
                                <button onClick={() => setSearchTerm('بيان')} className="bg-gray-100 text-gray-600 text-xs px-3 py-1.5 rounded-full hover:bg-emerald-50 hover:text-emerald-700">#بيان</button>
                                <button onClick={() => setSearchTerm('مياه')} className="bg-gray-100 text-gray-600 text-xs px-3 py-1.5 rounded-full hover:bg-emerald-50 hover:text-emerald-700">#مياه</button>
                                <button onClick={() => setSearchTerm('رخصة')} className="bg-gray-100 text-gray-600 text-xs px-3 py-1.5 rounded-full hover:bg-emerald-50 hover:text-emerald-700">#رخصة</button>
                                <button onClick={() => setSearchTerm('تنظيف')} className="bg-gray-100 text-gray-600 text-xs px-3 py-1.5 rounded-full hover:bg-emerald-50 hover:text-emerald-700">#تنظيف</button>
                            </div>
                        </div>
                    ) : totalResults === 0 ? (
                        <div className="text-center py-10 text-gray-500">
                            <p className="text-base font-semibold">لم يتم العثور على نتائج تطابق "{searchTerm}"</p>
                            <p className="text-xs text-gray-400 mt-1">تأكد من كتابة الكلمات بشكل صحيح أو جرب كلمات عامة أكثر.</p>
                        </div>
                    ) : (
                        <>
                            {/* Matching Activities */}
                            {matchingActivities.length > 0 && (
                                <div>
                                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                        <span>📸</span>
                                        <span>الأنشطة والفعاليات المحلية ({matchingActivities.length})</span>
                                    </h4>
                                    <div className="space-y-1.5">
                                        {matchingActivities.map(item => (
                                            <div 
                                                key={item.id}
                                                onClick={() => handleSelectResult('activity', item)}
                                                className="p-3 rounded-xl hover:bg-emerald-50 cursor-pointer transition-colors border border-transparent hover:border-emerald-100 flex justify-between items-center group"
                                            >
                                                <div className="min-w-0 flex-1">
                                                    <p className="font-bold text-gray-800 group-hover:text-emerald-700 truncate">{item.title}</p>
                                                    <p className="text-xs text-gray-500 truncate mt-0.5">{item.description}</p>
                                                </div>
                                                <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full shrink-0 mr-2 font-semibold">نشاط</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Matching News */}
                            {matchingNews.length > 0 && (
                                <div>
                                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                        <span>📰</span>
                                        <span>الأخبار والبيانات الرسمية ({matchingNews.length})</span>
                                    </h4>
                                    <div className="space-y-1.5">
                                        {matchingNews.map(item => (
                                            <div 
                                                key={item.id}
                                                onClick={() => handleSelectResult('news', item)}
                                                className="p-3 rounded-xl hover:bg-blue-50 cursor-pointer transition-colors border border-transparent hover:border-blue-100 flex justify-between items-center group"
                                            >
                                                <div className="min-w-0 flex-1">
                                                    <p className="font-bold text-gray-800 group-hover:text-blue-700 truncate">{item.title}</p>
                                                    <p className="text-xs text-gray-500 truncate mt-0.5">{item.content}</p>
                                                </div>
                                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full shrink-0 mr-2 font-semibold">خبر</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Matching Documents */}
                            {matchingDocs.length > 0 && (
                                <div>
                                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                        <span>📑</span>
                                        <span>المعاملات والنماذج ({matchingDocs.length})</span>
                                    </h4>
                                    <div className="space-y-1.5">
                                        {matchingDocs.map(item => (
                                            <div 
                                                key={item.id}
                                                onClick={() => handleSelectResult('document', item)}
                                                className="p-3 rounded-xl hover:bg-amber-50 cursor-pointer transition-colors border border-transparent hover:border-amber-100 flex justify-between items-center group"
                                            >
                                                <p className="font-bold text-gray-800 group-hover:text-amber-800 truncate flex-1">{item.title}</p>
                                                <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full shrink-0 mr-2 font-semibold">تحميل PDF</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Modal Footer */}
                <div className="p-3 bg-gray-50 border-t border-gray-100 text-center text-xs text-gray-400 flex items-center justify-between px-4">
                    <span>انقر على أي نتيجة للانتقال المباشر</span>
                    <span>بلدية رومين</span>
                </div>
            </div>
        </div>
    );
};


// --- MODERN CONFIRMATION & ALERT MODAL COMPONENT ---
const ConfirmationModal = ({ 
    isOpen, 
    onClose, 
    onConfirm, 
    title = 'تأكيد الإجراء', 
    message = 'هل أنت متأكد من رغبتك في المتابعة؟', 
    confirmText = 'تأكيد الحذف', 
    cancelText = 'إلغاء التراجع',
    type = 'danger' 
}) => {
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const isDanger = type === 'danger';

    return (
        <div 
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 animate-fadeIn"
            onClick={onClose}
        >
            <div 
                className="bg-white w-full max-w-md rounded-2xl shadow-2xl p-6 border border-gray-100 transform transition-all animate-fadeInUp text-right"
                onClick={(e) => e.stopPropagation()}
                dir="rtl"
            >
                {/* Header Icon + Title */}
                <div className="flex items-center gap-4 mb-4">
                    <div className={`p-3 rounded-full shrink-0 ${isDanger ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>
                        {isDanger ? (
                            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        ) : (
                            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        )}
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-gray-800">{title}</h3>
                        <p className="text-xs text-gray-400 mt-0.5">بلدية رومين - لوحة التحكم</p>
                    </div>
                </div>

                {/* Message Body */}
                <p className="text-gray-600 text-sm leading-relaxed mb-6 bg-gray-50 p-3.5 rounded-xl border border-gray-100">
                    {message}
                </p>

                {/* Action Buttons */}
                <div className="flex items-center justify-end gap-3 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 sm:flex-none px-5 py-2.5 rounded-xl font-bold text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
                    >
                        {cancelText}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            if (onConfirm) onConfirm();
                            onClose();
                        }}
                        className={`flex-1 sm:flex-none px-5 py-2.5 rounded-xl font-bold text-sm text-white shadow-md transition-all active:scale-95 ${
                            isDanger 
                                ? 'bg-red-600 hover:bg-red-700' 
                                : 'bg-emerald-600 hover:bg-emerald-700'
                        }`}
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- PWA INSTALL PROMPT COMPONENT ---
const PWAInstallPrompt = () => {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showInstallBanner, setShowInstallBanner] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [showIOSHint, setShowIOSHint] = useState(false);

    useEffect(() => {
        // Detect iOS device (iPhone/iPad)
        const isIosDevice = /iphone|ipad|ipod/.test(window.navigator.userAgent.toLowerCase());
        const isStandalone = window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches;

        if (isIosDevice && !isStandalone) {
            setIsIOS(true);
            const dismissed = sessionStorage.getItem('roumine_pwa_ios_dismissed');
            if (!dismissed) setShowInstallBanner(true);
        }

        // Android / Chrome / Desktop install prompt event
        const handleBeforeInstall = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            const dismissed = sessionStorage.getItem('roumine_pwa_dismissed');
            if (!dismissed) setShowInstallBanner(true);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstall);
        return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    }, []);

    const handleInstallClick = async () => {
        if (isIOS) {
            setShowIOSHint(true);
            return;
        }

        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
            setShowInstallBanner(false);
        }
        setDeferredPrompt(null);
    };

    const handleDismiss = () => {
        setShowInstallBanner(false);
        setShowIOSHint(false);
        sessionStorage.setItem(isIOS ? 'roumine_pwa_ios_dismissed' : 'roumine_pwa_dismissed', 'true');
    };

    if (!showInstallBanner) return null;

    return (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:w-96 z-50 bg-white border border-gray-100 rounded-2xl shadow-2xl p-4 animate-fadeInUp" dir="rtl">
            <div className="flex items-start gap-3">
                <div className="bg-emerald-100 text-emerald-800 p-2.5 rounded-xl text-xl shrink-0">
                    📲
                </div>
                <div className="flex-1">
                    <h4 className="font-bold text-gray-800 text-sm">تثبيت تطبيق بلدية رومين</h4>
                    <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                        أضف الموقع إلى شاشة هاتفك الرئيسية لتصفحه بضغطة واحدة وبسرعة فائقة.
                    </p>

                    {showIOSHint && isIOS && (
                        <div className="mt-2.5 p-2 bg-emerald-50 border border-emerald-100 rounded-xl text-xs text-emerald-900 leading-normal">
                            اضغط على زر المشاركة <strong>(Share ⎋)</strong> في أسفل متصفح Safari، ثم اختر <strong>"إضافة إلى الشاشة الرئيسية" (Add to Home Screen ➕)</strong>.
                        </div>
                    )}

                    <div className="flex items-center gap-2 mt-3">
                        <button
                            onClick={handleInstallClick}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-4 py-2 rounded-xl transition-colors shadow-sm"
                        >
                            {isIOS ? 'كيفية التثبيت على iPhone' : 'تثبيت الآن'}
                        </button>
                        <button
                            onClick={handleDismiss}
                            className="bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs px-3 py-2 rounded-xl transition-colors font-medium"
                        >
                            ليس الآن
                        </button>
                    </div>
                </div>
                <button onClick={handleDismiss} className="text-gray-400 hover:text-gray-600 text-sm p-1">
                    ✕
                </button>
            </div>
        </div>
    );
};


// --- DEDICATED SHAREABLE INSTALL PAGE COMPONENT ---
const InstallAppPage = ({ deferredPrompt, navigate }) => {
    const [isIOS, setIsIOS] = useState(false);
    const [isInstalled, setIsInstalled] = useState(false);
    const [installedOutcome, setInstalledOutcome] = useState(null);

    useEffect(() => {
        // Check if user is on iPhone / iPad
        const isIosDevice = /iphone|ipad|ipod/.test(window.navigator.userAgent.toLowerCase());
        setIsIOS(isIosDevice);

        // Check if already opened from Home Screen (standalone)
        const standalone = window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches;
        setIsInstalled(standalone);
    }, []);

    const handleInstallClick = async () => {
        if (!deferredPrompt) {
            alert("لتثبيت التطبيق على جهازك: اضغط على زر القائمة (⋮) في أعلى المتصفح ثم اختر 'تثبيت التطبيق' أو 'Install App'.");
            return;
        }

        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        setInstalledOutcome(outcome);
    };

    return (
        <PageWrapper>
            <div className="max-w-xl mx-auto text-center py-6 px-4" dir="rtl">
                {/* Municipal Logo */}
                <div className="w-24 h-24 mx-auto mb-4 bg-white p-3 rounded-3xl shadow-lg border border-gray-100 flex items-center justify-center">
                    <OfficialLogo className="w-full h-full object-contain" />
                </div>

                <span className="inline-block bg-emerald-100 text-emerald-800 text-xs font-bold px-3 py-1 rounded-full mb-3">
                    تطبيق الويب الرسمي
                </span>
                <h2 className="text-3xl font-black text-gray-800 mb-2">تثبيت تطبيق بلدية رومين</h2>
                <p className="text-gray-500 text-sm leading-relaxed mb-8">
                    احصل على وصول مباشر وسريع لكافة أخبار البلدة، بيانات الطوارئ، والاستعلام عن الرسوم دون الحاجة لمتجر التطبيقات.
                </p>

                {isInstalled || installedOutcome === 'accepted' ? (
                    <div className="bg-emerald-50 border border-emerald-200 p-6 rounded-2xl text-emerald-900 shadow-sm animate-fadeIn">
                        <span className="text-4xl block mb-2">🎉</span>
                        <h4 className="font-bold text-lg mb-1">التطبيق مثبت على هاتفك بنجاح!</h4>
                        <p className="text-sm text-emerald-700 mb-4">يمكنك الآن فتح التطبيق مباشرة من شاشة هاتفك الرئيسية في أي وقت.</p>
                        <button 
                            onClick={() => navigate('home')}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-6 py-2.5 rounded-xl transition-colors shadow"
                        >
                            الانتقال إلى الصفحة الرئيسية
                        </button>
                    </div>
                ) : isIOS ? (
                    /* --- IPHONE / IPAD INSTRUCTIONS --- */
                    <div className="bg-white border border-gray-200 rounded-3xl p-6 shadow-xl text-right animate-fadeIn">
                        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                            <span className="text-2xl">🍏</span>
                            <h4 className="font-bold text-gray-800 text-base">خطوات التثبيت على أجهزة iPhone</h4>
                        </div>

                        <div className="space-y-4 text-sm text-gray-700">
                            <div className="flex items-start gap-3 bg-gray-50 p-3.5 rounded-2xl border border-gray-100">
                                <span className="bg-emerald-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">1</span>
                                <p className="leading-relaxed">
                                    تأكد من فتح الرابط عبر متصفح <strong>Safari</strong>، ثم اضغط على زر <strong>المشاركة (Share ⎋)</strong> في أسفل الشاشة.
                                </p>
                            </div>

                            <div className="flex items-start gap-3 bg-gray-50 p-3.5 rounded-2xl border border-gray-100">
                                <span className="bg-emerald-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">2</span>
                                <p className="leading-relaxed">
                                    مرر للأسفل في القائمة واضغط على خيار <strong>"إضافة إلى الشاشة الرئيسية" (Add to Home Screen ➕)</strong>.
                                </p>
                            </div>

                            <div className="flex items-start gap-3 bg-gray-50 p-3.5 rounded-2xl border border-gray-100">
                                <span className="bg-emerald-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">3</span>
                                <p className="leading-relaxed">
                                    اضغط على <strong>"إضافة" (Add)</strong> في أعلى الزاوية. سيظهر أيقونة التطبيق على شاشة هاتفك مباشرة!
                                </p>
                            </div>
                        </div>
                    </div>
                ) : (
                    /* --- ANDROID / CHROME INSTALL --- */
                    <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-xl text-center animate-fadeIn">
                        <span className="text-5xl block mb-4">📲</span>
                        <h4 className="font-bold text-gray-800 text-lg mb-2">تثبيت بنقرة واحدة</h4>
                        <p className="text-sm text-gray-500 mb-6">
                            اضغط على الزر أدناه لإضافة التطبيق إلى شاشتك الرئيسية فوراً.
                        </p>

                        <button
                            onClick={handleInstallClick}
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-black text-base py-3.5 px-6 rounded-2xl transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2"
                        >
                            <span>📥</span>
                            <span>تثبيت التطبيق على الهاتف</span>
                        </button>

                        <p className="text-xs text-gray-400 mt-4 leading-relaxed">
                            إذا لم يستجب الزر، اضغط على النقاط الثلاث (⋮) في أعلى زاوية المتصفح واختر <strong>"تثبيت التطبيق" (Install app)</strong>.
                        </p>
                    </div>
                )}
            </div>
        </PageWrapper>
    );
};

// --- NOTIFICATION PERMISSION PROMPT ---
const NotificationPrompt = () => {
    const [showPrompt, setShowPrompt] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        // Do not display if already granted, denied, or dismissed this session
        if (!('Notification' in window)) return;
        if (Notification.permission === 'granted' || Notification.permission === 'denied') return;
        
        const dismissed = localStorage.getItem('roumine_notif_dismissed');
        if (!dismissed) {
            const timer = setTimeout(() => setShowPrompt(true), 3500); // Trigger after 3.5s
            return () => clearTimeout(timer);
        }
    }, []);

    const handleEnableNotifications = async () => {
        setIsLoading(true);
        try {
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
                const msg = await messaging();
                if (msg) {
                    const token = await getToken(msg, {
                        vapidKey: "BOJzt71ktlZFvKe4KzBXjbTQqnw1T6EPZAKoRbNEZJMc7tl91dkwbNz5X7ITXWBP_XTd8Z17wCxcIxnVLZ6B8DE" // <-- Paste your VAPID key from Step 1 here
                    });

                    if (token) {
                        // Store device token in Firestore for broadcast dispatching
                        await setDoc(doc(db, "fcmTokens", token), {
                            token: token,
                            deviceType: /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase()) ? 'ios' : 'android_or_desktop',
                            createdAt: serverTimestamp()
                        });
                    }
                }
                setShowPrompt(false);
            } else {
                setShowPrompt(false);
            }
        } catch (err) {
            console.error("Notification permission error:", err);
            setShowPrompt(false);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDismiss = () => {
        setShowPrompt(false);
        localStorage.setItem('roumine_notif_dismissed', 'true');
    };

    if (!showPrompt) return null;

    return (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:w-96 z-50 bg-white border border-gray-100 rounded-2xl shadow-2xl p-4.5 animate-fadeInUp" dir="rtl">
            <div className="flex items-start gap-3.5">
                <div className="bg-amber-100 text-amber-800 p-2.5 rounded-xl text-xl shrink-0">
                    🔔
                </div>
                <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-gray-800 text-sm">تفعيل إشعارات البلدية العاجلة</h4>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                        هل ترغب باستلام تنبيهات فورية عند نشر أخبار رسمية أو تنبيهات طارئة تخص بلدة رومين؟
                    </p>

                    <div className="flex items-center gap-2 mt-3.5">
                        <button
                            onClick={handleEnableNotifications}
                            disabled={isLoading}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-4 py-2 rounded-xl transition-all shadow-sm active:scale-95 disabled:bg-gray-300"
                        >
                            {isLoading ? 'جاري التفعيل...' : 'تفعيل الإشعارات'}
                        </button>
                        <button
                            onClick={handleDismiss}
                            className="bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs px-3 py-2 rounded-xl transition-colors font-semibold"
                        >
                            لاحقاً
                        </button>
                    </div>
                </div>
                <button onClick={handleDismiss} className="text-gray-400 hover:text-gray-600 text-xs p-1">
                    ✕
                </button>
            </div>
        </div>
    );
};

// --- MUNICIPALITY BRANDED LOADER ---
const MunicipalityLoader = ({ text = "جاري تحميل البيانات..." }) => {
    return (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gray-50/90 backdrop-blur-sm transition-all duration-300">
            <div className="relative flex items-center justify-center">
                {/* Outer decorative spinning ring */}
                <div className="w-24 h-24 rounded-full border-4 border-emerald-100 border-t-emerald-600 border-b-emerald-600 animate-spin"></div>
                
                {/* Secondary inner counter-spin accent ring */}
                <div className="absolute w-16 h-16 rounded-full border-2 border-amber-200 border-r-amber-500 border-l-transparent animate-[spin_1.5s_linear_infinite_reverse]"></div>

                {/* Central Civic Landmark Icon (Municipal Portico & Columns) */}
                <div className="absolute flex items-center justify-center w-12 h-12 bg-white rounded-full shadow-md text-emerald-700">
                    <svg 
                        viewBox="0 0 24 24" 
                        fill="currentColor" 
                        className="w-7 h-7 drop-shadow-sm animate-pulse"
                    >
                        {/* Pediment (Triangle Roof) */}
                        <path d="M12 2L1 8v2h22V8L12 2z" />
                        {/* 4 Classical Civic Columns */}
                        <rect x="3.5" y="11" width="2" height="7" rx="0.5" />
                        <rect x="8.5" y="11" width="2" height="7" rx="0.5" />
                        <rect x="13.5" y="11" width="2" height="7" rx="0.5" />
                        <rect x="18.5" y="11" width="2" height="7" rx="0.5" />
                        {/* Base Pedestal Steps */}
                        <path d="M2 19h20v2H2zm-1 3h22v1H1z" />
                    </svg>
                </div>
            </div>

            {/* Municipality Title and Animated Loading Text */}
            <div className="mt-6 text-center select-none">
                <h4 className="text-sm font-bold text-gray-800 tracking-wide">
                    بلدية رومين الرسمية
                </h4>
                <div className="flex items-center justify-center gap-1.5 mt-1 text-xs font-semibold text-emerald-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-bounce"></span>
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-bounce [animation-delay:0.2s]"></span>
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-bounce [animation-delay:0.4s]"></span>
                    <span className="mr-1">{text}</span>
                </div>
            </div>
        </div>
    );
};

// --- SMART WATER DISTRIBUTION WIDGET ---
const WaterScheduleWidget = ({ schedule }) => {
    const [showNotes, setShowNotes] = useState(false);
const [isHighlighted, setIsHighlighted] = useState(false);

    useEffect(() => {
        // Trigger a temporary glow ring if accessed via the water route
        if (window.location.hash === '#water') {
            setIsHighlighted(true);
            const timer = setTimeout(() => setIsHighlighted(false), 3500);
            return () => clearTimeout(timer);
        }
    }, []);

    if (!schedule || !schedule.isActive) return null;

    return (
        <div id="water-schedule" className={`relative overflow-hidden rounded-3xl bg-gradient-to-br from-cyan-600 via-sky-600 to-blue-700 text-white shadow-xl shadow-sky-500/25 mb-10 border border-white/20 animate-fadeIn transition-all ${isHighlighted ? 'ring-4 ring-emerald-400/50' : ''}`}>
            {/* Animated Wave Background SVG Accents */}
            <div className="absolute inset-0 pointer-events-none opacity-20 overflow-hidden">
                <svg className="absolute w-[200%] h-full animate-[spin_25s_linear_infinite]" viewBox="0 0 1000 1000" fill="none">
                    <path d="M0,500 C150,450 350,550 500,500 C650,450 850,550 1000,500 L1000,1000 L0,1000 Z" fill="white" />
                </svg>
            </div>

            <div className="relative z-10 p-5 sm:p-7">
                {/* Header: Title + Current Date Pill */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-5 border-b border-white/15">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center shrink-0 shadow-inner">
                            <span className="text-2xl animate-bounce">💧</span>
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-xl font-black tracking-wide">جدول توزيع مياه الشفة</h3>
                                <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-emerald-400/25 text-emerald-100 border border-emerald-300/30">
                                    <span className="w-2 h-2 rounded-full bg-emerald-300 animate-ping"></span>
                                    ضخ جاري
                                </span>
                            </div>
                            <p className="text-xs text-sky-100 mt-0.5">برنامج التوزيع اليومي وفق الآبار ومصادر الطاقة المتوفرة</p>
                        </div>
                    </div>

                    {/* Date Pill */}
                    <div className="self-start sm:self-auto bg-black/25 backdrop-blur-md px-4 py-1.5 rounded-xl border border-white/10 flex items-center gap-2 text-xs font-bold text-sky-100">
                        <span>📅</span>
                        <span>{schedule.date || "اليوم"}</span>
                    </div>
                </div>

                {/* Grid: 2 Wells (Eastern & Western) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-5">
                    
                    {/* Western Well Card */}
                    <div className="relative group bg-white/15 hover:bg-white/20 backdrop-blur-md rounded-2xl p-4 sm:p-5 border border-white/20 transition-all duration-300 shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-sky-400/30 flex items-center justify-center text-sm">
                                    🚰
                                </div>
                                <span className="text-base font-bold text-white">البئر الغربي</span>
                            </div>
                            <span className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-white/10 text-sky-100 border border-white/10">
                                المحطة الغربية
                            </span>
                        </div>

                        <div className="bg-black/20 rounded-xl p-3 border border-white/10 mb-3">
                            <p className="text-xs text-sky-200 mb-1">المنطقة المستفيدة اليوم:</p>
                            <p className="text-base sm:text-lg font-black text-amber-200 leading-snug">
                                {schedule.westernWell?.target || "غير محدد"}
                            </p>
                        </div>

                        <div className="flex items-center gap-2 text-xs text-sky-100/90 font-medium">
                            <span className="shrink-0">⚡ مصدر الطاقة:</span>
                            <span className="truncate bg-white/10 px-2 py-0.5 rounded text-[11px]">
                                {schedule.westernWell?.power || "طاقة شمسية / كهرباء الدولة"}
                            </span>
                        </div>
                    </div>

                    {/* Eastern Well Card */}
                    <div className="relative group bg-white/15 hover:bg-white/20 backdrop-blur-md rounded-2xl p-4 sm:p-5 border border-white/20 transition-all duration-300 shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-cyan-400/30 flex items-center justify-center text-sm">
                                    🚰
                                </div>
                                <span className="text-base font-bold text-white">البئر الشرقي</span>
                            </div>
                            <span className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-white/10 text-cyan-100 border border-white/10">
                                المحطة الشرقية
                            </span>
                        </div>

                        <div className="bg-black/20 rounded-xl p-3 border border-white/10 mb-3">
                            <p className="text-xs text-sky-200 mb-1">المنطقة المستفيدة اليوم:</p>
                            <p className="text-base sm:text-lg font-black text-amber-200 leading-snug">
                                {schedule.easternWell?.target || "غير محدد"}
                            </p>
                        </div>

                        <div className="flex items-center gap-2 text-xs text-sky-100/90 font-medium">
                            <span className="shrink-0">⚡ مصدر الطاقة:</span>
                            <span className="truncate bg-white/10 px-2 py-0.5 rounded text-[11px]">
                                {schedule.easternWell?.power || "طاقة شمسية / مولد البلدية"}
                            </span>
                        </div>
                    </div>

                </div>

                {/* Footer Notes Accordion */}
                <div className="border-t border-white/15 pt-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-sky-100">
                    <button 
                        onClick={() => setShowNotes(!showNotes)} 
                        className="inline-flex items-center gap-1.5 hover:text-white transition-colors underline font-medium"
                    >
                        <span>ℹ️ تنبيهات هامة بخصوص التوزيع والمراجعة</span>
                        <svg className={`w-3.5 h-3.5 transform transition-transform ${showNotes ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    <span className="text-[11px] text-sky-200/80">
                        يتم تحديث الجدول يومياً من قبل لجنة المياه في البلدية
                    </span>
                </div>

                {/* Expandable note text */}
                {showNotes && (
                    <div className="mt-3 bg-black/25 backdrop-blur-md rounded-xl p-3.5 border border-white/10 text-xs text-sky-100 space-y-1.5 animate-fadeIn">
                        <p className="leading-relaxed">
                            • <strong>ملاحظة:</strong> توفر المياه بالشبكة المذكورة مرتبط مباشرة بتوفر مصادر الطاقة المذكورة لكل بئر.
                        </p>
                        <p className="leading-relaxed">
                            • <strong>للمراجعة:</strong> في حال وجود أي مشكلة في وصول المياه، يرجى مراجعة البلدية أو مسؤول الضخ أثناء الدور لمعالجتها على الفور.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- OBITUARIES & CONDOLENCES PAGE (PURE TEXT & TIMETABLE) ---
const ObituariesPage = ({ obituaries = [] }) => {
    return (
        <PageWrapper>
            {/* Header */}
            <div className="text-center max-w-3xl mx-auto mb-12">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-stone-900 text-amber-400 mb-4 shadow-md text-2xl border border-stone-800">
                    🕊️
                </div>
                <h2 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">
                    وفيات وتعازي بلدة رومين
                </h2>
                <p className="text-sm sm:text-base text-gray-600 mt-2 font-medium">
                    "إِنَّا لِلَّهِ وَإِنَّا إِلَيْهِ رَاجِعُونَ" • مواعيد الدفن، مجالس العزاء، والذكرى السنوية
                </p>
                <div className="w-24 h-1 bg-amber-500/60 mx-auto mt-4 rounded-full"></div>
            </div>

            {/* List */}
            {obituaries.length === 0 ? (
                <div className="bg-white rounded-3xl p-12 text-center border border-gray-200 max-w-md mx-auto shadow-sm">
                    <span className="text-4xl block mb-3">🕯️</span>
                    <p className="text-gray-700 font-bold text-base">لا توجد إعلانات وفيات أو مجالس عزاء حالياً.</p>
                    <p className="text-gray-400 text-xs mt-1">نسأل الله تعالى دوام الصحة والسلامة لجميع أهالي بلدتنا.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
                    {obituaries.map((item) => {
                        

                        const audienceMap = {
    general: { label: "الدعوة عامة", color: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    men: { label: "الدعوة خاصة بالرجال", color: "bg-sky-50 text-sky-800 border-sky-200" },
    women: { label: "الدعوة خاصة بالنساء", color: "bg-pink-50 text-pink-800 border-pink-200" }
};
const aud = audienceMap[item.audience] || audienceMap.general;
                        const shareText = encodeURIComponent(
                            `🕊️ *${item.title}: ${item.deceasedName}*\n${item.eventDate ? `📅 التاريخ: ${item.eventDate}\n` : ""}${item.eventTime ? `⏰ الوقت: ${item.eventTime}\n` : ""}${item.location ? `📍 المكان: ${item.location}\n` : ""}\nتفاصيل أكثر عبر موقع بلدية رومين:\nhttps://roumine.org/#obituaries`
                        );

                        return (
                            <div 
                                key={item.id} 
                                className="bg-white rounded-3xl border border-gray-200 shadow-md hover:shadow-xl transition-all duration-300 flex flex-col justify-between overflow-hidden"
                            >
                                <div>
                                    {/* Card Header: Type Badge as Title */}
                                    <div className="bg-gradient-to-r from-stone-900 via-stone-800 to-stone-900 text-white p-5 flex items-center justify-between border-b border-stone-700">
                                        <div className="flex items-center gap-3">
                                            <span className="text-xl">🕯️</span>
                                            <h3 className="font-black text-lg text-amber-100">
                                                {item.deceasedName}
                                            </h3>
                                        </div>
                                       <div className="flex items-center gap-2">
                    <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full border shadow-sm ${aud.color}`}>
                        {aud.label}
                    </span>
                    <span className="text-xs font-bold px-3 py-1 rounded-full bg-stone-800 text-stone-200 border border-stone-700 shadow-sm">
                        {item.title}
                    </span>
                </div>
                                    </div>

                                    {/* Details */}
                                    <div className="p-5 sm:p-6 space-y-4">
                                        {/* Date, Time, Venue Pills */}
                                        <div className="bg-stone-50 rounded-2xl p-4 border border-stone-200/70 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-stone-700 font-medium">
                                            {item.eventDate && (
                                                <div className="flex items-center gap-2">
                                                    <span className="text-stone-400">📅</span>
                                                    <span className="font-bold">{item.eventDate}</span>
                                                </div>
                                            )}
                                            {item.eventTime && (
                                                <div className="flex items-center gap-2">
                                                    <span className="text-stone-400">⏰</span>
                                                    <span className="font-bold">{item.eventTime}</span>
                                                </div>
                                            )}
                                            {item.location && (
                                                <div className="flex items-center gap-2">
                                                    <span className="text-stone-400">📍</span>
                                                    <span className="font-bold truncate">{item.location}</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Notice Text (WhatsApp formatting preserved) */}
                                        {item.content && (
                                            <div className="bg-stone-50/60 p-4 rounded-2xl border border-stone-100">
                                                <p className="text-sm text-stone-800 leading-relaxed whitespace-pre-line font-medium text-right font-cairo">
                                                    {item.content}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Footer: Share Button */}
                                <div className="px-5 sm:px-6 pb-5 pt-3 border-t border-gray-100 flex items-center justify-between">
                                    <span className="text-[11px] text-gray-400">
                                        الفاتحة لروحه الطاهرة
                                    </span>
                                    <a
                                        href={`https://wa.me/?text=${shareText}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 px-3.5 py-1.5 rounded-xl text-xs font-bold transition-colors shadow-sm"
                                    >
                                        <span>📲</span>
                                        <span>مشاركة عبر واتساب</span>
                                    </a>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </PageWrapper>
    );
};

// --- HOMEPAGE CONDOLENCES & OBITUARIES WIDGET ---
const CondolencesWidget = ({ obituaries = [], navigate }) => {
    if (!obituaries || obituaries.length === 0) return null;

    // 📍 Sort ascending by event date & time so today's event is chosen first
    const sortedUpcoming = [...obituaries].sort((a, b) => {
        const timeA = `${a.eventDateIso || "9999-99-99"} ${a.eventTimeRaw || "00:00"}`;
        const timeB = `${b.eventDateIso || "9999-99-99"} ${b.eventTimeRaw || "00:00"}`;
        return timeA.localeCompare(timeB);
    });

    const upcoming = sortedUpcoming[0];
    const totalCount = sortedUpcoming.length;

    const audienceStyles = {
        general: { label: "الدعوة عامة", class: "bg-emerald-950/70 text-emerald-300 border-emerald-800/60" },
        men: { label: "خاصة بالرجال", class: "bg-sky-950/70 text-sky-300 border-sky-800/60" },
        women: { label: "خاصة بالنساء", class: "bg-pink-950/70 text-pink-300 border-pink-800/60" }
    };
    const audienceBadge = audienceStyles[upcoming.audience] || audienceStyles.general;

    return (
        <div 
            onClick={() => navigate('obituaries')}
            className="group relative cursor-pointer overflow-hidden rounded-3xl bg-gradient-to-r from-stone-950 via-stone-900 to-stone-950 text-white p-5 sm:p-6 shadow-xl border border-stone-800 hover:border-amber-500/50 transition-all duration-300 mb-10 animate-fadeIn"
        >
            <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
                
                <div className="flex items-start sm:items-center gap-4 min-w-0">
                    <div className="w-12 h-12 rounded-2xl bg-stone-800/90 border border-stone-700 flex items-center justify-center text-2xl shrink-0 group-hover:scale-105 transition-transform shadow-inner">
                        🕊️
                    </div>

                    <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-stone-800 text-stone-200 border border-stone-700">
                                {upcoming.title}
                            </span>
                            
                            <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full border shadow-sm ${audienceBadge.class}`}>
                                {audienceBadge.label}
                            </span>

                            <span className="text-xs text-stone-400 font-medium">
                                إِنَّا لِلَّهِ وَإِنَّا إِلَيْهِ رَاجِعُونَ
                            </span>
                        </div>

                        <h3 className="text-base sm:text-lg font-black text-stone-100 group-hover:text-amber-200 transition-colors break-words leading-snug">
        {upcoming.deceasedName}
    </h3>

                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-stone-300 mt-1.5 font-medium">
                            {upcoming.eventDate && <span>📅 {upcoming.eventDate}</span>}
                            {upcoming.eventTime && <span>⏰ {upcoming.eventTime}</span>}
                            {upcoming.location && <span className="truncate">📍 {upcoming.location}</span>}
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-between md:justify-end gap-3 shrink-0 pt-3 md:pt-0 border-t md:border-t-0 border-stone-800/80">
                    {totalCount > 1 && (
                        <span className="text-xs font-bold px-3 py-1 rounded-xl bg-stone-800 text-stone-300 border border-stone-700">
                            +{totalCount - 1} إعلانات أخرى
                        </span>
                    )}

                    <div className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-amber-300 group-hover:text-amber-200 transition-colors">
                        <span>التفاصيل ومجالس العزاء</span>
                        <span className="text-base transform group-hover:-translate-x-1 transition-transform">←</span>
                    </div>
                </div>

            </div>
        </div>
    );
};

ReactGA.initialize(process.env.REACT_APP_MEASUREMENT_ID);


// --- MAIN APP COMPONENT ---

export default function App() {
    const [currentPage, setCurrentPage] = useState('home');
    const [news, setNews] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [surveys, setSurveys] = useState([]);
    const [complaints, setComplaints] = useState([]);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activities, setActivities] = useState([]);
    const [selectedActivityId, setSelectedActivityId] = useState(null);
const [banners, setBanners] = useState([]);
const [isSearchOpen, setIsSearchOpen] = useState(false);
const [isLoadingData, setIsLoadingData] = useState(true);
const [waterSchedule, setWaterSchedule] = useState(null);
const [obituaries, setObituaries] = useState([]);

// 1. Get current date in local YYYY-MM-DD format (avoids UTC timezone shift)
const getTodayIso = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// 2. Auto-clean expired obituaries & sync state
const syncAndCleanObituaries = async (docs) => {
    const todayIso = getTodayIso();
    const activeList = [];

    for (const d of docs) {
        const data = d.data();
        // Auto-delete expired notices
        if (data.eventDateIso && data.eventDateIso < todayIso) {
            deleteDoc(doc(db, "obituaries", d.id)).catch(err => 
                console.warn("Auto-delete expired obituary error:", err)
            );
        } else {
            activeList.push({ id: d.id, ...data });
        }
    }

    // 📍 Chronological sort: Closest upcoming date/time first (Ascending)
    activeList.sort((a, b) => {
        const timeA = `${a.eventDateIso || "9999-99-99"} ${a.eventTimeRaw || "00:00"}`;
        const timeB = `${b.eventDateIso || "9999-99-99"} ${b.eventTimeRaw || "00:00"}`;
        return timeA.localeCompare(timeB);
    });

    setObituaries(activeList);
};
// --- CONFIRMATION MODAL STATE ---
    const [confirmDialog, setConfirmDialog] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: null,
        confirmText: 'تأكيد الحذف',
        cancelText: 'إلغاء التراجع',
        type: 'danger'
    });

    const triggerConfirm = ({ title, message, onConfirm, confirmText = 'تأكيد الحذف', cancelText = 'إلغاء التراجع', type = 'danger' }) => {
        setConfirmDialog({
            isOpen: true,
            title,
            message,
            onConfirm,
            confirmText,
            cancelText,
            type
        });
    };

    const closeConfirm = () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false, onConfirm: null }));
    };
// --- install prompt state ---
const [deferredPrompt, setDeferredPrompt] = useState(null);

useEffect(() => {
    const handleBeforeInstall = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
}, []);

// --- 2. UPDATED NAVIGATION FUNCTION ---
const navigate = (page) => {
    window.scrollTo(0, 0);
    const targetPage = typeof page === 'string' ? page : 'home';
    if (window.location.hash !== `#${targetPage}`) {
        window.location.hash = `#${targetPage}`;
    }
    setCurrentPage(targetPage);
}

    // --- 1. SYNC URL HASH WITH CURRENT PAGE ---
   useEffect(() => {
    const validPages = [
        'home', 'about', 'news', 'activities', 'permits', 
        'fees', 'emergency', 'surveys','complaints', 'faqs','directory', 'contact', 'admin', 'install','obituaries'
    ];


    const scrollToWaterWidget = () => {
        setTimeout(() => {
            const element = document.getElementById('water-schedule');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 350);
    };

    const syncPageFromHash = () => {
        const hash = window.location.hash.replace('#', '').trim();

        // 📍 1. Handle #water route: load 'home' and scroll to the widget
        if (hash === 'water') {
            setCurrentPage('home');
            scrollToWaterWidget();
            return;
        }

        // 📍 2. Standard page routing
        const activePage = validPages.includes(hash) ? hash : 'home';
        setCurrentPage(activePage);
    };

    // Sync current page on page load / link paste
    syncPageFromHash();

    window.addEventListener('hashchange', syncPageFromHash);
    return () => window.removeEventListener('hashchange', syncPageFromHash);
}, []);

useEffect(() => {
        if (!isLoadingData && window.location.hash === '#water') {
            const timer = setTimeout(() => {
                const element = document.getElementById('water-schedule');
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 300);

            return () => clearTimeout(timer);
        }
    }, [isLoadingData]);

useEffect(() => {
        ReactGA.send({ 
            hitType: "pageview", 
            page: `/${currentPage}`, 
            title: currentPage 
        });
    }, [currentPage]);
    
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

useEffect(() => {

    
    // 1. Fetch initial collections in parallel for fast loading
    const fetchData = async () => {
        try {
            const newsQuery = query(collection(db, "news"), orderBy("createdAt", "desc"));
            const docQuery = query(collection(db, "documents"), orderBy("createdAt", "desc"));
            const surveyQuery = query(collection(db, "surveys"), orderBy("createdAt", "desc"));
            const complaintQuery = query(collection(db, "complaints"), orderBy("createdAt", "desc"));
            const obituariesQuery = query(collection(db, "obituaries"), orderBy("createdAt", "desc"));
            // Run all 4 queries concurrently instead of sequentially
            const [
                newsSnapshot,
                documentsSnapshot,
                surveysSnapshot,
                complaintsSnapshot,
                obituariesSnapshot
            ] = await Promise.all([
                getDocs(newsQuery),
                getDocs(docQuery),
                getDocs(surveyQuery),
                getDocs(complaintQuery),
                getDocs(obituariesQuery),                
            ]);

            setNews(newsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setDocuments(documentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setSurveys(surveysSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setComplaints(complaintsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      syncAndCleanObituaries(obituariesSnapshot.docs);
        } catch (error) {
            console.error("Error fetching initial municipal data:", error);
        } finally {
            // Dismiss loader once all initial data arrives (or fails gracefully)
            setIsLoadingData(false);
        }
    };

    fetchData();

    // 2. Real-time live listener for multi-banner alerts
    const bannersQuery = query(collection(db, "banners"), orderBy("createdAt", "desc"));
    const unsubscribeBanners = onSnapshot(bannersQuery, (snapshot) => {
        setBanners(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (err) => console.error("Banners error:", err));
const unsubscribeWater = onSnapshot(
        doc(db, "settings", "waterSchedule"),
        (docSnap) => {
            if (docSnap.exists()) {
                setWaterSchedule(docSnap.data());
            }
        },
        (err) => console.error("Water schedule error:", err)
    );
    
// 📍 4. Real-time live listener for obituaries & condolences
    const obituariesQuery = query(collection(db, "obituaries"), orderBy("createdAt", "desc"));
   const unsubscribeObituaries = onSnapshot(obituariesQuery, (snapshot) => {
    syncAndCleanObituaries(snapshot.docs);
}, (err) => console.error("Obituaries listener error:", err));
    // 3. Cleanup listener on component unmount
    return () => {
        unsubscribeBanners();
        unsubscribeWater();
        unsubscribeObituaries();
    };
// eslint-disable-next-line react-hooks/exhaustive-deps
},[]);

    // Foreground notification listener
useEffect(() => {
    let unsubscribe = () => {};

    const setupForegroundListener = async () => {
        try {
            const msg = typeof messaging === 'function' ? await messaging() : null;
            if (msg) {
                unsubscribe = onMessage(msg, (payload) => {
                console.log("Foreground message received:", payload);
                    // Trigger browser notification directly when tab is in foreground
                });
            }
        } catch (err) {
            console.error("Foreground messaging setup error:", err);
        }
    };

    setupForegroundListener();
    return () => unsubscribe();
}, []);

    const handleLogin = (email, password) => signInWithEmailAndPassword(auth, email, password);
    const handleLogout = () => signOut(auth);

    const addNews = async (newsItem) => {
        const { title, content, date, imageFile } = newsItem;
        const fileType = detectFileType(imageFile);

        let fileUrl = '';
        let thumbnailUrl = '';

        if (fileType === 'image') {
            const compressedBlob = await compressToWebP(imageFile, 1000, 0.65);
            const storageRef = ref(storage, `news-files/opt_${Date.now()}.webp`);
            await uploadBytes(storageRef, compressedBlob, { contentType: 'image/webp' });
            fileUrl = await getDownloadURL(storageRef);
            thumbnailUrl = fileUrl;
        } else {
            const ext = imageFile.name.split('.').pop();
            const docRef = ref(storage, `news-files/doc_${Date.now()}.${ext}`);
            await uploadBytes(docRef, imageFile);
            fileUrl = await getDownloadURL(docRef);

            const thumbBlob = await generateBrandedDocumentThumbnail(imageFile, title, fileType);
            const thumbRef = ref(storage, `news-files/thumb_${Date.now()}.webp`);
            await uploadBytes(thumbRef, thumbBlob, { contentType: 'image/webp' });
            thumbnailUrl = await getDownloadURL(thumbRef);
        }

        const newNews = { 
            title, 
            content, 
            date, 
            imageUrl: fileUrl, 
            thumbnailUrl: thumbnailUrl, 
            fileType, 
            createdAt: new Date() 
        };

        const docRef = await addDoc(collection(db, "news"), newNews);
        setNews(prev => [{ id: docRef.id, ...newNews }, ...prev]);
    };

    const optimizeAllExistingNews = async (onProgress) => {
        let count = 0;
        const total = news.length;

        for (const item of news) {
            count++;
            if (onProgress) onProgress(`جاري ضغط الصورة (${count} من ${total}): ${item.title}`);

            try {
                if (item.imageUrl && item.imageUrl.includes('opt_') && item.imageUrl.includes('.webp')) {
                    continue;
                }

                const compressedBlob = await compressToWebP(item.imageUrl, 1000, 0.65);
                const newStorageRef = ref(storage, `news-images/opt_${Date.now()}_${item.id}.webp`);
                await uploadBytes(newStorageRef, compressedBlob, { contentType: 'image/webp' });
                const newImageUrl = await getDownloadURL(newStorageRef);

                const docRef = doc(db, "news", item.id);
                await updateDoc(docRef, { imageUrl: newImageUrl });

                if (item.imageUrl) {
                    const oldStorageRef = ref(storage, item.imageUrl);
                    await deleteObject(oldStorageRef).catch(() => {});
                }
            } catch (error) {
                console.error(`Error optimizing ${item.title}:`, error);
            }
        }

        const newsSnapshot = await getDocs(query(collection(db, "news"), orderBy("createdAt", "desc")));
        setNews(newsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
        
const deleteNews = (id) => {
        const newsToDelete = news.find(item => item.id === id);
        triggerConfirm({
            title: "حذف خبر رسمي",
            message: newsToDelete?.title 
                ? `هل أنت متأكد من حذف خبر "${newsToDelete.title}" نهائياً من الموقع وقاعدة البيانات؟`
                : "هل أنت متأكد من حذف هذا الخبر نهائياً؟",
            confirmText: "نعم، حذف الخبر",
            type: "danger",
            onConfirm: async () => {
                try {
                    if (newsToDelete && newsToDelete.imageUrl) {
                        const imageRef = ref(storage, newsToDelete.imageUrl);
                        await deleteObject(imageRef).catch(() => {});
                    }
                    if (newsToDelete && newsToDelete.thumbnailUrl && newsToDelete.thumbnailUrl !== newsToDelete.imageUrl) {
                        const thumbRef = ref(storage, newsToDelete.thumbnailUrl);
                        await deleteObject(thumbRef).catch(() => {});
                    }
                    await deleteDoc(doc(db, "news", id));
                    setNews(prev => prev.filter(item => item.id !== id));
                } catch (error) {
                    console.error("Failed to delete news:", error);
                }
            }
        });
    };

    const updateNews = async (id, { title, content, newImageFile, oldImageUrl, oldThumbnailUrl }) => {
        let imageUrl = oldImageUrl;
        let thumbnailUrl = oldThumbnailUrl || oldImageUrl;
        let fileType = detectFileType(newImageFile || oldImageUrl);

        if (newImageFile) {
            if (fileType === 'image') {
                const compressedBlob = await compressToWebP(newImageFile, 1000, 0.65);
                const storageRef = ref(storage, `news-files/opt_${Date.now()}.webp`);
                await uploadBytes(storageRef, compressedBlob, { contentType: 'image/webp' });
                imageUrl = await getDownloadURL(storageRef);
                thumbnailUrl = imageUrl;
            } else {
                const ext = newImageFile.name.split('.').pop();
                const docRef = ref(storage, `news-files/doc_${Date.now()}.${ext}`);
                await uploadBytes(docRef, newImageFile);
                imageUrl = await getDownloadURL(docRef);

                const thumbBlob = await generateBrandedDocumentThumbnail(newImageFile, title, fileType);
                const thumbRef = ref(storage, `news-files/thumb_${Date.now()}.webp`);
                await uploadBytes(thumbRef, thumbBlob, { contentType: 'image/webp' });
                thumbnailUrl = await getDownloadURL(thumbRef);
            }

            if (oldImageUrl) {
                deleteObject(ref(storage, oldImageUrl)).catch(() => {});
            }
            if (oldThumbnailUrl && oldThumbnailUrl !== oldImageUrl) {
                deleteObject(ref(storage, oldThumbnailUrl)).catch(() => {});
            }
        }

        const newsRef = doc(db, "news", id);
        const updatedFields = { title, content, imageUrl, thumbnailUrl, fileType };
        await updateDoc(newsRef, updatedFields);

        setNews(prev => prev.map(item => item.id === id ? { ...item, ...updatedFields } : item));
    };

    const addBanner = async (bannerData) => {
    try {
        await addDoc(collection(db, "banners"), {
            ...bannerData,
            createdAt: serverTimestamp()
        });
    } catch (error) {
        console.error("Failed to add banner:", error);
        alert("حدث خطأ أثناء إضافة التنبيه.");
    }
};

const toggleBannerStatus = async (id, currentStatus) => {
    try {
        await updateDoc(doc(db, "banners", id), { isActive: !currentStatus });
    } catch (error) {
        console.error("Failed to toggle banner:", error);
    }
};

const deleteBanner = (id) => {
    triggerConfirm({
        title: "حذف تنبيه",
        message: "هل أنت متأكد من حذف هذا التنبيه نهائياً؟",
        confirmText: "حذف",
        type: "danger",
        onConfirm: async () => {
            await deleteDoc(doc(db, "banners", id));
        }
    });
};
  
const updateWaterSchedule = async (scheduleData) => {
    try {
        await setDoc(doc(db, "settings", "waterSchedule"), {
            ...scheduleData,
            updatedAt: serverTimestamp()
        }, { merge: true });
    } catch (error) {
        console.error("Failed to update water schedule:", error);
        alert("فشل تحديث الجدول، يرجى المحاولة لاحقاً.");
    }
};

    // 1. Fetch Activities from Firestore
useEffect(() => {
    const fetchActivities = async () => {
        const activitiesColl = collection(db, "activities");
        const actQuery = query(activitiesColl, orderBy("createdAt", "desc"));
        const snapshot = await getDocs(actQuery);
        setActivities(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    fetchActivities();
}, []);

// 2. Add Activity CMS function (Uploads Collage Images + Automatically adds to News Page)
const addActivity = async ({ title, description, date, imageFiles }) => {
    if (!imageFiles || imageFiles.length === 0) return;

    // Upload & Compress All Collage Images
    const imageUrls = await Promise.all(
        Array.from(imageFiles).map(async (file) => {
            const compressedBlob = await compressToWebP(file, 1000, 0.65);
            const storageRef = ref(storage, `activities-files/opt_${Date.now()}_${Math.random().toString(36).substring(7)}.webp`);
            await uploadBytes(storageRef, compressedBlob, { contentType: 'image/webp' });
            return await getDownloadURL(storageRef);
        })
    );

    const newActivity = {
        title,
        description,
        date,
        images: imageUrls,
        imageUrl: imageUrls[0], // Main cover photo
        createdAt: new Date()
    };

    // Save to Firestore "activities"
    const actDocRef = await addDoc(collection(db, "activities"), newActivity);
    const activityWithId = { id: actDocRef.id, ...newActivity };
    setActivities(prev => [activityWithId, ...prev]);

    // Automatically create corresponding entry in "news" collection
    const newsItem = {
        title: `نشاط بلدي: ${title}`,
        content: description,
        date,
        imageUrl: imageUrls[0],
        thumbnailUrl: imageUrls[0],
        fileType: 'activity',
        activityId: actDocRef.id,
        createdAt: new Date()
    };

    const newsDocRef = await addDoc(collection(db, "news"), newsItem);
    setNews(prev => [{ id: newsDocRef.id, ...newsItem }, ...prev]);
};

// 3. Delete Activity Function
const deleteActivity = (id) => {
        const actToDelete = activities.find(a => a.id === id);
        triggerConfirm({
            title: "حذف نشاط محلي",
            message: actToDelete?.title
                ? `هل أنت متأكد من حذف نشاط "${actToDelete.title}" وكافة ألبوم الصور التابع له نهائياً؟`
                : "هل أنت متأكد من حذف هذا النشاط وكافة صوره؟",
            confirmText: "نعم، حذف النشاط",
            type: "danger",
            onConfirm: async () => {
                try {
                    if (actToDelete && actToDelete.images) {
                        actToDelete.images.forEach(imgUrl => {
                            deleteObject(ref(storage, imgUrl)).catch(() => {});
                        });
                    }
                    await deleteDoc(doc(db, "activities", id));
                    setActivities(prev => prev.filter(a => a.id !== id));
                    setNews(prev => prev.filter(n => n.activityId !== id));
                } catch (error) {
                    console.error("Failed to delete activity:", error);
                }
            }
        });
    };
    
    
    
const addDocument = async ({ title, category, file }) => {
    const filePath = `documents/${Date.now()}_${file.name}`;
    const fileRef = ref(storage, filePath);
    
    await uploadBytes(fileRef, file);
    const url = await getDownloadURL(fileRef);

    const newDoc = { 
        title, 
        category: category || 'عام', 
        url, 
        filePath, 
        createdAt: new Date() 
    };
    const docRef = await addDoc(collection(db, "documents"), newDoc);
    
    setDocuments(prev => [{ id: docRef.id, ...newDoc }, ...prev]);
};
        
const deleteDocument = (id, filePath) => {
        const docToDelete = documents.find(d => d.id === id);
        triggerConfirm({
            title: "حذف معاملة أو نموذج",
            message: docToDelete?.title
                ? `هل أنت متأكد من حذف مستند "${docToDelete.title}" نهائياً؟ لن يتمكن المواطنون من تحميله بعد الآن.`
                : "هل أنت متأكد من حذف هذا المستند؟",
            confirmText: "نعم، حذف المستند",
            type: "danger",
            onConfirm: async () => {
                try {
                    if (filePath) {
                        const fileRef = ref(storage, filePath);
                        await deleteObject(fileRef).catch(() => {});
                    }
                    await deleteDoc(doc(db, "documents", id));
                    setDocuments(prev => prev.filter(doc => doc.id !== id));
                } catch (error) {
                    console.error("Failed to delete document:", error);
                }
            }
        });
    };

    const updateDocumentCategory = async (id, newCategory) => {
    try {
        const docRef = doc(db, "documents", id);
        await updateDoc(docRef, { category: newCategory });
        setDocuments(prev => prev.map(d => d.id === id ? { ...d, category: newCategory } : d));
    } catch (error) {
        console.error("Error updating document category:", error);
        alert("حدث خطأ أثناء تحديث التصنيف.");
    }
};

    const addSurvey = async (survey) => {
        const newSurvey = { ...survey, createdAt: new Date() };
        const docRef = await addDoc(collection(db, "surveys"), newSurvey);
        setSurveys(prev => [{ id: docRef.id, ...newSurvey }, ...prev]);
    };

const deleteSurvey = (id) => {
        const surveyToDelete = surveys.find(s => s.id === id);
        triggerConfirm({
            title: "حذف استطلاع أو استمارة",
            message: surveyToDelete?.title
                ? `هل أنت متأكد من رغبتك في إيقاف وحذف استطلاع "${surveyToDelete.title}"؟`
                : "هل أنت متأكد من حذف هذا الاستطلاع؟",
            confirmText: "نعم، حذف الاستطلاع",
            type: "danger",
            onConfirm: async () => {
                try {
                    await deleteDoc(doc(db, "surveys", id));
                    setSurveys(prev => prev.filter(survey => survey.id !== id));
                } catch (error) {
                    console.error("Failed to delete survey:", error);
                }
            }
        });
    };

    const addComplaint = async (complaint) => {
        const newComplaint = { ...complaint, createdAt: serverTimestamp(), status: 'pending' };
        await addDoc(collection(db, "complaints"), newComplaint);
        
        if (user) {
            const complaintsSnapshot = await getDocs(query(collection(db, "complaints"), orderBy("createdAt", "desc")));
            setComplaints(complaintsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }
    };

    const updateComplaintStatus = async (id, newStatus) => {
        const complaintRef = doc(db, "complaints", id);
        await updateDoc(complaintRef, { status: newStatus });
        setComplaints(prev => prev.map(c => c.id === id ? { ...c, status: newStatus } : c));
    };

const deleteComplaint = (id) => {
        const complaintToDelete = complaints.find(c => c.id === id);
        triggerConfirm({
            title: "حذف شكوى مواطن",
            message: complaintToDelete?.subject
                ? `هل أنت متأكد من رغبتك في حذف الشكوى المعنونة "${complaintToDelete.subject}" من الأرشيف؟`
                : "هل أنت متأكد من حذف هذه الشكوى؟",
            confirmText: "نعم، حذف الشكوى",
            type: "danger",
            onConfirm: async () => {
                try {
                    await deleteDoc(doc(db, "complaints", id));
                    setComplaints(prev => prev.filter(c => c.id !== id));
                } catch (error) {
                    console.error("Failed to delete complaint:", error);
                }
            }
        });
    };

// Add Obituary (Pure Firestore - No Storage needed)
const addObituary = async ({ deceasedName, type,audience, content, eventDate, eventDateIso, eventTime, eventTimeRaw, location, sendPush }) => {
    try {
        const typeLabels = {
            funeral: "وفاة ودفن",
            memorial: "مجلس عزاء",
            anniversary: "ذكرى سنوية"
        };

        await addDoc(collection(db, "obituaries"), {
            title: typeLabels[type] || "نعي أليم",
            deceasedName,
            type: type || "funeral",
            audience: audience || "general", // 👈 'general' | 'men' | 'women'
            content: content || "",
            eventDate: eventDate || "",
            eventDateIso: eventDateIso || "",
            eventTime: eventTime || "",
            eventTimeRaw: eventTimeRaw || "",
            location: location || "حسينية بلدة رومين",
            sendPush: Boolean(sendPush),
            createdAt: serverTimestamp()
        });

        alert("تم نشر الإعلان بنجاح!");
    } catch (error) {
        console.error("Error adding obituary:", error);
        alert("حدث خطأ أثناء إضافة النعي.");
    }
};

// Delete Obituary (Direct Firestore document delete)
const deleteObituary = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا الإعلان؟")) return;
    try {
        await deleteDoc(doc(db, "obituaries", id));
        alert("تم الحذف بنجاح.");
    } catch (error) {
        console.error("Error deleting obituary:", error);
    }
};  



// 3. Update existing obituary
const updateObituary = async (id, updatedData) => {
    try {
        const typeLabels = {
            funeral: "وفاة ودفن",
            memorial: "مجلس عزاء",
            anniversary: "ذكرى سنوية"
        };

        const docRef = doc(db, "obituaries", id);
        await updateDoc(docRef, {
            ...updatedData,
            audience: updatedData.audience || "general", // 👈 Include audience
            title: typeLabels[updatedData.type] || "نعي أليم",
            updatedAt: serverTimestamp()
        });

        alert("تم تحديث الإعلان بنجاح!");
    } catch (error) {
        console.error("Error updating obituary:", error);
        alert("حدث خطأ أثناء تعديل الإعلان.");
    }
};



    const handleFeesUpload = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = async (event) => {
                try {
                    const jsonData = JSON.parse(event.target.result);
                    if (!Array.isArray(jsonData)) {
                        throw new Error("JSON file must be an array.");
                    }

                    const batch = writeBatch(db);
                    const feesCollection = collection(db, "dueFees");

                    console.log("Deleting old fees data...");
                    const oldDocsSnapshot = await getDocs(feesCollection);
                    oldDocsSnapshot.forEach(doc => {
                        batch.delete(doc.ref);
                    });
                    
                    console.log("Adding new fees data...");
                    jsonData.forEach(item => {
                        const newFeeEntry = {
                            fullName: String(item.fullName || ''),
                            propertyNumber: String(item.propertyNumber || ''),
                            location: String(item.location || ''),
                            dueFee: Number(item.dueFee || 0),
                            updatedAt: serverTimestamp()
                        };
                        
                        const docRef = doc(db, "dueFees", newFeeEntry.propertyNumber);
                        batch.set(docRef, newFeeEntry);
                    });

                    await batch.commit();
                    console.log("Batch commit successful.");
                    resolve();

                } catch (err) {
                    console.error("Error parsing JSON or uploading to Firestore: ", err);
                    reject(err);
                }
            };
            reader.onerror = (err) => reject(err);
            reader.readAsText(file);
        });
    };



    const renderPage = () => {
        switch (currentPage) {
            case 'about': return <AboutPage />;
           case 'news': 
            return (
                <NewsPage 
                    news={news} 
                    navigate={setCurrentPage}
                    setSelectedActivityId={setSelectedActivityId} 
                />
            );
            case 'activities': 
    return (
        <LocalActivitiesPage 
            activities={activities} 
            selectedActivityId={selectedActivityId} 
            setSelectedActivityId={setSelectedActivityId} 
        />
    );
            case 'faqs': return <FAQPage />;
            case 'directory': return <TownDirectoryPage />;
            case 'permits': return <PermitsPage documents={documents} />;
            case 'fees': return <DueFeesPage />;
            case 'emergency': return <EmergencyDirectoryPage />;
            case 'contact': return <ContactPage />;
            case 'install':
    return <InstallAppPage deferredPrompt={deferredPrompt} navigate={navigate} />;
case 'surveys': 
            return <ComplaintsAndSurveysPage view="surveys" surveys={surveys} addComplaint={addComplaint} navigate={navigate} />;
        case 'complaints': 
            return <ComplaintsAndSurveysPage view="complaints" surveys={surveys} addComplaint={addComplaint} navigate={navigate} />;           case 'admin': return <AdminPage 
                        user={user} 
                        handleLogin={handleLogin} 
                        handleLogout={handleLogout}
                        news={news}
                        activities={activities}          
                        documents={documents}
                        surveys={surveys}
                        complaints={complaints}
                        banners={banners}
                        addBanner={addBanner}
                        toggleBannerStatus={toggleBannerStatus}
                        deleteBanner={deleteBanner}
                        waterSchedule={waterSchedule}
                        updateWaterSchedule={updateWaterSchedule}
                        addNews={addNews}
                        addActivity={addActivity}        
                        deleteActivity={deleteActivity}  
                        deleteNews={deleteNews}
                        updateNews={updateNews}
                        addDocument={addDocument}
                        updateDocumentCategory={updateDocumentCategory}
                        deleteDocument={deleteDocument}
                        addSurvey={addSurvey}
                        deleteSurvey={deleteSurvey}
                        updateComplaintStatus={updateComplaintStatus}
                        deleteComplaint={deleteComplaint}
                        handleFeesUpload={handleFeesUpload}
                        optimizeAllExistingNews={optimizeAllExistingNews}
                        obituaries={obituaries}
                        addObituary={addObituary}
                        updateObituary={updateObituary}
                        deleteObituary={deleteObituary}
                    />;
            case 'obituaries': return <ObituariesPage obituaries={obituaries} />;
            case 'water':
            case 'home':
            default:
                return <HomePage navigate={navigate} waterSchedule={waterSchedule} obituaries={obituaries} />;
        }
    };

    if (loading) {
        return <div className="flex justify-center items-center h-screen"><p>Loading...</p></div>
    }

    return (
        <div dir="rtl" className="bg-gray-100 min-h-screen flex flex-col font-cairo">
            <style>{`
              @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;700;900&display=swap');
              .font-cairo { font-family: 'Cairo', sans-serif; }
              @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
              .animate-fadeIn { animation: fadeIn 0.5s ease-in-out; }
              @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
              .animate-fadeInUp { animation: fadeInUp 0.6s ease-out; }
              .delay-300 { animation-delay: 0.3s; }
            `}</style>

            {isLoadingData && <MunicipalityLoader />}
            <Header navigate={navigate} currentPage={currentPage} onOpenSearch={() => setIsSearchOpen(true)} />
            <UrgentTickerBanner banners={banners} />
            {/* Global Search Modal */}
        <GlobalSearchModal 
            isOpen={isSearchOpen} 
            onClose={() => setIsSearchOpen(false)} 
            navigate={navigate} 
            news={news} 
            activities={activities} 
            documents={documents} 
            setSelectedActivityId={setSelectedActivityId} 
        />

        <ConfirmationModal 
            isOpen={confirmDialog.isOpen}
            onClose={closeConfirm}
            onConfirm={confirmDialog.onConfirm}
            title={confirmDialog.title}
            message={confirmDialog.message}
            confirmText={confirmDialog.confirmText}
            cancelText={confirmDialog.cancelText}
            type={confirmDialog.type}
        />

        <PWAInstallPrompt />
            
            <NotificationPrompt />
            <main className="container mx-auto px-6 py-16 flex-grow">
                {renderPage()}
            </main>
            <Footer />
        </div>
    );
}